import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  collectSystemOwnerContext,
  isProtectedOwnerGp,
  isProtectedOwnerUgp,
  isProtectedOwnerUserId,
  permissionTouchesOwnerGranteesFromLists,
  SYSTEM_GROUP_NAME,
  viewerIsPlatformOwner,
} from '../utils/systemOwner';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import ErrorModal from '../components/modals/ErrorModal';
import AlertModal from '../components/modals/AlertModal';
import CornerNotice from '../components/notifications/CornerNotice';
import RoleTemplatesReference, {
  ROLE_TEMPLATES_PANEL_ID
} from '../components/pagePermissions/RoleTemplatesReference';
import {
  usePlatformPermissionsByProduct,
  sortPlatformPermissions,
  formatPlatformPermissionDetails,
} from '../components/pagePermissions/PlatformPermissionsPanel';
import GrantsByGranteePanel from '../components/pagePermissions/GrantsByGranteePanel';
import { GROUP_ROLE_TEMPLATES } from '../constants/groupRoleTemplates';
import {
  PLATFORM_PRODUCTS,
  PLATFORM_ACCESS_RESOURCE_ID,
  getPlatformKeysForType,
  getPlatformProduct,
  labelForPlatformKey,
  descriptionForPlatformKey,
  canonicalPlatformResourceId,
  allowedGranteeTypesForPlatformKey,
  exclusiveRoleCapabilitiesForPlatform,
  exclusiveScopeCapabilitiesForPlatform,
  ROLE_BADGE_PLATFORMS,
  SCOPE_EXCLUSIVE_PLATFORMS,
} from '../constants/platformPermissions';
import { normalizeCollaborationConfig } from '../utils/profileFormPermissions';
import {
  getUserProfileViewLayoutFromConfig,
  summarizePageConfiguration
} from '../utils/pageConfigurationSummary';
import './PagePermissionsManager.css';

function makeEmptyCollaboration() {
  return normalizeCollaborationConfig(null);
}

/** LearnPath app.sidebar item shape for PPM editor. */
function normalizeSidebarItem(raw = {}) {
  const children = Array.isArray(raw.children)
    ? raw.children.map((c) => normalizeSidebarItem(c))
    : [];
  const orderNum = Number(raw.order);
  return {
    id: String(raw.id || '').trim(),
    label: String(raw.label || '').trim(),
    path: String(raw.path || '').trim(),
    icon: String(raw.icon || '').trim(),
    order: Number.isFinite(orderNum) ? orderNum : 10,
    hidden: raw.hidden === true,
    children,
  };
}

function serializeSidebarItems(items) {
  return (items || []).map((raw) => {
    const item = normalizeSidebarItem(raw);
    const out = {
      id: item.id,
      label: item.label,
      path: item.path,
      icon: item.icon || undefined,
      order: item.order,
    };
    if (item.hidden) out.hidden = true;
    if (item.children.length) out.children = serializeSidebarItems(item.children);
    return out;
  });
}

const HOUR_WAIVER_APPROVAL_PIPELINE_KEY =
  'request-fe-hours-by-external-activity.ApprovalPipeline';

const DEFAULT_HOUR_WAIVER_APPROVAL_SLOTS = [
  {
    rank: 1,
    key: 'supervisor',
    label: 'Supervisor',
    required: false,
    channel: 'guest_supervisor',
  },
  {
    rank: 1,
    key: 'tor',
    label: 'Teacher of Record',
    required: false,
    channel: 'tor_membership',
  },
  {
    rank: 2,
    key: 'schoolAdmin',
    label: 'School Admin',
    required: false,
    channel: 'cap_school_admin',
  },
  {
    rank: 3,
    key: 'systemAdmin',
    label: 'System Admin',
    required: true,
    channel: 'cap_system_admin',
  },
];

/** Always the full catalog. Only `required` is overlaid from the saved grant. */
function normalizeHourWaiverApprovalPipeline(raw) {
  const catalog = DEFAULT_HOUR_WAIVER_APPROVAL_SLOTS;
  const rawSlots = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.slots)
      ? raw.slots
      : null;
  const overlayByKey = new Map(
    (rawSlots || []).filter((s) => s && s.key).map((s) => [String(s.key), s])
  );
  return catalog.map((def) => {
    const overlay = overlayByKey.get(def.key) || {};
    return {
      rank: def.rank,
      key: def.key,
      label: def.label,
      required:
        typeof overlay.required === 'boolean' ? overlay.required : def.required,
      channel: def.channel,
    };
  });
}

const DEFAULT_LEARNPATH_SIDEBAR_ITEMS = [
  normalizeSidebarItem({
    id: 'learn',
    label: 'Learn',
    path: '/learn',
    icon: 'message-circle',
    order: 5,
  }),
  normalizeSidebarItem({
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'layout-dashboard',
    order: 10,
  }),
  normalizeSidebarItem({
    id: 'trainings',
    label: 'Trainings',
    path: '/trainings',
    icon: 'list-checks',
    order: 15,
  }),
  normalizeSidebarItem({
    id: 'schools',
    label: 'Schools',
    path: '/schools',
    icon: 'school',
    order: 20,
  }),
  normalizeSidebarItem({
    id: 'programs',
    label: 'Programs',
    path: '/programs',
    icon: 'book-open',
    order: 30,
  }),
  normalizeSidebarItem({
    id: 'library',
    label: 'Library',
    path: '/library',
    icon: 'library',
    order: 35,
  }),
  normalizeSidebarItem({
    id: 'people',
    label: 'People',
    path: '/people',
    icon: 'users',
    order: 40,
  }),
];

function updateSidebarAt(items, path, updater) {
  if (!path.length) return items;
  const [idx, ...rest] = path;
  return (items || []).map((item, i) => {
    if (i !== idx) return item;
    if (!rest.length) return updater(item);
    return {
      ...item,
      children: updateSidebarAt(item.children || [], rest, updater),
    };
  });
}

function removeSidebarAt(items, path) {
  if (!path.length) return items;
  const [idx, ...rest] = path;
  if (!rest.length) return (items || []).filter((_, i) => i !== idx);
  return (items || []).map((item, i) => {
    if (i !== idx) return item;
    return { ...item, children: removeSidebarAt(item.children || [], rest) };
  });
}

function moveSidebarAt(items, path, direction) {
  if (!path.length) return items;
  const [idx, ...rest] = path;
  if (!rest.length) {
    const list = [...(items || [])];
    const j = idx + direction;
    if (j < 0 || j >= list.length) return list;
    const tmp = list[idx];
    list[idx] = list[j];
    list[j] = tmp;
    return list;
  }
  return (items || []).map((item, i) => {
    if (i !== idx) return item;
    return { ...item, children: moveSidebarAt(item.children || [], rest, direction) };
  });
}

/** Parse permissions.rules for student_info vendor filters. */
function parsePermissionRules(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function buildVendorRulesPayload(selectedGpIds, matchByGroup, groupsPeriodsList) {
  const ids = Array.from(
    new Set((selectedGpIds || []).map(String).map((s) => s.trim()).filter(Boolean))
  ).sort();
  if (!ids.length) return null;
  if (!matchByGroup) {
    return { vendor_groups_periods_ids: ids };
  }
  const groupIds = [];
  const seen = new Set();
  for (const gpId of ids) {
    const gp = (groupsPeriodsList || []).find((g) => String(g.id) === String(gpId));
    const gid = gp?.group_id != null ? String(gp.group_id) : '';
    if (gid && !seen.has(gid)) {
      seen.add(gid);
      groupIds.push(gid);
    }
  }
  groupIds.sort();
  return groupIds.length ? { vendor_group_ids: groupIds } : null;
}

const EMPTY_PEOPLE_LIST_DIRECTORY = {
  list_students: false,
  list_instructors: false,
  list_admins: false,
  list_super_admins: false,
};

const PEOPLE_LIST_DIRECTORY_ROLE_OPTIONS = [
  { key: 'list_students', role: 'students', label: 'Learners (Students)' },
  { key: 'list_instructors', role: 'instructors', label: 'Instructors' },
  { key: 'list_admins', role: 'admins', label: 'Admins' },
  { key: 'list_super_admins', role: 'super_admins', label: 'Super Admins' },
];

/** people.ListDirectory — metadata.list_roles (not rules; rules is vendor/scope only). */
function buildPeopleListDirectoryMetadataPayload(flags = {}) {
  const list_roles = [];
  if (flags.list_students === true) list_roles.push('students');
  if (flags.list_instructors === true) list_roles.push('instructors');
  if (flags.list_admins === true) list_roles.push('admins');
  if (flags.list_super_admins === true) list_roles.push('super_admins');
  return { list_roles };
}

function peopleListDirectoryFromMetadata(metadata, rulesFallback = null) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  const roles = new Set(
    (Array.isArray(meta.list_roles) ? meta.list_roles : [])
      .map((r) => String(r || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const r = parsePermissionRules(rulesFallback) || {};
  return {
    list_students:
      roles.has('students') ||
      roles.has('learners') ||
      meta.list_students === true ||
      meta.students === true ||
      r.list_students === true ||
      r.students === true ||
      r.learners === true,
    list_instructors:
      roles.has('instructors') ||
      roles.has('teachers') ||
      meta.list_instructors === true ||
      meta.instructors === true ||
      r.list_instructors === true ||
      r.instructors === true ||
      r.teachers === true,
    list_admins:
      roles.has('admins') ||
      roles.has('admin') ||
      meta.list_admins === true ||
      meta.list_admin === true ||
      meta.admins === true ||
      r.list_admins === true ||
      r.list_admin === true ||
      r.admins === true,
    list_super_admins:
      roles.has('super_admins') ||
      roles.has('super_admin') ||
      meta.list_super_admins === true ||
      meta.list_super_admin === true ||
      meta.super_admins === true ||
      r.list_super_admins === true ||
      r.list_super_admin === true ||
      r.super_admins === true,
  };
}

function peopleListDirectoryHasAnySlice(flags = {}) {
  return (
    flags.list_students === true ||
    flags.list_instructors === true ||
    flags.list_admins === true ||
    flags.list_super_admins === true
  );
}

const EMPTY_SCHOOL_ADD_USERS = {
  add_super_admins: false,
  add_admins: false,
  add_teachers: false,
  add_students: false,
  add_curriculum_developers: false,
  add_counselors: false,
  add_parents: false,
};

const SCHOOL_ADD_USERS_ROLE_OPTIONS = [
  { key: 'add_admins', role: 'admins', label: 'Admins' },
  { key: 'add_teachers', role: 'teachers', label: 'Teachers' },
  { key: 'add_students', role: 'students', label: 'Students' },
  { key: 'add_curriculum_developers', role: 'curriculum_developers', label: 'Curriculum Developers' },
  { key: 'add_counselors', role: 'counselors', label: 'Counselors' },
  { key: 'add_parents', role: 'parents', label: 'Parents' },
];

/** school.AddUsers — metadata.add_roles (not rules). */
function buildSchoolAddUsersMetadataPayload(flags = {}) {
  const add_roles = [];
  if (flags.add_admins === true) add_roles.push('admins');
  if (flags.add_teachers === true) add_roles.push('teachers');
  if (flags.add_students === true) add_roles.push('students');
  if (flags.add_curriculum_developers === true) add_roles.push('curriculum_developers');
  if (flags.add_counselors === true) add_roles.push('counselors');
  if (flags.add_parents === true) add_roles.push('parents');
  return { add_roles };
}

function schoolAddUsersFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  const roles = new Set(
    (Array.isArray(meta.add_roles) ? meta.add_roles : [])
      .map((r) => String(r || '').trim().toLowerCase())
      .filter(Boolean)
  );
  return {
    add_super_admins: roles.has('super_admins') || roles.has('super_admin') || meta.add_super_admins === true,
    add_admins: roles.has('admins') || roles.has('admin') || meta.add_admins === true,
    add_teachers: roles.has('teachers') || roles.has('teacher') || meta.add_teachers === true,
    add_students: roles.has('students') || roles.has('student') || meta.add_students === true,
    add_curriculum_developers:
      roles.has('curriculum_developers') ||
      roles.has('curriculum_developer') ||
      meta.add_curriculum_developers === true,
    add_counselors: roles.has('counselors') || roles.has('counselor') || meta.add_counselors === true,
    add_parents: roles.has('parents') || roles.has('parent') || meta.add_parents === true,
  };
}

const EMPTY_LINK_PERSON_TYPES = {
  link_guardians: false,
  link_counselors: false,
  link_siblings: false,
};

const LINK_PERSON_TYPE_OPTIONS = [
  { key: 'link_guardians', type: 'guardians', label: 'Guardians' },
  { key: 'link_counselors', type: 'counselors', label: 'Counselors' },
  { key: 'link_siblings', type: 'siblings', label: 'Siblings' },
];

const EMPTY_MESSAGE_THREAD_KINDS = {
  office: false,
};

function buildMessageThreadKindsMetadataPayload(flags = {}) {
  const thread_kinds = [];
  if (flags.office === true) thread_kinds.push('office');
  return { thread_kinds };
}

function isMessageThreadKindKey(key) {
  return (
    key === 'messages.ReceiveMessages' ||
    key === 'messages.SendMessages' ||
    key === 'messages.ReplyMessages'
  );
}

function messageThreadKindHint(key) {
  if (key === 'messages.SendMessages') {
    return 'Office is required when access is allow. Stored on metadata.thread_kinds as office. Checked means they may start an office request and view the threads they started. This does not open the school office inbox.';
  }
  if (key === 'messages.ReplyMessages') {
    return 'Checked kinds are the inboxes they may answer as staff. Office means reply on office posts they can already read. Support, once stored, means reply on support posts they receive. A parent continuing their own note uses Message Students Office, not this cap.';
  }
  return 'Office is required when access is allow. Stored on metadata.thread_kinds as office. Checked means they can read the office inbox for schools in this membership’s scope: this school, Listed Groups, or Global. This does not start a thread and does not reply.';
}

function messageThreadKindsFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  if (!Object.prototype.hasOwnProperty.call(meta, 'thread_kinds')) {
    return { office: true };
  }
  const kinds = new Set(
    (Array.isArray(meta.thread_kinds) ? meta.thread_kinds : [])
      .map((kind) => String(kind || '').trim().toLowerCase())
      .filter(Boolean)
  );
  return { office: kinds.has('office') };
}

function buildLinkPersonTypesMetadataPayload(flags = {}) {
  const link_types = [];
  if (flags.link_guardians === true) link_types.push('guardians');
  if (flags.link_counselors === true) link_types.push('counselors');
  if (flags.link_siblings === true) link_types.push('siblings');
  return { link_types };
}

function linkPersonTypesFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  if (!Object.prototype.hasOwnProperty.call(meta, 'link_types')) {
    return { link_guardians: true, link_counselors: true, link_siblings: false };
  }
  const types = new Set(
    (Array.isArray(meta.link_types) ? meta.link_types : [])
      .map((r) => String(r || '').trim().toLowerCase())
      .filter(Boolean)
  );
  // Older grants stored parents as its own type. Parents are guardians.
  const guardians =
    types.has('guardians') ||
    types.has('guardian') ||
    types.has('parents') ||
    types.has('parent');
  return {
    link_guardians: guardians,
    link_counselors: types.has('counselors') || types.has('counselor'),
    link_siblings: types.has('siblings') || types.has('sibling'),
  };
}

function linkPersonTypesHasAny(flags = {}) {
  return (
    flags.link_guardians === true ||
    flags.link_counselors === true ||
    flags.link_siblings === true
  );
}

function schoolAddUsersHasAnySlice(flags = {}) {
  return (
    flags.add_super_admins === true ||
    flags.add_admins === true ||
    flags.add_teachers === true ||
    flags.add_students === true ||
    flags.add_curriculum_developers === true ||
    flags.add_counselors === true ||
    flags.add_parents === true
  );
}

const EMPTY_SCHOOL_VIEW_USERS = {
  view_super_admins: false,
  view_admins: false,
  view_teachers: false,
  view_students: false,
  view_curriculum_developers: false,
  view_counselors: false,
};

const SCHOOL_VIEW_USERS_ROLE_OPTIONS = [
  { key: 'view_super_admins', role: 'super_admins', label: 'Super Admins' },
  { key: 'view_admins', role: 'admins', label: 'Admins' },
  { key: 'view_teachers', role: 'teachers', label: 'Teachers' },
  { key: 'view_students', role: 'students', label: 'Students' },
  { key: 'view_curriculum_developers', role: 'curriculum_developers', label: 'Curriculum Developers' },
  { key: 'view_counselors', role: 'counselors', label: 'Counselors' },
];

/** school.ViewUsers — metadata.view_roles (not rules). */
function buildSchoolViewUsersMetadataPayload(flags = {}) {
  const view_roles = [];
  if (flags.view_super_admins === true) view_roles.push('super_admins');
  if (flags.view_admins === true) view_roles.push('admins');
  if (flags.view_teachers === true) view_roles.push('teachers');
  if (flags.view_students === true) view_roles.push('students');
  if (flags.view_curriculum_developers === true) view_roles.push('curriculum_developers');
  if (flags.view_counselors === true) view_roles.push('counselors');
  return { view_roles };
}

function schoolViewUsersFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  const roles = new Set(
    (Array.isArray(meta.view_roles) ? meta.view_roles : [])
      .map((r) => String(r || '').trim().toLowerCase())
      .filter(Boolean)
  );
  return {
    view_super_admins: roles.has('super_admins') || roles.has('super_admin') || meta.view_super_admins === true,
    view_admins: roles.has('admins') || roles.has('admin') || meta.view_admins === true,
    view_teachers: roles.has('teachers') || roles.has('teacher') || meta.view_teachers === true,
    view_students: roles.has('students') || roles.has('student') || meta.view_students === true,
    view_curriculum_developers:
      roles.has('curriculum_developers') ||
      roles.has('curriculum_developer') ||
      meta.view_curriculum_developers === true,
    view_counselors: roles.has('counselors') || roles.has('counselor') || meta.view_counselors === true,
  };
}

const EMPTY_INBOX_ROLES = {
  inbox_super_admins: false,
  inbox_admins: false,
  inbox_teachers: false,
  inbox_students: false,
  inbox_curriculum_developers: false,
  inbox_counselors: false,
  inbox_parents: false,
};

const INBOX_ROLE_OPTIONS = [
  { key: 'inbox_super_admins', role: 'super_admins', label: 'Super Admins' },
  { key: 'inbox_admins', role: 'admins', label: 'Admins' },
  { key: 'inbox_teachers', role: 'teachers', label: 'Teachers' },
  { key: 'inbox_students', role: 'students', label: 'Students' },
  { key: 'inbox_curriculum_developers', role: 'curriculum_developers', label: 'Curriculum Developers' },
  { key: 'inbox_counselors', role: 'counselors', label: 'Counselors' },
  { key: 'inbox_parents', role: 'parents', label: 'Parents' },
];

function buildInboxRolesMetadataPayload(flags = {}) {
  const inbox_roles = [];
  for (const opt of INBOX_ROLE_OPTIONS) {
    if (flags[opt.key] === true) inbox_roles.push(opt.role);
  }
  return { inbox_roles };
}

function inboxRolesFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  const roles = new Set(
    (Array.isArray(meta.inbox_roles) ? meta.inbox_roles : [])
      .map((role) => String(role || '').trim().toLowerCase())
      .filter(Boolean)
  );
  return {
    inbox_super_admins: roles.has('super_admins') || roles.has('super_admin'),
    inbox_admins: roles.has('admins') || roles.has('admin'),
    inbox_teachers: roles.has('teachers') || roles.has('teacher'),
    inbox_students: roles.has('students') || roles.has('student'),
    inbox_curriculum_developers: roles.has('curriculum_developers') || roles.has('curriculum_developer'),
    inbox_counselors: roles.has('counselors') || roles.has('counselor'),
    inbox_parents: roles.has('parents') || roles.has('parent'),
  };
}

function inboxRolesHasAny(flags = {}) {
  return INBOX_ROLE_OPTIONS.some((opt) => flags[opt.key] === true);
}

const EMPTY_RELATIONSHIP_TYPES = {
  rel_guardian: false,
  rel_sibling: false,
  rel_teacher_of_record: false,
  rel_counselor: false,
  rel_supervisor: false,
};

const RELATIONSHIP_TYPE_OPTIONS = [
  { key: 'rel_guardian', type: 'guardian', label: 'Guardian' },
  { key: 'rel_sibling', type: 'sibling', label: 'Sibling' },
  { key: 'rel_teacher_of_record', type: 'teacher_of_record', label: 'Teacher of Record' },
  { key: 'rel_counselor', type: 'counselor', label: 'Counselor' },
  { key: 'rel_supervisor', type: 'supervisor', label: 'Supervisor' },
];

function buildRelationshipTypesMetadataPayload(flags = {}) {
  const relationship_types = [];
  for (const opt of RELATIONSHIP_TYPE_OPTIONS) {
    if (flags[opt.key] === true) relationship_types.push(opt.type);
  }
  return { relationship_types };
}

function relationshipTypesFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  const types = new Set(
    (Array.isArray(meta.relationship_types) ? meta.relationship_types : [])
      .map((type) => String(type || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const flags = { ...EMPTY_RELATIONSHIP_TYPES };
  for (const opt of RELATIONSHIP_TYPE_OPTIONS) {
    flags[opt.key] = types.has(opt.type);
  }
  return flags;
}

function relationshipTypesHasAny(flags = {}) {
  return RELATIONSHIP_TYPE_OPTIONS.some((opt) => flags[opt.key] === true);
}

function schoolViewUsersHasAnySlice(flags = {}) {
  return (
    flags.view_super_admins === true ||
    flags.view_admins === true ||
    flags.view_teachers === true ||
    flags.view_students === true ||
    flags.view_curriculum_developers === true ||
    flags.view_counselors === true
  );
}

/** course.TeachEnrolled — metadata.instructor_kit (not rules). Defaults all four on when omitted. */
const EMPTY_INSTRUCT_ASSIGNED = {
  propose_activities: true,
  edit_activities: true,
  grade_activities: true,
  override_activities: true,
};

const INSTRUCT_ASSIGNED_KIT_OPTIONS = [
  { key: 'propose_activities', verb: 'propose_activities', label: 'Propose activities' },
  { key: 'edit_activities', verb: 'edit_activities', label: 'Edit activities' },
  { key: 'grade_activities', verb: 'grade_activities', label: 'Grade activities' },
  { key: 'override_activities', verb: 'override_activities', label: 'Override activities' },
];

function buildInstructAssignedMetadataPayload(flags = {}) {
  const instructor_kit = [];
  if (flags.propose_activities === true) instructor_kit.push('propose_activities');
  if (flags.edit_activities === true) instructor_kit.push('edit_activities');
  if (flags.grade_activities === true) instructor_kit.push('grade_activities');
  if (flags.override_activities === true) instructor_kit.push('override_activities');
  return { instructor_kit };
}

function instructAssignedFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  if (!Object.prototype.hasOwnProperty.call(meta, 'instructor_kit')) {
    return { ...EMPTY_INSTRUCT_ASSIGNED };
  }
  const verbs = new Set(
    (Array.isArray(meta.instructor_kit) ? meta.instructor_kit : [])
      .map((r) => String(r || '').trim().toLowerCase())
      .filter(Boolean)
  );
  return {
    propose_activities: verbs.has('propose_activities') || meta.propose_activities === true,
    edit_activities: verbs.has('edit_activities') || meta.edit_activities === true,
    grade_activities: verbs.has('grade_activities') || meta.grade_activities === true,
    override_activities: verbs.has('override_activities') || meta.override_activities === true,
  };
}

/** course.TeachWithoutEnrollment — metadata.supervisor_kit. Defaults all on when omitted. */
const EMPTY_SUPERVISOR_KIT = {
  grade_activities: true,
  view_activities: true,
  override_activities: true,
  student_progress_chat: true,
  staff_progress_chat: true,
};

const SUPERVISOR_KIT_OPTIONS = [
  { key: 'grade_activities', verb: 'grade_activities', label: 'Grade papers' },
  { key: 'view_activities', verb: 'view_activities', label: 'View papers (no mark)' },
  { key: 'override_activities', verb: 'override_activities', label: 'Override attempts' },
  { key: 'student_progress_chat', verb: 'student_progress_chat', label: 'Student chat' },
  { key: 'staff_progress_chat', verb: 'staff_progress_chat', label: 'Staff chat' },
];

function buildSupervisorKitMetadataPayload(flags = {}) {
  const supervisor_kit = [];
  for (const opt of SUPERVISOR_KIT_OPTIONS) {
    if (flags[opt.key] === true) supervisor_kit.push(opt.verb);
  }
  return { supervisor_kit };
}

function supervisorKitFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  const field = Object.prototype.hasOwnProperty.call(meta, 'supervisor_kit')
    ? 'supervisor_kit'
    : Object.prototype.hasOwnProperty.call(meta, 'instructor_kit')
      ? 'instructor_kit'
      : null;
  if (!field) {
    return { ...EMPTY_SUPERVISOR_KIT };
  }
  const verbs = new Set(
    (Array.isArray(meta[field]) ? meta[field] : [])
      .map((r) => String(r || '').trim().toLowerCase())
      .filter(Boolean)
  );
  return {
    grade_activities: verbs.has('grade_activities'),
    view_activities: verbs.has('view_activities'),
    override_activities: verbs.has('override_activities'),
    student_progress_chat: verbs.has('student_progress_chat'),
    staff_progress_chat: verbs.has('staff_progress_chat'),
  };
}

/** library.EditSources / ProposeSources — metadata.source_kit (not rules). Defaults all on when omitted. */
const EMPTY_LIBRARY_SOURCE_KIT = {
  document: true,
  image: true,
  audio: true,
  video: true,
  url: true,
  replace_file: true,
  edit_analysis: true,
  max_12mb: false,
  max_80mb: false,
  max_200mb: false,
};

const LIBRARY_SOURCE_KIT_KIND_OPTIONS = [
  { key: 'document', verb: 'document', label: 'Document (PDF, Word, text)' },
  { key: 'image', verb: 'image', label: 'Image' },
  { key: 'audio', verb: 'audio', label: 'Audio' },
  { key: 'video', verb: 'video', label: 'Video' },
  { key: 'url', verb: 'url', label: 'URL' },
];

const LIBRARY_SOURCE_KIT_ACTION_OPTIONS = [
  { key: 'replace_file', verb: 'replace_file', label: 'Replace file / URL' },
  { key: 'edit_analysis', verb: 'edit_analysis', label: 'Edit tags / taxonomy / transcript' },
];

const LIBRARY_SOURCE_KIT_SIZE_OPTIONS = [
  { key: 'max_12mb', verb: 'max_12mb', label: '12 MB cap' },
  { key: 'max_80mb', verb: 'max_80mb', label: '80 MB cap' },
  { key: 'max_200mb', verb: 'max_200mb', label: '200 MB cap' },
];

function buildLibrarySourceKitMetadataPayload(flags = {}) {
  const source_kit = [];
  for (const opt of [
    ...LIBRARY_SOURCE_KIT_KIND_OPTIONS,
    ...LIBRARY_SOURCE_KIT_ACTION_OPTIONS,
    ...LIBRARY_SOURCE_KIT_SIZE_OPTIONS,
  ]) {
    if (flags[opt.key] === true) source_kit.push(opt.verb);
  }
  return { source_kit };
}

function librarySourceKitFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  if (!Object.prototype.hasOwnProperty.call(meta, 'source_kit')) {
    return { ...EMPTY_LIBRARY_SOURCE_KIT };
  }
  const verbs = new Set(
    (Array.isArray(meta.source_kit) ? meta.source_kit : [])
      .map((r) => String(r || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const flags = { ...EMPTY_LIBRARY_SOURCE_KIT };
  for (const key of Object.keys(flags)) flags[key] = false;
  for (const opt of [
    ...LIBRARY_SOURCE_KIT_KIND_OPTIONS,
    ...LIBRARY_SOURCE_KIT_ACTION_OPTIONS,
    ...LIBRARY_SOURCE_KIT_SIZE_OPTIONS,
  ]) {
    flags[opt.key] = verbs.has(opt.verb);
  }
  return flags;
}

function librarySourceKitHasAny(flags = {}) {
  return (
    flags.document === true ||
    flags.image === true ||
    flags.audio === true ||
    flags.video === true ||
    flags.url === true ||
    flags.replace_file === true ||
    flags.edit_analysis === true
  );
}

function isLibrarySourceKitKey(key) {
  return key === 'library.EditSources' || key === 'library.ProposeSources';
}

/** page_access.grade-rubrics — metadata.rubric_actions. Omitted means create, edit, and delete. */
const EMPTY_GRADE_RUBRIC_ACTIONS = {
  create: true,
  edit: true,
  delete: true,
};

const GRADE_RUBRIC_ACTION_OPTIONS = [
  { key: 'create', verb: 'create', label: 'Create' },
  { key: 'edit', verb: 'edit', label: 'Edit' },
  { key: 'delete', verb: 'delete', label: 'Delete' },
];

function buildGradeRubricActionsMetadataPayload(flags = {}) {
  const rubric_actions = [];
  for (const opt of GRADE_RUBRIC_ACTION_OPTIONS) {
    if (flags[opt.key] === true) rubric_actions.push(opt.verb);
  }
  return { rubric_actions };
}

function gradeRubricActionsFromMetadata(metadata) {
  const meta =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  if (!Object.prototype.hasOwnProperty.call(meta, 'rubric_actions')) {
    return { ...EMPTY_GRADE_RUBRIC_ACTIONS };
  }
  const verbs = new Set(
    (Array.isArray(meta.rubric_actions) ? meta.rubric_actions : [])
      .map((verb) => String(verb || '').trim().toLowerCase())
      .filter(Boolean)
  );
  return {
    create: verbs.has('create'),
    edit: verbs.has('edit'),
    delete: verbs.has('delete'),
  };
}

/** scope.ListedGroups — explicit school/group allowlist (not student vendor). */
function buildScopeListedRulesPayload(selectedGpIds, matchByGroup, groupsPeriodsList) {
  const ids = Array.from(
    new Set((selectedGpIds || []).map(String).map((s) => s.trim()).filter(Boolean))
  ).sort();
  if (!ids.length) return null;
  if (!matchByGroup) {
    return { scope_groups_periods_ids: ids };
  }
  const groupIds = [];
  const seen = new Set();
  for (const gpId of ids) {
    const gp = (groupsPeriodsList || []).find((g) => String(g.id) === String(gpId));
    const gid = gp?.group_id != null ? String(gp.group_id) : '';
    if (gid && !seen.has(gid)) {
      seen.add(gid);
      groupIds.push(gid);
    }
  }
  groupIds.sort();
  return groupIds.length ? { scope_group_ids: groupIds } : null;
}

/** Empty/null rules and GP+vendor / scope-list rules are different scopes. */
function rulesScopeKey(rules) {
  const r = parsePermissionRules(rules);
  if (!r) return '';
  const gp = [
    ...new Set(
      (Array.isArray(r.vendor_groups_periods_ids) ? r.vendor_groups_periods_ids : [])
        .map(String)
        .filter(Boolean)
    ),
  ].sort();
  const groups = [
    ...new Set(
      (Array.isArray(r.vendor_group_ids) ? r.vendor_group_ids : []).map(String).filter(Boolean)
    ),
  ].sort();
  const scopeGp = [
    ...new Set(
      (Array.isArray(r.scope_groups_periods_ids) ? r.scope_groups_periods_ids : [])
        .map(String)
        .filter(Boolean)
    ),
  ].sort();
  const scopeGroups = [
    ...new Set(
      (Array.isArray(r.scope_group_ids) ? r.scope_group_ids : []).map(String).filter(Boolean)
    ),
  ].sort();
  if (!gp.length && !groups.length && !scopeGp.length && !scopeGroups.length) return '';
  return JSON.stringify({
    vendor_groups_periods_ids: gp,
    vendor_group_ids: groups,
    scope_groups_periods_ids: scopeGp,
    scope_group_ids: scopeGroups,
  });
}

function formatRulesScopeLabel(rules, groupsPeriodsList = []) {
  const key = rulesScopeKey(rules);
  if (!key) return 'no vendor rule (GP members only)';
  const r = parsePermissionRules(rules) || {};
  const gpIds = Array.isArray(r.vendor_groups_periods_ids) ? r.vendor_groups_periods_ids : [];
  const groupIds = Array.isArray(r.vendor_group_ids) ? r.vendor_group_ids : [];
  const bits = [];
  if (gpIds.length) {
    const labels = gpIds.slice(0, 2).map((id) => {
      const gp = groupsPeriodsList.find((g) => String(g.id) === String(id));
      return gp
        ? `${gp.group_name || '?'} — ${gp.period_title || gp.period_id || '?'}`
        : String(id).slice(0, 8);
    });
    bits.push(
      `vendor GP: ${labels.join(', ')}${gpIds.length > 2 ? ` +${gpIds.length - 2}` : ''}`
    );
  }
  if (groupIds.length) {
    const labels = groupIds.slice(0, 2).map((id) => {
      const gp = groupsPeriodsList.find((g) => String(g.group_id) === String(id));
      return gp?.group_name || String(id).slice(0, 8);
    });
    bits.push(
      `vendor group: ${labels.join(', ')}${groupIds.length > 2 ? ` +${groupIds.length - 2}` : ''}`
    );
  }
  return bits.join('; ') || 'vendor rule';
}

function vendorRuleGpIdsFromRules(rules, groupsPeriodsList) {
  const r = parsePermissionRules(rules);
  if (!r) return { gpIds: [], matchByGroup: false };
  const exact = Array.isArray(r.vendor_groups_periods_ids)
    ? r.vendor_groups_periods_ids.map(String).filter(Boolean)
    : [];
  if (exact.length) return { gpIds: exact, matchByGroup: false };
  const groupIds = Array.isArray(r.vendor_group_ids)
    ? r.vendor_group_ids.map(String).filter(Boolean)
    : [];
  if (!groupIds.length) return { gpIds: [], matchByGroup: false };
  const gpIds = [];
  const seenGroups = new Set();
  for (const gid of groupIds) {
    if (seenGroups.has(gid)) continue;
    const match = (groupsPeriodsList || []).find((g) => String(g.group_id) === String(gid));
    if (match) {
      gpIds.push(String(match.id));
      seenGroups.add(gid);
    }
  }
  return { gpIds, matchByGroup: true };
}

function scopeListedGpIdsFromRules(rules, groupsPeriodsList) {
  const r = parsePermissionRules(rules);
  if (!r) return { gpIds: [], matchByGroup: false };
  const exact = Array.isArray(r.scope_groups_periods_ids)
    ? r.scope_groups_periods_ids.map(String).filter(Boolean)
    : [];
  if (exact.length) return { gpIds: exact, matchByGroup: false };
  const groupIds = Array.isArray(r.scope_group_ids)
    ? r.scope_group_ids.map(String).filter(Boolean)
    : [];
  if (!groupIds.length) return { gpIds: [], matchByGroup: false };
  const gpIds = [];
  const seenGroups = new Set();
  for (const gid of groupIds) {
    if (seenGroups.has(gid)) continue;
    const match = (groupsPeriodsList || []).find((g) => String(g.group_id) === String(gid));
    if (match) {
      gpIds.push(String(match.id));
      seenGroups.add(gid);
    }
  }
  return { gpIds, matchByGroup: true };
}

function cloneCollaboration(c) {
  return {
    chat: {
      public: { ...c.chat.public },
      internal: { ...c.chat.internal },
      private: { ...c.chat.private }
    },
    flags: { ...c.flags },
    admin: { ...c.admin }
  };
}

/** Rendering model for the collaboration capability editor. */
const COLLABORATION_CHAT_ROWS = [
  {
    group: 'public',
    label: 'Public (assignee)',
    hint: 'The Public tab — messages the assignee can see.',
    caps: [
      ['read', 'View'],
      ['write', 'Post']
    ]
  },
  {
    group: 'internal',
    label: 'Internal (staff)',
    hint: 'Staff-only tab — never visible to the student.',
    caps: [
      ['read', 'View'],
      ['write', 'Post']
    ]
  },
  {
    group: 'private',
    label: 'My Notes (private)',
    hint: 'Personal notes visible only to their author.',
    caps: [
      ['read', 'Use'],
      ['write', 'Write'],
      ['edit', 'Edit own']
    ]
  }
];

const COLLABORATION_FLAG_ROWS = [
  ['read', 'View flags'],
  ['create', 'Create flags'],
  ['resolve', 'Resolve flags'],
  ['reopen', 'Reopen flags'],
  ['markAddressed', 'Mark addressed'],
  ['createWorkSample', 'Create work-sample flags']
];

const KNOWN_PAGES = [
  { id: 'acuitysis', label: 'Platform Access' },
  { id: 'dashboard', label: 'Dashboard', configKeys: ['widgets'] },
  { id: 'groups', label: 'Groups', hasConfig: true },
  { id: 'profile', label: 'Profile' },
  { id: 'auth', label: 'Sign in' },
  { id: 'user-profiles', label: 'User Profiles' },
  { id: 'group-diagram', label: 'Group Diagram' },
  { id: 'page-permissions', label: 'Page Permissions' },
  { id: 'apps', label: 'Apps' },
  { id: 'payments', label: 'Payments' },
  { id: 'scheduler', label: 'Scheduler' },
  { id: 'enrollment-data-manager', label: 'Enrollment Data Manager' },
  { id: 'forms', label: 'Forms' },
  { id: 'work-samples', label: 'Work Samples', hasConfig: true },
  { id: 'taxonomy', label: 'Taxonomy' },
  { id: 'inquiries', label: 'Inquiries', hasConfig: true }
];

const getPagePath = (pageId) => (pageId === 'auth' ? '/auth' : `/${pageId}`);

const PageOpenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const DASHBOARD_WIDGETS = [
  { id: 'groups', label: 'Groups' },
  { id: 'organizations', label: 'Organizations' },
  { id: 'profile', label: 'Profile' },
  { id: 'forms', label: 'Forms' },
  { id: 'work-samples', label: 'Work Samples' },
  { id: 'apps', label: 'Apps' },
  { id: 'payments', label: 'Payments' },
  { id: 'scheduler', label: 'Scheduler' },
  { id: 'page-permissions', label: 'Page Permissions' },
  { id: 'group-diagram', label: 'Group Diagram' },
  { id: 'enrollment-data-manager', label: 'Enrollment Data Manager' },
  { id: 'taxonomy', label: 'Taxonomy' },
  { id: 'inquiries', label: 'Inquiries' }
];

const GROUPS_VIEW_OPTIONS = [
  { id: 'organization', label: 'Organization (top level — all orgs for System)' },
  { id: 'team', label: 'Teams (current org only; use switcher to change org)' }
];

/** Layout object stored at configuration.user_profile_view_config */
const EMPTY_USER_PROFILE_VIEW_LAYOUT = { columns: [] };

const DEFAULT_USER_PROFILE_VIEW_CONFIG = {
  columns: [
    {
      id: 'academic',
      title: 'Academic Performance',
      order: 1,
      enabled: true,
      buckets: [
        { id: 'evaluations', title: 'Evaluations', enabled: true, order: 1 },
        { id: 'benchmarks', title: 'Benchmarks', enabled: true, order: 2 },
        { id: 'grades', title: 'Grades', enabled: true, order: 3 }
      ]
    },
    {
      id: 'records',
      title: 'Records & Billing',
      order: 2,
      enabled: true,
      buckets: [
        { id: 'certificates', title: 'Certificates', enabled: false, order: 1 }
      ]
    },
    {
      id: 'apps',
      title: 'Apps',
      order: 3,
      enabled: true,
      buckets: [] // Populated from /api/apps when editing user-profiles config
    }
  ]
};

function stringifyUserProfileViewLayout(layout) {
  const columns = layout?.columns;
  if (!Array.isArray(columns) || columns.length === 0) {
    return '[]';
  }
  return JSON.stringify({ columns }, null, 2);
}

const USER_PROFILE_CAPABILITY_GROUPS = [
  {
    title: 'Basic information',
    items: [
      {
        id: 'edit_user_data',
        label: 'Edit user data (users table)',
        stateKey: 'edit'
      },
      {
        id: 'edit_sign_in',
        label: 'Edit sign-in (can_login). Does not grant AcuitySIS Platform Access.',
        stateKey: 'editSignIn'
      },
      {
        id: 'grant_platform_access',
        label: 'Grant AcuitySIS Platform Access (this context on the profile)',
        stateKey: 'grantPlatformAccess'
      },
      {
        id: 'edit_student_information',
        label: 'Edit student information (enrollment attributes, save/cancel)',
        stateKey: 'editStudentInformation'
      }
    ]
  },
  {
    title: 'Person links',
    items: [
      {
        id: 'add_person_links',
        label: 'Add person links (guardians, counselors, supervisors)',
        stateKey: 'addPersonLinks'
      },
      {
        id: 'remove_person_links',
        label: 'Remove person links (double-click × to confirm)',
        stateKey: 'removePersonLinks'
      },
      {
        id: 'edit_person_links',
        label: 'Edit person links (guardian type, primary, name, email, phone)',
        stateKey: 'editPersonLinks'
      }
    ]
  },
  {
    title: 'Associations',
    items: [
      {
        id: 'add_associations',
        label: 'Add associations (vendor partner track, program site, etc.)',
        stateKey: 'addAssociations'
      },
      {
        id: 'remove_associations',
        label: 'Remove associations',
        stateKey: 'removeAssociations'
      }
    ]
  },
  {
    title: 'Credentials & keys',
    items: [
      {
        id: 'add_credentials',
        label: 'Add credentials & keys entries',
        stateKey: 'addCredentials'
      },
      {
        id: 'edit_credentials',
        label: 'Edit credentials & keys entries',
        stateKey: 'editCredentials'
      },
      {
        id: 'remove_credentials',
        label: 'Remove credentials & keys entries',
        stateKey: 'removeCredentials'
      }
    ]
  },
  {
    title: 'Profile properties',
    items: [
      {
        id: 'add_ugp_properties',
        label: 'Add profile properties (meeting links, etc.)',
        stateKey: 'addUgpProperties'
      },
      {
        id: 'edit_ugp_properties',
        label: 'Edit profile properties',
        stateKey: 'editUgpProperties'
      },
      {
        id: 'remove_ugp_properties',
        label: 'Remove profile properties',
        stateKey: 'removeUgpProperties'
      }
    ]
  },
  {
    title: 'Attachments',
    items: [
      {
        id: 'add_attachments',
        label: 'Add attachments (links or uploads)',
        stateKey: 'addAttachments'
      },
      {
        id: 'edit_attachments',
        label: 'Edit attachments',
        stateKey: 'editAttachments'
      },
      {
        id: 'remove_attachments',
        label: 'Remove attachments',
        stateKey: 'removeAttachments'
      }
    ]
  },
  {
    title: 'Custom forms',
    items: [
      {
        id: 'view_form',
        label: 'View form (see assigned forms in Custom Forms section)',
        stateKey: 'viewForm'
      },
      {
        id: 'create_submission',
        label: 'Create submission (start a new attempt; max attempts still apply)',
        stateKey: 'createSubmission'
      },
      {
        id: 'view_submission',
        label: 'View submission (read existing responses)',
        stateKey: 'viewSubmission'
      },
      {
        id: 'edit_draft',
        label: 'Edit draft (save and submit before turn-in)',
        stateKey: 'editDraft'
      },
      {
        id: 'edit_submission',
        label: 'Edit submitted responses (staff override — bypasses submit lock)',
        stateKey: 'editSubmission'
      },
      {
        id: 'delete_submission',
        label: 'Delete submission',
        stateKey: 'deleteSubmission'
      },
      {
        id: 'view_question_answers',
        label: 'View answer key (creator scoring rules on assignment viewer)',
        stateKey: 'viewQuestionAnswers'
      },
      {
        id: 'update_points',
        label: 'Update points (manual grading on points forms)',
        stateKey: 'updatePoints'
      }
    ]
  }
];

function userGroupsPeriodsLabel(ugp) {
  const name = `${ugp.first_name || ''} ${ugp.last_name || ''}`.trim() || ugp.email || ugp.user_email || ugp.user_id || '?';
  const role = ugp.relationship_type ? ` (${ugp.relationship_type})` : '';
  const groupPeriod = `${ugp.group_name || '?'} — ${ugp.period_title || ugp.period_code || ugp.groups_periods_id || '?'}`;
  return `${name}${role} • ${groupPeriod}`;
}

function formatGroupsPeriodsGranteeId(gpId, groupsPeriodsById, groupsPeriods = []) {
  if (gpId === '*all*') return '*all* (every groups_period grantee)';
  const gp =
    groupsPeriodsById.get(String(gpId)) ||
    groupsPeriods.find((g) => String(g.id) === String(gpId));
  if (gp) {
    return `${gp.group_name || '?'} — ${gp.period_title || gp.period_code || gp.id}`;
  }
  return String(gpId);
}

function computeGranteeOverlap(proposedGrantees, existingGrantees) {
  const proposed = (proposedGrantees || []).map(String);
  const existing = (existingGrantees || []).map(String);
  const proposedHasAll = proposed.includes('*all*');
  const existingHasAll = existing.includes('*all*');
  if (proposedHasAll && existingHasAll) return ['*all*'];
  if (proposedHasAll) {
    return existing.length > 0 ? existing : ['*all*'];
  }
  if (existingHasAll) {
    return proposed.filter((id) => id !== '*all*');
  }
  const proposedSet = new Set(proposed);
  return existing.filter((id) => proposedSet.has(id));
}

function permissionOverlapsGrantees(perm, {
  page,
  productId,
  resourceId,
  permissionType,
  granteeType,
  editingId,
  grantees,
  rules
}) {
  if (perm.permission_type !== permissionType || perm.grantee_type !== granteeType) {
    return false;
  }
  if (editingId && String(perm.id) === String(editingId)) return false;

  if (productId) {
    if ((perm.platform || perm.product_id) !== productId) return false;
    const permResource =
      perm.permission_type === 'platform_access'
        ? PLATFORM_ACCESS_RESOURCE_ID
        : canonicalPlatformResourceId(
            perm.platform || perm.product_id,
            perm.permission_type,
            perm.resource_id
          );
    const wantResource =
      permissionType === 'platform_access'
        ? PLATFORM_ACCESS_RESOURCE_ID
        : canonicalPlatformResourceId(productId, permissionType, resourceId);
    if (String(permResource || '') !== String(wantResource || '')) return false;
  } else if (perm.page_name !== page) {
    return false;
  }

  // Different rules scope (e.g. GP-only vs GP+vendor) → not a conflict.
  if (rulesScopeKey(perm.rules) !== rulesScopeKey(rules)) return false;

  const granteesSet = new Set((grantees || []).map(String));
  const existing = (perm.grantees || []).map(String);
  if (granteeType === 'groups_periods') {
    const hasAll = granteesSet.has('*all*');
    const existingHasAll = existing.includes('*all*');
    if (hasAll || existingHasAll) return true;
    return existing.some((g) => granteesSet.has(g));
  }
  if (granteeType === 'groups') {
    return existing.some((g) => granteesSet.has(g));
  }
  if (granteeType === 'user_groups_periods') {
    return existing.some((g) => granteesSet.has(g));
  }
  if (granteeType === 'users') {
    const hasAll = granteesSet.has('*all*');
    const existingHasAll = existing.includes('*all*');
    if (hasAll || existingHasAll) return true;
    return existing.some((g) => granteesSet.has(g));
  }
  return false;
}

function findGranteePermissionConflicts(params) {
  return (params.permissions || [])
    .filter((perm) => permissionOverlapsGrantees(perm, params))
    .map((perm) => ({
      permission: perm,
      overlappingGranteeIds: computeGranteeOverlap(params.grantees, perm.grantees)
    }));
}

/** Exclusive role.* badges: at most one identity per overlapping GP/UGP grantee. */
function findExclusiveRoleBadgeConflicts({
  permissions,
  productId,
  resourceId,
  permissionType,
  granteeType,
  editingId,
  grantees
}) {
  const exclusiveRoles = exclusiveRoleCapabilitiesForPlatform(productId);
  if (
    !ROLE_BADGE_PLATFORMS.has(productId) ||
    permissionType !== 'capability' ||
    !exclusiveRoles.has(resourceId)
  ) {
    return [];
  }
  if (granteeType !== 'groups_periods' && granteeType !== 'user_groups_periods') {
    return [];
  }
  return (permissions || [])
    .filter((perm) => {
      if (editingId && String(perm.id) === String(editingId)) return false;
      if ((perm.platform || perm.product_id) !== productId) return false;
      if (perm.permission_type !== 'capability') return false;
      if (perm.grantee_type !== granteeType) return false;
      if (!exclusiveRoles.has(perm.resource_id)) return false;
      if (String(perm.resource_id || '') === String(resourceId || '')) return false;
      return computeGranteeOverlap(grantees, perm.grantees).length > 0;
    })
    .map((perm) => ({
      permission: perm,
      overlappingGranteeIds: computeGranteeOverlap(grantees, perm.grantees)
    }));
}

const ROLE_BADGE_EXCLUSIVE_MESSAGE =
  'A membership can have only one role badge per product. Use a separate UGP for a different identity.';

function scopeExclusiveMessageForPlatform(productId) {
  if (productId === 'acuitycte') {
    return (
      'A membership can have only one scope capability (Global, Same Level Children, Parent Peer Children, ' +
      'Organization Children, or Listed Groups). Remove the other scope grant or use a separate UGP for a different scope.'
    );
  }
  return (
    'A membership can have only one school scope (Global or Listed Groups). ' +
    'Remove the other scope grant or use a separate UGP for a different scope.'
  );
}

/** Emphasis warning when configuring scope.ListedGroups in PPM. */
function scopeListedGroupsWarningForPlatform(productId) {
  const productLabel = getPlatformProduct(productId)?.label || productId;
  return (
    `Important — Listed Groups (${productLabel}): every other capability on this membership applies ` +
    'uniformly to all schools in the list. You cannot assign different roles or permissions per listed ' +
    'school on this GP/UGP. If someone needs different access at different schools, create a separate ' +
    'UGP (membership in that school’s group) instead of adding more schools here. Per-school permission ' +
    'differences may be supported later; for now one UGP per school role is the recommended pattern.'
  );
}

/** Exclusive scope.* caps: at most one school visibility scope per overlapping GP/UGP grantee. */
function findExclusiveScopeConflicts({
  permissions,
  productId,
  resourceId,
  permissionType,
  granteeType,
  editingId,
  grantees,
}) {
  const exclusiveScopes = exclusiveScopeCapabilitiesForPlatform(productId);
  if (
    !SCOPE_EXCLUSIVE_PLATFORMS.has(productId) ||
    permissionType !== 'capability' ||
    !exclusiveScopes.has(resourceId)
  ) {
    return [];
  }
  if (granteeType !== 'groups_periods' && granteeType !== 'user_groups_periods') {
    return [];
  }
  return (permissions || [])
    .filter((perm) => {
      if (editingId && String(perm.id) === String(editingId)) return false;
      if ((perm.platform || perm.product_id) !== productId) return false;
      if (perm.permission_type !== 'capability') return false;
      if (perm.grantee_type !== granteeType) return false;
      if (!exclusiveScopes.has(perm.resource_id)) return false;
      if (String(perm.resource_id || '') === String(resourceId || '')) return false;
      return computeGranteeOverlap(grantees, perm.grantees).length > 0;
    })
    .map((perm) => ({
      permission: perm,
      overlappingGranteeIds: computeGranteeOverlap(grantees, perm.grantees),
    }));
}

function buildGranteeOverlapConflictDetails(
  conflicts,
  { granteeType, groupsPeriodsById, groupsPeriods, userGroupsPeriods, groupsById }
) {
  if (!conflicts.length) return '';
  const lines = [
    `${conflicts.length} existing permission${conflicts.length === 1 ? '' : 's'} conflict:`,
    '',
    'Note: a grant for the same groups-period with no vendor rule and another with a vendor rule are different scopes and can both exist. This save matches an existing row with the same grantees and the same rules scope.'
  ];
  conflicts.forEach(({ permission, overlappingGranteeIds }, index) => {
    const name = (permission.permission_name || '').trim() || 'Untitled permission';
    const summary = summarizePageConfiguration(permission.configuration, {
      pageName: permission.page_name || permission.resource_id
    });
    lines.push('');
    lines.push(`${index + 1}. ${name}`);
    if (permission.id) lines.push(`   ID: ${permission.id}`);
    lines.push(`   Permission type: ${permission.permission_type}`);
    lines.push(`   Rules scope: ${formatRulesScopeLabel(permission.rules, groupsPeriods)}`);
    const granteeLabels = overlappingGranteeIds.map((id) => {
      if (granteeType === 'groups_periods') {
        return formatGroupsPeriodsGranteeId(id, groupsPeriodsById, groupsPeriods);
      }
      if (granteeType === 'groups') {
        const g = groupsById?.get(String(id));
        if (g) {
          const type = g.group_type ? ` (${g.group_type})` : '';
          return `${g.name || '?'}${type}`;
        }
        return String(id);
      }
      if (granteeType === 'user_groups_periods') {
        const ugp = userGroupsPeriods.find((row) => String(row.id) === String(id));
        return ugp ? userGroupsPeriodsLabel(ugp) : String(id);
      }
      return String(id);
    });
    lines.push(
      `   Overlapping grantee${granteeLabels.length === 1 ? '' : 's'}: ${granteeLabels.join('; ')}`
    );
    if (
      permission.permission_type === 'page_access' ||
      permission.permission_type === 'platform_access' ||
      permission.permission_type === 'capability'
    ) {
      const accessVal = permission.access;
      const isDeny = accessVal === false || accessVal === 'false';
      lines.push(`   Access: ${isDeny ? 'deny' : 'allow'}`);
    } else if (summary && summary !== 'page access only') {
      lines.push(`   Config: ${summary}`);
    }
  });
  return lines.join('\n');
}

const PERMISSION_CONFLICT_MESSAGE =
  'Cannot complete this permission because it conflicts with another known permission for the same resource, overlapping grantees, and the same rules scope. Edit the existing permission instead.';

const GRANTEE_ACRONYMS = {
  groups: { code: 'G', label: 'Groups' },
  groups_periods: { code: 'GP', label: 'Groups Periods' },
  user_groups_periods: { code: 'UGP', label: 'User Groups Periods' },
  users: { code: 'U', label: 'Users' },
  global: { code: 'GL', label: 'Global' }
};

const GRANTEE_TYPE_OPTIONS = [
  {
    id: 'groups',
    code: 'G',
    title: 'Groups',
    description:
      'Durable organization or team (groups.id). Not tied to a period. Used for product markers like LearnPath School.'
  },
  {
    id: 'groups_periods',
    code: 'GP',
    title: 'Groups Periods',
    description:
      'All members in selected teams or periods. Config grants page access unless you add a separate deny rule.'
  },
  {
    id: 'user_groups_periods',
    code: 'UGP',
    title: 'User Groups Periods',
    description:
      'Per-enrollment overrides for one user in one team/period. UGP config prevails over GP config for that enrollment.'
  },
  {
    id: 'users',
    code: 'Users',
    title: 'Users',
    description:
      'Target specific user accounts by ID or email. Config still implies access unless access is explicitly denied.'
  }
];

const PAGES_WITH_CONFIG = new Set(['dashboard', 'groups', 'user-profiles', 'profile', 'scheduler', 'forms']);

const CLIP_NAME = 28;
const CLIP_GRANTEES = 36;
const CLIP_CONFIG = 32;
const COMPACT_COL_NAME = 108;

const BOTTOM_PANEL_HEIGHT_MIN_VH = 28;
const BOTTOM_PANEL_HEIGHT_MAX_VH = 90;
const BOTTOM_PANEL_HEIGHT_DEFAULT_VH = 50;

const PANEL_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'page_configuration', label: 'Config' },
  { value: 'page_access', label: 'Access' },
  { value: 'platform_access', label: 'Platform access' },
  { value: 'capability', label: 'Capability' }
];

const PANEL_SCOPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All scopes' },
  { value: 'groups', label: 'G' },
  { value: 'groups_periods', label: 'GP' },
  { value: 'user_groups_periods', label: 'UGP' },
  { value: 'users', label: 'Users' }
];

const PANEL_SORT_OPTIONS = [
  { value: 'default', label: 'Sort: default' },
  { value: 'type', label: 'Sort: type' },
  { value: 'name', label: 'Sort: name' },
  { value: 'scope', label: 'Sort: scope' }
];

function applyPermissionListView(permissions, { typeFilter, scopeFilter, sortBy, isPlatform }) {
  let list = [...(permissions || [])];
  if (typeFilter && typeFilter !== 'all') {
    list = list.filter((p) => p.permission_type === typeFilter);
  }
  if (scopeFilter && scopeFilter !== 'all') {
    list = list.filter((p) => p.grantee_type === scopeFilter);
  }
  const cmp = (a, b, key) =>
    String(a?.[key] || '').localeCompare(String(b?.[key] || ''), undefined, {
      sensitivity: 'base'
    });
  if (sortBy === 'type') {
    list.sort((a, b) => cmp(a, b, 'permission_type') || cmp(a, b, 'permission_name'));
    return list;
  }
  if (sortBy === 'name') {
    list.sort((a, b) => cmp(a, b, 'permission_name') || cmp(a, b, 'permission_type'));
    return list;
  }
  if (sortBy === 'scope') {
    list.sort((a, b) => cmp(a, b, 'grantee_type') || cmp(a, b, 'permission_name'));
    return list;
  }
  return isPlatform ? sortPlatformPermissions(list) : list;
}

const clipText = (text, maxLen) => {
  const s = String(text ?? '');
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
};

const IconGear = ({ size = 14, color = '#4f46e5' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const IconCheck = ({ size = 14, color = '#16a34a' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const IconDeny = ({ size = 14, color = '#dc2626' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" aria-hidden>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const IconPencil = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconDelete = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const GRANTEE_TREE_STEP = 16;

/** Branch rails + elbow for nested grantee rows. */
function GranteeTreeGuides({ depth = 0, pipes = [], isLast = true }) {
  const d = Math.max(0, Number(depth) || 0);
  if (d <= 0) return null;
  return (
    <span
      className="ppm-grantee-tree-guides"
      aria-hidden
      style={{ width: d * GRANTEE_TREE_STEP }}
    >
      {(pipes || []).map((cont, i) =>
        cont ? (
          <span
            key={`rail-${i}`}
            className="ppm-grantee-tree-rail"
            style={{ left: i * GRANTEE_TREE_STEP + 7 }}
          />
        ) : null
      )}
      <span
        className={`ppm-grantee-tree-elbow${isLast ? ' ppm-grantee-tree-elbow--last' : ''}`}
        style={{ left: (d - 1) * GRANTEE_TREE_STEP }}
      />
    </span>
  );
}

const OWNER_PROTECTED_HINT =
  'Platform Owner (System → Owner) cannot be assigned or edited here.';

/** Org/system root GPs are not grantee targets — only nested teams/periods are selectable. */
function isHiddenGroupsPeriodGrantee(gp, hierarchyMeta = {}) {
  if (!gp) return true;
  const type = String(gp.group_type || '').toLowerCase();
  if (type === 'organization' || type === 'system') return true;
  const meta = hierarchyMeta[String(gp.id)];
  if (
    String(gp.group_name || '').trim() === SYSTEM_GROUP_NAME &&
    meta?.organization === SYSTEM_GROUP_NAME &&
    (meta.depth === 0 || meta.parentGpId == null)
  ) {
    return true;
  }
  return false;
}

/**
 * scope.ListedGroups picker — include organization GPs (and teams).
 * Still hide system shell; orgs/teams are valid allowlist targets.
 */
function isHiddenScopeListedGroupsPeriod(gp, hierarchyMeta = {}) {
  if (!gp) return true;
  const type = String(gp.group_type || '').toLowerCase();
  if (type === 'system') return true;
  const meta = hierarchyMeta[String(gp.id)];
  if (
    String(gp.group_name || '').trim() === SYSTEM_GROUP_NAME &&
    meta?.organization === SYSTEM_GROUP_NAME &&
    (meta.depth === 0 || meta.parentGpId == null)
  ) {
    return true;
  }
  return false;
}

function scopeListedGroupsPeriodLabel(gp) {
  if (!gp) return '';
  const type = String(gp.group_type || '').toLowerCase();
  const typeLabel =
    type === 'organization' ? 'Org' : type === 'team' ? 'Team' : type || 'Group';
  const period = gp.period_title || gp.period_id || '—';
  return `${typeLabel} · ${gp.group_name || '?'} — ${period}`;
}

const PagePermissionsManager = () => {
  const { fetchWithAuth, user: authUser, availableUgp, currentRole } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [platformPermissions, setPlatformPermissions] = useState([]);
  const [groupsPeriods, setGroupsPeriods] = useState([]);
  const [userGroupsPeriods, setUserGroupsPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingPermission, setEditingPermission] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [selectedPlatformId, setSelectedPlatformId] = useState(null);
  const [platformGrantView, setPlatformGrantView] = useState('rows');
  /** 'page' | 'platform' — which resource the create/edit sheet targets */
  const [formScope, setFormScope] = useState('page');
  const [formPlatformId, setFormPlatformId] = useState('acuitycte');
  const [formPlatformKey, setFormPlatformKey] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [cornerNotice, setCornerNotice] = useState(null);

  // Create/Edit form state
  const [formPermissionName, setFormPermissionName] = useState('');
  const [formPermissionType, setFormPermissionType] = useState('page_configuration');
  const [formPage, setFormPage] = useState('dashboard');
  const [formGranteeType, setFormGranteeType] = useState('groups_periods');
  const [, setFormGrantees] = useState([]);
  const [formGranteesInput, setFormGranteesInput] = useState('');
  const [formAccess, setFormAccess] = useState(true); // true=allow, false=deny (for page_access)
  const [formConfig, setFormConfig] = useState({});
  const [formConfigWidgets, setFormConfigWidgets] = useState([]);
  const [formSidebarItems, setFormSidebarItems] = useState([]);
  const [formApprovalPipeline, setFormApprovalPipeline] = useState(() =>
    normalizeHourWaiverApprovalPipeline(null)
  );
  const [formConfigGroupsView, setFormConfigGroupsView] = useState('');
  const [formConfigRoleGroupNameIncludes, setFormConfigRoleGroupNameIncludes] = useState('');
  const [formConfigRoleGroupNameExcludes, setFormConfigRoleGroupNameExcludes] = useState('');
  const [formConfigRoleGroupIdExcludes, setFormConfigRoleGroupIdExcludes] = useState([]);
  const [excludeTeamsDetailsOpen, setExcludeTeamsDetailsOpen] = useState(false);
  const [formConfigGroupsCreateEntities, setFormConfigGroupsCreateEntities] = useState(false);
  const [formConfigGroupsCreateOrganizations, setFormConfigGroupsCreateOrganizations] = useState(false);
  const [formConfigGroupsCreateOwnGroup, setFormConfigGroupsCreateOwnGroup] = useState(false);
  const [formConfigGroupsCreateSameLevel, setFormConfigGroupsCreateSameLevel] = useState(false);
  const [formConfigGroupsCreateOrgWide, setFormConfigGroupsCreateOrgWide] = useState(false);
  const [formConfigGroupsAddMembers, setFormConfigGroupsAddMembers] = useState(false);
  const [formConfigGroupsSeeAllSameLevel, setFormConfigGroupsSeeAllSameLevel] = useState(false);
  const [formConfigGroupsAddMembersOwnGroup, setFormConfigGroupsAddMembersOwnGroup] = useState(true);
  const [formConfigGroupsAddMembersSameLevel, setFormConfigGroupsAddMembersSameLevel] = useState(false);
  const [formConfigGroupsOpenMemberProfiles, setFormConfigGroupsOpenMemberProfiles] = useState(false);
  const [formConfigGroupsOpenMemberProfilesOwnGroup, setFormConfigGroupsOpenMemberProfilesOwnGroup] =
    useState(true);
  const [formConfigGroupsOpenMemberProfilesSameLevel, setFormConfigGroupsOpenMemberProfilesSameLevel] =
    useState(false);
  const [formConfigGroupsViewGroupMembers, setFormConfigGroupsViewGroupMembers] = useState(false);
  const [formConfigGroupsViewGroupMembersOwnGroup, setFormConfigGroupsViewGroupMembersOwnGroup] =
    useState(true);
  const [formConfigGroupsViewGroupMembersSameLevel, setFormConfigGroupsViewGroupMembersSameLevel] =
    useState(false);
  const [formConfigGroupsViewMemberDetails, setFormConfigGroupsViewMemberDetails] = useState(false);
  const [formConfigGroupsViewMemberDetailsOwnGroup, setFormConfigGroupsViewMemberDetailsOwnGroup] =
    useState(true);
  const [formConfigGroupsViewMemberDetailsSameLevel, setFormConfigGroupsViewMemberDetailsSameLevel] =
    useState(false);
  const [formConfigGroupsViewMemberAccess, setFormConfigGroupsViewMemberAccess] = useState(false);
  const [formConfigGroupsViewMemberAccessOwnGroup, setFormConfigGroupsViewMemberAccessOwnGroup] =
    useState(true);
  const [formConfigGroupsViewMemberAccessSameLevel, setFormConfigGroupsViewMemberAccessSameLevel] =
    useState(false);
  const [formConfigGroupsViewDerivedGroups, setFormConfigGroupsViewDerivedGroups] = useState(false);
  const [formConfigUserProfilesEdit, setFormConfigUserProfilesEdit] = useState(false);
  const [formConfigUserProfilesEditSignIn, setFormConfigUserProfilesEditSignIn] = useState(false);
  const [formConfigUserProfilesGrantPlatformAccess, setFormConfigUserProfilesGrantPlatformAccess] = useState(false);
  const [formConfigUserProfilesAddPersonLinks, setFormConfigUserProfilesAddPersonLinks] = useState(false);
  const [formConfigUserProfilesRemovePersonLinks, setFormConfigUserProfilesRemovePersonLinks] = useState(false);
  const [formConfigUserProfilesEditPersonLinks, setFormConfigUserProfilesEditPersonLinks] = useState(false);
  const [formConfigUserProfilesAddAssociations, setFormConfigUserProfilesAddAssociations] = useState(false);
  const [formConfigUserProfilesRemoveAssociations, setFormConfigUserProfilesRemoveAssociations] = useState(false);
  const [formConfigUserProfilesEditStudentInformation, setFormConfigUserProfilesEditStudentInformation] = useState(false);
  const [formConfigUserProfilesAddCredentials, setFormConfigUserProfilesAddCredentials] = useState(false);
  const [formConfigUserProfilesEditCredentials, setFormConfigUserProfilesEditCredentials] = useState(false);
  const [formConfigUserProfilesRemoveCredentials, setFormConfigUserProfilesRemoveCredentials] = useState(false);
  const [formConfigUserProfilesAddAttachments, setFormConfigUserProfilesAddAttachments] = useState(false);
  const [formConfigUserProfilesEditAttachments, setFormConfigUserProfilesEditAttachments] = useState(false);
  const [formConfigUserProfilesRemoveAttachments, setFormConfigUserProfilesRemoveAttachments] = useState(false);
  const [formConfigUserProfilesAddUgpProperties, setFormConfigUserProfilesAddUgpProperties] = useState(false);
  const [formConfigUserProfilesEditUgpProperties, setFormConfigUserProfilesEditUgpProperties] = useState(false);
  const [formConfigUserProfilesRemoveUgpProperties, setFormConfigUserProfilesRemoveUgpProperties] = useState(false);
  const [formConfigUserProfilesViewForm, setFormConfigUserProfilesViewForm] = useState(false);
  const [formConfigUserProfilesCreateSubmission, setFormConfigUserProfilesCreateSubmission] = useState(false);
  const [formConfigUserProfilesViewSubmission, setFormConfigUserProfilesViewSubmission] = useState(false);
  const [formConfigUserProfilesEditDraft, setFormConfigUserProfilesEditDraft] = useState(false);
  const [formConfigUserProfilesEditSubmission, setFormConfigUserProfilesEditSubmission] = useState(false);
  const [formConfigUserProfilesDeleteSubmission, setFormConfigUserProfilesDeleteSubmission] = useState(false);
  const [formConfigUserProfilesViewQuestionAnswers, setFormConfigUserProfilesViewQuestionAnswers] = useState(false);
  const [formConfigUserProfilesUpdatePoints, setFormConfigUserProfilesUpdatePoints] = useState(false);
  const [formConfigCollaboration, setFormConfigCollaboration] = useState(() =>
    makeEmptyCollaboration()
  );
  const [formConfigCustomFormsSubmissionScope, setFormConfigCustomFormsSubmissionScope] = useState(
    'own_profile_only'
  );
  const [formConfigProfileEditName, setFormConfigProfileEditName] = useState(false);
  const [formConfigProfileUpdatePassword, setFormConfigProfileUpdatePassword] = useState(false);
  const [formConfigSchedulerCreateAllOrgs, setFormConfigSchedulerCreateAllOrgs] = useState(false);
  const [formConfigSchedulerEditAllOrgs, setFormConfigSchedulerEditAllOrgs] = useState(false);
  const [formConfigSchedulerRemoveAllOrgs, setFormConfigSchedulerRemoveAllOrgs] = useState(false);
  const [formConfigSchedulerCreateOwnOrg, setFormConfigSchedulerCreateOwnOrg] = useState(false);
  const [formConfigSchedulerEditOwnOrg, setFormConfigSchedulerEditOwnOrg] = useState(false);
  const [formConfigSchedulerRemoveOwnOrg, setFormConfigSchedulerRemoveOwnOrg] = useState(false);
  const [formConfigFormsViewScope, setFormConfigFormsViewScope] = useState('own');
  const [formConfigFormsTaxonomyScope, setFormConfigFormsTaxonomyScope] = useState('own_organization');
  const [formConfigFormsCreate, setFormConfigFormsCreate] = useState(false);
  const [formConfigFormsEdit, setFormConfigFormsEdit] = useState(false);
  const [formConfigFormsDelete, setFormConfigFormsDelete] = useState(false);
  const [formConfigFormsAssign, setFormConfigFormsAssign] = useState(false);
  const [formConfigWsAssignForms, setFormConfigWsAssignForms] = useState(false);
  const [formConfigWsViewScope, setFormConfigWsViewScope] = useState('linked_students');
  const [formConfigWsCurate, setFormConfigWsCurate] = useState(false);
  const [formConfigWsRemove, setFormConfigWsRemove] = useState(false);
  const [formConfigWsSubmit, setFormConfigWsSubmit] = useState(false);
  const [formConfigWsReview, setFormConfigWsReview] = useState(false);
  const [formConfigNotifyOnInquiry, setFormConfigNotifyOnInquiry] = useState(false);
  const [formUserProfileViewJson, setFormUserProfileViewJson] = useState('');
  const [formUserProfileViewJsonError, setFormUserProfileViewJsonError] = useState('');
  const [resetPolicyRules, setResetPolicyRules] = useState([]);
  const [resetPolicyLoading, setResetPolicyLoading] = useState(false);
  const [resetPolicySaving, setResetPolicySaving] = useState(false);

  const userProfileCapabilityBindings = {
    edit: [formConfigUserProfilesEdit, setFormConfigUserProfilesEdit],
    editSignIn: [formConfigUserProfilesEditSignIn, setFormConfigUserProfilesEditSignIn],
    grantPlatformAccess: [
      formConfigUserProfilesGrantPlatformAccess,
      setFormConfigUserProfilesGrantPlatformAccess
    ],
    editStudentInformation: [
      formConfigUserProfilesEditStudentInformation,
      setFormConfigUserProfilesEditStudentInformation
    ],
    addPersonLinks: [formConfigUserProfilesAddPersonLinks, setFormConfigUserProfilesAddPersonLinks],
    removePersonLinks: [
      formConfigUserProfilesRemovePersonLinks,
      setFormConfigUserProfilesRemovePersonLinks
    ],
    editPersonLinks: [formConfigUserProfilesEditPersonLinks, setFormConfigUserProfilesEditPersonLinks],
    addAssociations: [formConfigUserProfilesAddAssociations, setFormConfigUserProfilesAddAssociations],
    removeAssociations: [
      formConfigUserProfilesRemoveAssociations,
      setFormConfigUserProfilesRemoveAssociations
    ],
    addCredentials: [formConfigUserProfilesAddCredentials, setFormConfigUserProfilesAddCredentials],
    editCredentials: [formConfigUserProfilesEditCredentials, setFormConfigUserProfilesEditCredentials],
    removeCredentials: [
      formConfigUserProfilesRemoveCredentials,
      setFormConfigUserProfilesRemoveCredentials
    ],
    addUgpProperties: [formConfigUserProfilesAddUgpProperties, setFormConfigUserProfilesAddUgpProperties],
    editUgpProperties: [formConfigUserProfilesEditUgpProperties, setFormConfigUserProfilesEditUgpProperties],
    removeUgpProperties: [
      formConfigUserProfilesRemoveUgpProperties,
      setFormConfigUserProfilesRemoveUgpProperties
    ],
    addAttachments: [formConfigUserProfilesAddAttachments, setFormConfigUserProfilesAddAttachments],
    editAttachments: [formConfigUserProfilesEditAttachments, setFormConfigUserProfilesEditAttachments],
    removeAttachments: [
      formConfigUserProfilesRemoveAttachments,
      setFormConfigUserProfilesRemoveAttachments
    ],
    viewForm: [formConfigUserProfilesViewForm, setFormConfigUserProfilesViewForm],
    createSubmission: [formConfigUserProfilesCreateSubmission, setFormConfigUserProfilesCreateSubmission],
    viewSubmission: [formConfigUserProfilesViewSubmission, setFormConfigUserProfilesViewSubmission],
    editDraft: [formConfigUserProfilesEditDraft, setFormConfigUserProfilesEditDraft],
    editSubmission: [formConfigUserProfilesEditSubmission, setFormConfigUserProfilesEditSubmission],
    deleteSubmission: [formConfigUserProfilesDeleteSubmission, setFormConfigUserProfilesDeleteSubmission],
    viewQuestionAnswers: [
      formConfigUserProfilesViewQuestionAnswers,
      setFormConfigUserProfilesViewQuestionAnswers
    ],
    updatePoints: [formConfigUserProfilesUpdatePoints, setFormConfigUserProfilesUpdatePoints]
  };

  const toggleCollaboration = (section, group, key) => {
    setFormConfigCollaboration((prev) => {
      const next = cloneCollaboration(prev);
      if (group) {
        next[section][group][key] = !next[section][group][key];
      } else {
        next[section][key] = !next[section][key];
      }
      return next;
    });
  };

  const [groupPeriodsSearch, setGroupPeriodsSearch] = useState('');
  const [groupsSearch, setGroupsSearch] = useState('');
  const [formVendorRuleGpIds, setFormVendorRuleGpIds] = useState([]);
  const [formVendorRuleMatchByGroup, setFormVendorRuleMatchByGroup] = useState(false);
  const [vendorRuleGpSearch, setVendorRuleGpSearch] = useState('');
  const [formScopeListedGpIds, setFormScopeListedGpIds] = useState([]);
  const [formScopeListedMatchByGroup, setFormScopeListedMatchByGroup] = useState(true);
  const [scopeListedGpSearch, setScopeListedGpSearch] = useState('');
  const [formPeopleListDirectory, setFormPeopleListDirectory] = useState({
    ...EMPTY_PEOPLE_LIST_DIRECTORY,
  });
  const [formSchoolAddUsers, setFormSchoolAddUsers] = useState({
    ...EMPTY_SCHOOL_ADD_USERS,
  });
  const [formLinkPersonTypes, setFormLinkPersonTypes] = useState({
    ...EMPTY_LINK_PERSON_TYPES,
  });
  const [formMessageThreadKinds, setFormMessageThreadKinds] = useState({
    ...EMPTY_MESSAGE_THREAD_KINDS,
  });
  const [formInboxRoles, setFormInboxRoles] = useState({
    ...EMPTY_INBOX_ROLES,
  });
  const [formRelationshipTypes, setFormRelationshipTypes] = useState({
    ...EMPTY_RELATIONSHIP_TYPES,
  });
  const [formSchoolViewUsers, setFormSchoolViewUsers] = useState({
    ...EMPTY_SCHOOL_VIEW_USERS,
  });
  const [formInstructAssigned, setFormInstructAssigned] = useState({
    ...EMPTY_INSTRUCT_ASSIGNED,
  });
  const [formSupervisorKit, setFormSupervisorKit] = useState({
    ...EMPTY_SUPERVISOR_KIT,
  });
  const [formLibrarySourceKit, setFormLibrarySourceKit] = useState({
    ...EMPTY_LIBRARY_SOURCE_KIT,
  });
  const [formGradeRubricActions, setFormGradeRubricActions] = useState({
    ...EMPTY_GRADE_RUBRIC_ACTIONS,
  });
  const [userGroupsPeriodsSearch, setUserGroupsPeriodsSearch] = useState('');
  const [pagePickerOpen, setPagePickerOpen] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  /** 'target' | 'grantee' | 'rules' — create/edit sheet body tabs */
  const [permissionSheetTab, setPermissionSheetTab] = useState('target');
  const [bottomPanelHeightVh, setBottomPanelHeightVh] = useState(BOTTOM_PANEL_HEIGHT_DEFAULT_VH);
  const [isResizingBottomPanel, setIsResizingBottomPanel] = useState(false);
  const [panelTypeFilter, setPanelTypeFilter] = useState('all');
  const [panelScopeFilter, setPanelScopeFilter] = useState('all');
  const [panelSortBy, setPanelSortBy] = useState('default');
  const [groupsPeriodsHierarchyMeta, setGroupsPeriodsHierarchyMeta] = useState({});
  const [apps, setApps] = useState([]);
  const groupsPeriodsById = useMemo(
    () => new Map((groupsPeriods || []).map((gp) => [String(gp.id), gp])),
    [groupsPeriods]
  );

  /** Durable groups (groups.id) derived from loaded groups-periods — for LearnPath school grants. */
  const durableGroups = useMemo(() => {
    const byId = new Map();
    (groupsPeriods || []).forEach((gp) => {
      const id = gp.group_id != null ? String(gp.group_id) : '';
      if (!id || byId.has(id)) return;
      byId.set(id, {
        id,
        name: gp.group_name || id,
        group_type: gp.group_type || '',
        display_label: gp.display_label || '',
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
    );
  }, [groupsPeriods]);

  const groupsById = useMemo(
    () => new Map(durableGroups.map((g) => [String(g.id), g])),
    [durableGroups]
  );

  const filteredDurableGroups = useMemo(() => {
    const q = groupsSearch.trim().toLowerCase();
    if (!q) return durableGroups;
    return durableGroups.filter((g) => {
      const hay = `${g.name} ${g.group_type} ${g.display_label} ${g.id}`.toLowerCase();
      return hay.includes(q);
    });
  }, [durableGroups, groupsSearch]);

  const ownerContext = useMemo(
    () =>
      collectSystemOwnerContext(
        groupsPeriods,
        groupsPeriodsHierarchyMeta,
        userGroupsPeriods
      ),
    [groupsPeriods, groupsPeriodsHierarchyMeta, userGroupsPeriods]
  );

  const platformOwnerViewer = useMemo(
    () =>
      viewerIsPlatformOwner({
        userId: authUser?.id,
        currentRole,
        enrollments: availableUgp,
        ownerUserIds: ownerContext.ownerUserIds
      }),
    [authUser?.id, currentRole, availableUgp, ownerContext.ownerUserIds]
  );

  const applyOwnerGranteeProtection = !platformOwnerViewer;

  const modalOwnerReadOnly = useMemo(() => {
    if (platformOwnerViewer || !editingPermission) return false;
    return permissionTouchesOwnerGranteesFromLists(
      editingPermission,
      ownerContext,
      groupsPeriods,
      groupsPeriodsHierarchyMeta,
      groupsPeriodsById
    );
  }, [
    platformOwnerViewer,
    editingPermission,
    ownerContext,
    groupsPeriods,
    groupsPeriodsHierarchyMeta,
    groupsPeriodsById
  ]);

  const showOwnerProtectedBanner = useMemo(() => {
    if (platformOwnerViewer) return false;
    if (modalOwnerReadOnly) return true;
    if (!showCreateModal) return false;
    const grantees = (formGranteesInput || '')
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((x) => x !== '*all*');
    if (grantees.length === 0) return false;
    const { ownerUserIds, ownerUgpIds, ownerGpIds } = ownerContext;
    if (formGranteeType === 'users') {
      return grantees.some((id) => ownerUserIds.has(id));
    }
    if (formGranteeType === 'user_groups_periods') {
      return grantees.some((id) => ownerUgpIds.has(id));
    }
    if (formGranteeType === 'groups_periods') {
      return grantees.some((id) => {
        if (ownerGpIds.has(id)) return true;
        const gp = groupsPeriods.find((row) => String(row.id) === id);
        return gp
          ? isProtectedOwnerGp(gp, ownerGpIds, groupsPeriodsHierarchyMeta, groupsPeriodsById)
          : false;
      });
    }
    return false;
  }, [
    platformOwnerViewer,
    modalOwnerReadOnly,
    showCreateModal,
    formGranteesInput,
    formGranteeType,
    ownerContext,
    groupsPeriods,
    groupsPeriodsHierarchyMeta,
    groupsPeriodsById
  ]);

  const permissionIncludesOwnerGrantees = (perm) =>
    permissionTouchesOwnerGranteesFromLists(
      perm,
      ownerContext,
      groupsPeriods,
      groupsPeriodsHierarchyMeta,
      groupsPeriodsById
    );

  const permissionIsOwnerProtected = (perm) =>
    applyOwnerGranteeProtection && permissionIncludesOwnerGrantees(perm);

  const hidePlatformOwnerFromViewer = applyOwnerGranteeProtection;

  const isPermissionHiddenFromViewer = (perm) =>
    hidePlatformOwnerFromViewer && permissionIncludesOwnerGrantees(perm);

  const isPlatformOwnerGpHidden = (gp) =>
    hidePlatformOwnerFromViewer &&
    isProtectedOwnerGp(
      gp,
      ownerContext.ownerGpIds,
      groupsPeriodsHierarchyMeta,
      groupsPeriodsById
    );

  const isPlatformOwnerUgpHidden = (ugp) =>
    hidePlatformOwnerFromViewer &&
    isProtectedOwnerUgp(ugp, ownerContext.ownerUgpIds, ownerContext.ownerGpIds);

  const formSelectionTouchesOwnerGrantees = (granteeType, grantees) => {
    if (!applyOwnerGranteeProtection) return false;
    const { ownerUserIds, ownerUgpIds, ownerGpIds } = ownerContext;
    const list = (grantees || []).map(String);
    if (granteeType === 'users') {
      return list.some((id) => isProtectedOwnerUserId(id, ownerUserIds));
    }
    if (granteeType === 'user_groups_periods') {
      return list.some((id) => ownerUgpIds.has(id));
    }
    if (granteeType === 'groups_periods') {
      return list.some((id) => {
        if (ownerGpIds.has(id)) return true;
        const gp = groupsPeriods.find((row) => String(row.id) === id);
        return gp
          ? isProtectedOwnerGp(gp, ownerGpIds, groupsPeriodsHierarchyMeta, groupsPeriodsById)
          : false;
      });
    }
    return false;
  };

  const getGpOrganizationLabel = (gp, byId, hierarchyMeta) => {
    if (!gp) return 'Other / System';
    const meta = hierarchyMeta?.[String(gp.id)];
    if (meta?.organization) return String(meta.organization);
    if (gp.organization_name) return String(gp.organization_name);
    const visited = new Set();
    let cur = gp;
    while (cur && !visited.has(String(cur.id))) {
      visited.add(String(cur.id));
      if (String(cur.group_type || '').toLowerCase() === 'organization') {
        return String(cur.group_name || 'Organization');
      }
      const parentGpId = cur.parent_groups_periods_id ? String(cur.parent_groups_periods_id) : '';
      if (!parentGpId) break;
      cur = byId.get(parentGpId);
    }
    if (gp.parent_group_name && gp.parent_group_name !== 'System') return String(gp.parent_group_name);
    return 'Other / System';
  };

  const getUgpOrganizationLabel = (ugp) => {
    const gpId = ugp?.groups_periods_id || ugp?.groups_periods_id_full;
    const gp = gpId ? groupsPeriodsById.get(String(gpId)) : null;
    return getGpOrganizationLabel(gp, groupsPeriodsById, groupsPeriodsHierarchyMeta);
  };

  const groupedTeamsForIdExcludePicker = useMemo(() => {
    const byGpId = new Map((groupsPeriods || []).map((gp) => [String(gp.id), gp]));
    const teamByGroupId = new Map();
    (groupsPeriods || []).forEach((gp) => {
      const gid = gp.group_id;
      if (!gid) return;
      const type = String(gp.group_type || '').toLowerCase();
      if (type === 'organization' || type === 'system') return;
      const key = String(gid);
      if (!teamByGroupId.has(key)) {
        teamByGroupId.set(key, {
          id: key,
          name: String(gp.group_name || gid).trim() || gid,
          organization: getGpOrganizationLabel(gp, byGpId, groupsPeriodsHierarchyMeta),
        });
      }
    });
    const byOrg = new Map();
    teamByGroupId.forEach((team) => {
      const org = team.organization || 'Other / System';
      if (!byOrg.has(org)) byOrg.set(org, []);
      byOrg.get(org).push(team);
    });
    return Array.from(byOrg.entries())
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
      .map(([organization, items]) => ({
        organization,
        items: items.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [groupsPeriods, groupsPeriodsHierarchyMeta]);

  useEffect(() => {
    if (formConfigRoleGroupIdExcludes.length === 0) {
      setExcludeTeamsDetailsOpen(false);
    }
  }, [formConfigRoleGroupIdExcludes.length]);

  // Re-hydrate vendor_group_ids → GP checkboxes once groupsPeriods is available.
  useEffect(() => {
    if (!editingPermission || !groupsPeriods.length) return;
    const rules = parsePermissionRules(editingPermission.rules);
    if (!rules?.vendor_group_ids?.length) return;
    if (formVendorRuleGpIds.length > 0) return;
    const { gpIds, matchByGroup } = vendorRuleGpIdsFromRules(
      editingPermission.rules,
      groupsPeriods
    );
    if (gpIds.length) {
      setFormVendorRuleGpIds(gpIds);
      setFormVendorRuleMatchByGroup(matchByGroup);
    }
  }, [editingPermission, groupsPeriods, formVendorRuleGpIds.length]);

  // Re-hydrate scope_group_ids → GP checkboxes for scope.ListedGroups.
  useEffect(() => {
    if (!editingPermission || !groupsPeriods.length) return;
    const rules = parsePermissionRules(editingPermission.rules);
    if (!rules?.scope_group_ids?.length && !rules?.scope_groups_periods_ids?.length) return;
    if (formScopeListedGpIds.length > 0) return;
    const { gpIds, matchByGroup } = scopeListedGpIdsFromRules(
      editingPermission.rules,
      groupsPeriods
    );
    if (gpIds.length) {
      setFormScopeListedGpIds(gpIds);
      setFormScopeListedMatchByGroup(matchByGroup);
    }
  }, [editingPermission, groupsPeriods, formScopeListedGpIds.length]);

  const groupedGroupsPeriods = useMemo(() => {
    const q = groupPeriodsSearch.trim().toLowerCase();
    const visible = groupsPeriods.filter((gp) => {
      if (isHiddenGroupsPeriodGrantee(gp, groupsPeriodsHierarchyMeta)) return false;
      if (isPlatformOwnerGpHidden(gp)) return false;
      if (!q) return true;
      return `${gp.group_name || ''} ${gp.period_title || ''} ${gp.period_id || ''} ${gp.group_type || ''}`
        .toLowerCase()
        .includes(q);
    });
    const byId = new Map((groupsPeriods || []).map((gp) => [String(gp.id), gp]));
    const map = new Map();
    visible.forEach((gp) => {
      const org = getGpOrganizationLabel(gp, byId, groupsPeriodsHierarchyMeta);
      if (!map.has(org)) map.set(org, []);
      map.get(org).push(gp);
    });

    const orderInOrganization = (items) => {
      const ids = new Set(items.map((gp) => String(gp.id)));
      const children = new Map();
      const roots = [];
      const keyLabel = (gp) => `${gp.group_name || ''} ${gp.period_title || gp.period_id || ''}`.toLowerCase();
      const compare = (a, b) => keyLabel(a).localeCompare(keyLabel(b));

      const parentIdFor = (gp) => {
        const direct = gp.parent_groups_periods_id ? String(gp.parent_groups_periods_id) : '';
        if (direct) return direct;
        const meta = groupsPeriodsHierarchyMeta?.[String(gp.id)];
        return meta?.parentGpId ? String(meta.parentGpId) : '';
      };
      items.forEach((gp) => {
        const parentId = parentIdFor(gp);
        if (!parentId || !ids.has(parentId)) {
          roots.push(gp);
          return;
        }
        if (!children.has(parentId)) children.set(parentId, []);
        children.get(parentId).push(gp);
      });

      roots.sort(compare);
      children.forEach((arr) => arr.sort(compare));

      const out = [];
      const visit = (gp, depth, isLast, pipes, guard = new Set()) => {
        const id = String(gp.id);
        if (guard.has(id)) return;
        const nextGuard = new Set(guard);
        nextGuard.add(id);
        out.push({ ...gp, _depth: depth, _isLast: isLast, _pipes: pipes });
        const kids = children.get(id) || [];
        kids.forEach((child, index) => {
          visit(child, depth + 1, index === kids.length - 1, [...pipes, !isLast], nextGuard);
        });
      };
      roots.forEach((gp, index) => visit(gp, 0, index === roots.length - 1, []));

      // If some items weren't connected (bad parent refs), append deterministically.
      const seen = new Set(out.map((x) => String(x.id)));
      const orphans = items
        .filter((gp) => !seen.has(String(gp.id)))
        .filter((gp) => !isHiddenGroupsPeriodGrantee(gp, groupsPeriodsHierarchyMeta))
        .sort(compare);
      orphans.forEach((gp, index) => {
        out.push({
          ...gp,
          _depth: 0,
          _isLast: index === orphans.length - 1,
          _pipes: []
        });
      });
      return out;
    };

    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([organization, items]) => ({
        organization,
        items: orderInOrganization(items)
      }))
      .filter((group) => group.items.length > 0);
  }, [
    groupsPeriods,
    groupPeriodsSearch,
    groupsPeriodsHierarchyMeta,
    groupsPeriodsById,
    ownerContext,
    hidePlatformOwnerFromViewer,
  ]);

  const vendorRuleGpChoices = useMemo(() => {
    const q = vendorRuleGpSearch.trim().toLowerCase();
    return (groupsPeriods || [])
      .filter((gp) => {
        if (isHiddenGroupsPeriodGrantee(gp, groupsPeriodsHierarchyMeta)) return false;
        if (!q) return true;
        return `${gp.group_name || ''} ${gp.period_title || ''} ${gp.period_id || ''} ${gp.group_type || ''}`
          .toLowerCase()
          .includes(q);
      })
      .slice(0, 250);
  }, [groupsPeriods, groupsPeriodsHierarchyMeta, vendorRuleGpSearch]);

  const scopeListedGpChoices = useMemo(() => {
    const q = scopeListedGpSearch.trim().toLowerCase();
    return (groupsPeriods || [])
      .filter((gp) => {
        if (isHiddenScopeListedGroupsPeriod(gp, groupsPeriodsHierarchyMeta)) return false;
        if (!q) return true;
        return `${gp.group_name || ''} ${gp.period_title || ''} ${gp.period_id || ''} ${gp.group_type || ''}`
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => {
        const ta = String(a.group_type || '').toLowerCase();
        const tb = String(b.group_type || '').toLowerCase();
        const rank = (t) => (t === 'organization' ? 0 : t === 'team' ? 1 : 2);
        const rd = rank(ta) - rank(tb);
        if (rd) return rd;
        return String(a.group_name || '').localeCompare(String(b.group_name || ''), undefined, {
          sensitivity: 'base',
        });
      })
      .slice(0, 400);
  }, [groupsPeriods, groupsPeriodsHierarchyMeta, scopeListedGpSearch]);

  const groupedUserGroupsPeriods = useMemo(() => {
    const q = userGroupsPeriodsSearch.trim().toLowerCase();
    const visible = userGroupsPeriods.filter((ugp) => {
      if (isPlatformOwnerUgpHidden(ugp)) return false;
      if (!q) return true;
      const org = getUgpOrganizationLabel(ugp);
      return `${ugp.first_name || ''} ${ugp.last_name || ''} ${ugp.email || ''} ${ugp.user_email || ''} ${ugp.group_name || ''} ${ugp.period_title || ''} ${ugp.period_code || ''} ${org}`
        .toLowerCase()
        .includes(q);
    });
    const map = new Map();
    visible.forEach((ugp) => {
      const org = getUgpOrganizationLabel(ugp);
      if (!map.has(org)) map.set(org, []);
      map.get(org).push(ugp);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
      .map(([organization, items]) => ({
        organization,
        items: items.sort((a, b) =>
          userGroupsPeriodsLabel(a).localeCompare(userGroupsPeriodsLabel(b), undefined, { sensitivity: 'base' })
        )
      }));
  }, [
    userGroupsPeriods,
    userGroupsPeriodsSearch,
    groupsPeriodsById,
    groupsPeriodsHierarchyMeta,
    ownerContext,
    hidePlatformOwnerFromViewer
  ]);

  useEffect(() => {
    loadPermissions();
    loadPlatformPermissions();
    loadGroupsPeriods();
    loadUserGroupsPeriods();
    loadPasswordResetPolicy();
  }, []);

  const loadPasswordResetPolicy = async () => {
    setResetPolicyLoading(true);
    try {
      const res = await fetchWithAuth('/api/auth/password-reset-policy');
      const data = await res.json();
      if (data.success && Array.isArray(data.data?.rules)) {
        setResetPolicyRules(
          data.data.rules.map((rule) => ({
            group_name_includes: rule.group_name_includes || '',
            relationship_type: rule.relationship_type || '',
            parent_group_name_equals: rule.parent_group_name_equals || '',
            groups_periods_ids: Array.isArray(rule.groups_periods_ids)
              ? rule.groups_periods_ids.join(', ')
              : rule.groups_periods_id || '',
          }))
        );
      } else {
        setResetPolicyRules([]);
      }
    } catch (err) {
      console.error('Failed to load password reset policy:', err);
    } finally {
      setResetPolicyLoading(false);
    }
  };

  const handleSavePasswordResetPolicy = async () => {
    setResetPolicySaving(true);
    try {
      const rules = resetPolicyRules
        .map((rule) => {
          const next = {};
          if ((rule.group_name_includes || '').trim()) {
            next.group_name_includes = rule.group_name_includes.trim();
          }
          if ((rule.relationship_type || '').trim()) {
            next.relationship_type = rule.relationship_type.trim();
          }
          if ((rule.parent_group_name_equals || '').trim()) {
            next.parent_group_name_equals = rule.parent_group_name_equals.trim();
          }
          const gpIds = (rule.groups_periods_ids || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          if (gpIds.length > 0) {
            next.groups_periods_ids = gpIds;
          }
          return next;
        })
        .filter((rule) => Object.keys(rule).length > 0);

      const res = await fetchWithAuth('/api/auth/password-reset-policy', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to save password reset policy');
      }
      setCornerNotice({ message: 'Password reset block policy saved', type: 'success' });
      await loadPasswordResetPolicy();
    } catch (err) {
      setModalData({
        title: 'Error',
        message: 'Failed to save password reset policy',
        details: err.message,
      });
      setShowErrorModal(true);
    } finally {
      setResetPolicySaving(false);
    }
  };

  const addResetPolicyRule = () => {
    setResetPolicyRules((prev) => [
      ...prev,
      {
        group_name_includes: '',
        relationship_type: '',
        parent_group_name_equals: '',
        groups_periods_ids: '',
      },
    ]);
  };

  const updateResetPolicyRule = (index, field, value) => {
    setResetPolicyRules((prev) =>
      prev.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule))
    );
  };

  const removeResetPolicyRule = (index) => {
    setResetPolicyRules((prev) => prev.filter((_, i) => i !== index));
  };

  const loadApps = async () => {
    try {
      const res = await fetchWithAuth('/api/apps');
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setApps(data.data);
      }
    } catch (err) {
      console.error('Failed to load apps:', err);
    }
  };

  const loadPermissions = async () => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth('/api/page-configurations');
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setPermissions(data.data.filter(p => p.permission_type !== 'global_page_configuration'));
      } else {
        setPermissions([]);
      }
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setModalData({ title: 'Error', message: 'Failed to load permissions', details: err.message });
      setShowErrorModal(true);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPlatformPermissions = async () => {
    try {
      const res = await fetchWithAuth('/api/platform-permissions');
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setPlatformPermissions(data.data);
      } else {
        setPlatformPermissions([]);
      }
    } catch (err) {
      console.error('Failed to load platform permissions:', err);
      setPlatformPermissions([]);
    }
  };

  const loadGroupsPeriods = async () => {
    try {
      const res = await fetchWithAuth('/api/groups-periods');
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setGroupsPeriods(data.data);
        buildGroupsPeriodsHierarchyMeta(data.data);
      }
    } catch (err) {
      console.error('Failed to load groups-periods:', err);
    }
  };

  const buildGroupsPeriodsHierarchyMeta = async (allGpRows) => {
    try {
      const orgRows = (allGpRows || []).filter(
        (gp) => String(gp.group_type || '').toLowerCase() === 'organization'
      );
      const systemRows = (allGpRows || []).filter(
        (gp) => String(gp.group_type || '').toLowerCase() === 'system'
      );
      const nextMeta = {};
      orgRows.forEach((org) => {
        nextMeta[String(org.id)] = {
          organization: org.group_name || 'Organization',
          parentGpId: null,
          depth: 0
        };
      });
      systemRows.forEach((sys) => {
        nextMeta[String(sys.id)] = {
          organization: SYSTEM_GROUP_NAME,
          parentGpId: null,
          depth: 0
        };
      });

      const loadNested = async (rootGp, organizationLabel) => {
        try {
          const r = await fetchWithAuth(
            `/api/relationships/nested-groups?superparent_groups_periods_id=${encodeURIComponent(rootGp.id)}`
          );
          const d = await r.json();
          return {
            organizationLabel,
            groups: d.success && Array.isArray(d.data) ? d.data : []
          };
        } catch {
          return { organizationLabel, groups: [] };
        }
      };

      const nestedResults = await Promise.all([
        ...orgRows.map((org) => loadNested(org, org.group_name || 'Organization')),
        ...systemRows.map((sys) => loadNested(sys, SYSTEM_GROUP_NAME))
      ]);

      nestedResults.forEach(({ organizationLabel, groups }) => {
        groups.forEach((g) => {
          (g.periods || []).forEach((p) => {
            nextMeta[String(p.id)] = {
              organization: organizationLabel,
              parentGpId: p.parent_groups_periods_id || g.parent_groups_periods_id || null,
              depth: null
            };
          });
        });
      });

      const depthMemo = {};
      const depthOf = (gpId, guard = new Set()) => {
        const id = String(gpId || '');
        if (!id) return 0;
        if (depthMemo[id] != null) return depthMemo[id];
        if (guard.has(id)) return 0;
        const row = nextMeta[id];
        if (!row || !row.parentGpId) {
          depthMemo[id] = row?.depth ?? 0;
          return depthMemo[id];
        }
        const nextGuard = new Set(guard);
        nextGuard.add(id);
        const parentDepth = depthOf(row.parentGpId, nextGuard);
        depthMemo[id] = parentDepth + 1;
        return depthMemo[id];
      };
      Object.keys(nextMeta).forEach((id) => {
        nextMeta[id].depth = depthOf(id);
      });
      setGroupsPeriodsHierarchyMeta(nextMeta);
    } catch (error) {
      console.error('Failed to build groups-periods hierarchy map:', error);
      setGroupsPeriodsHierarchyMeta({});
    }
  };

  const loadUserGroupsPeriods = async () => {
    try {
      const res = await fetchWithAuth('/api/user-groups-periods?active=true');
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setUserGroupsPeriods(data.data);
      }
    } catch (err) {
      console.error('Failed to load user-groups-periods:', err);
    }
  };

  useEffect(() => {
    if (showCreateModal && formPage === 'user-profiles' && formPermissionType === 'page_configuration') {
      if (formUserProfileViewJson === '') {
        const layout = getUserProfileViewLayoutFromConfig(formConfig);
        setFormUserProfileViewJson(stringifyUserProfileViewLayout(layout));
      }
      loadApps();
    }
  }, [showCreateModal, formPage, formPermissionType, formConfig, formUserProfileViewJson]);

  const resetForm = () => {
    setFormScope('page');
    setFormPlatformId('acuitycte');
    setFormPlatformKey(PLATFORM_ACCESS_RESOURCE_ID);
    setFormPermissionName('');
    setFormPermissionType('page_configuration');
    setFormPage('dashboard');
    setFormGranteeType('groups_periods');
    setFormGrantees([]);
    setFormGranteesInput('');
    setFormAccess(true);
    setFormConfig({});
    setFormConfigWidgets([]);
    setFormSidebarItems([]);
    setFormApprovalPipeline(normalizeHourWaiverApprovalPipeline(null));
    setFormConfigGroupsView('');
    setFormConfigRoleGroupNameIncludes('');
    setFormConfigRoleGroupNameExcludes('');
    setFormConfigRoleGroupIdExcludes([]);
    setExcludeTeamsDetailsOpen(false);
    setFormConfigGroupsCreateEntities(false);
    setFormConfigGroupsCreateOrganizations(false);
    setFormConfigGroupsCreateOwnGroup(false);
    setFormConfigGroupsCreateSameLevel(false);
    setFormConfigGroupsCreateOrgWide(false);
    setFormConfigGroupsAddMembers(false);
    setFormConfigGroupsSeeAllSameLevel(false);
    setFormConfigGroupsAddMembersOwnGroup(true);
    setFormConfigGroupsAddMembersSameLevel(false);
    setFormConfigGroupsOpenMemberProfiles(false);
    setFormConfigGroupsOpenMemberProfilesOwnGroup(true);
    setFormConfigGroupsOpenMemberProfilesSameLevel(false);
    setFormConfigGroupsViewGroupMembers(false);
    setFormConfigGroupsViewGroupMembersOwnGroup(true);
    setFormConfigGroupsViewGroupMembersSameLevel(false);
    setFormConfigGroupsViewMemberDetails(false);
    setFormConfigGroupsViewMemberDetailsOwnGroup(true);
    setFormConfigGroupsViewMemberDetailsSameLevel(false);
    setFormConfigGroupsViewMemberAccess(false);
    setFormConfigGroupsViewMemberAccessOwnGroup(true);
    setFormConfigGroupsViewMemberAccessSameLevel(false);
    setFormConfigGroupsViewDerivedGroups(false);
    setFormConfigUserProfilesEdit(false);
    setFormConfigUserProfilesEditSignIn(false);
    setFormConfigUserProfilesGrantPlatformAccess(false);
    setFormConfigUserProfilesAddPersonLinks(false);
    setFormConfigUserProfilesEditPersonLinks(false);
    setFormConfigUserProfilesRemovePersonLinks(false);
    setFormConfigUserProfilesAddAssociations(false);
    setFormConfigUserProfilesRemoveAssociations(false);
    setFormConfigUserProfilesEditStudentInformation(false);
    setFormConfigUserProfilesAddCredentials(false);
    setFormConfigUserProfilesEditCredentials(false);
    setFormConfigUserProfilesRemoveCredentials(false);
    setFormConfigUserProfilesAddAttachments(false);
    setFormConfigUserProfilesEditAttachments(false);
    setFormConfigUserProfilesRemoveAttachments(false);
    setFormConfigUserProfilesAddUgpProperties(false);
    setFormConfigUserProfilesEditUgpProperties(false);
    setFormConfigUserProfilesRemoveUgpProperties(false);
    setFormConfigUserProfilesViewForm(false);
    setFormConfigUserProfilesCreateSubmission(false);
    setFormConfigUserProfilesViewSubmission(false);
    setFormConfigUserProfilesEditSubmission(false);
    setFormConfigUserProfilesDeleteSubmission(false);
    setFormConfigUserProfilesViewQuestionAnswers(false);
    setFormConfigUserProfilesUpdatePoints(false);
    setFormConfigCollaboration(makeEmptyCollaboration());
    setFormConfigProfileEditName(false);
    setFormConfigProfileUpdatePassword(false);
    setFormConfigSchedulerCreateAllOrgs(false);
    setFormConfigSchedulerEditAllOrgs(false);
    setFormConfigSchedulerRemoveAllOrgs(false);
    setFormConfigSchedulerCreateOwnOrg(false);
    setFormConfigSchedulerEditOwnOrg(false);
    setFormConfigSchedulerRemoveOwnOrg(false);
    setFormConfigFormsViewScope('own');
    setFormConfigFormsTaxonomyScope('own_organization');
    setFormConfigFormsCreate(false);
    setFormConfigFormsEdit(false);
    setFormConfigFormsDelete(false);
    setFormConfigFormsAssign(false);
    setFormConfigWsAssignForms(false);
    setFormConfigWsViewScope('linked_students');
    setFormConfigWsCurate(false);
    setFormConfigWsRemove(false);
    setFormConfigNotifyOnInquiry(false);
    setFormUserProfileViewJson('');
    setFormUserProfileViewJsonError('');
    setGroupPeriodsSearch('');
    setGroupsSearch('');
    setUserGroupsPeriodsSearch('');
    setFormVendorRuleGpIds([]);
    setFormVendorRuleMatchByGroup(false);
    setVendorRuleGpSearch('');
    setFormScopeListedGpIds([]);
    setFormScopeListedMatchByGroup(true);
    setScopeListedGpSearch('');
    setFormPeopleListDirectory({ ...EMPTY_PEOPLE_LIST_DIRECTORY });
    setFormSchoolAddUsers({ ...EMPTY_SCHOOL_ADD_USERS });
    setFormLinkPersonTypes({ ...EMPTY_LINK_PERSON_TYPES });
    setFormMessageThreadKinds({ ...EMPTY_MESSAGE_THREAD_KINDS });
    setFormInboxRoles({ ...EMPTY_INBOX_ROLES });
    setFormRelationshipTypes({ ...EMPTY_RELATIONSHIP_TYPES });
    setFormSchoolViewUsers({ ...EMPTY_SCHOOL_VIEW_USERS });
    setFormInstructAssigned({ ...EMPTY_INSTRUCT_ASSIGNED });
    setFormSupervisorKit({ ...EMPTY_SUPERVISOR_KIT });
    setFormLibrarySourceKit({ ...EMPTY_LIBRARY_SOURCE_KIT });
    setFormGradeRubricActions({ ...EMPTY_GRADE_RUBRIC_ACTIONS });
    setPagePickerOpen(false);
    setProductPickerOpen(false);
    setPermissionSheetTab('target');
    setEditingId(null);
    setEditingPermission(null);
  };

  const splitGranteesInput = (input) => (
    input.trim()
      ? input.split(/[,\s]+/).map(s => s.trim()).filter(Boolean)
      : []
  );

  const normalizeGranteesForType = (granteeType, rawGrantees) => {
    const list = Array.from(new Set((rawGrantees || []).map(String).map(v => v.trim()).filter(Boolean)));
    if (granteeType === 'user_groups_periods') {
      return list.filter(v => v !== '*all*' && v !== '*global*');
    }
    if (granteeType === 'groups_periods') {
      if (list.includes('*all*')) return ['*all*'];
      return list.filter(v => v !== '*global*');
    }
    if (granteeType === 'global') {
      return ['*global*'];
    }
    return list.filter(v => v !== '*global*');
  };

  const closeCreateModal = () => {
    if (isSaving) return;
    setPagePickerOpen(false);
    setProductPickerOpen(false);
    setShowCreateModal(false);
  };

  const handleFormPageChange = (pageId) => {
    setFormPage(pageId);
    setPagePickerOpen(false);
    if (pageId === 'acuitysis') {
      setFormPermissionType('platform_access');
      setFormAccess(true);
      return;
    }
    if (formPage === 'acuitysis' || formPermissionType === 'platform_access') {
      setFormPermissionType(PAGES_WITH_CONFIG.has(pageId) ? 'page_configuration' : 'page_access');
      return;
    }
    if (!editingId) {
      setFormPermissionType(PAGES_WITH_CONFIG.has(pageId) ? 'page_configuration' : 'page_access');
    }
  };

  const handleFormGranteeTypeChange = (nextType) => {
    if (nextType === formGranteeType) return;
    if (!formAllowedGranteeTypes.includes(nextType)) return;
    const current = splitGranteesInput(formGranteesInput);
    const normalized = normalizeGranteesForType(nextType, current);
    setFormGranteeType(nextType);
    setFormGranteesInput(normalized.join(', '));
  };

  const openCreateModal = (pageId = '') => {
    resetForm();
    setFormScope('page');
    const initialPage = pageId || 'dashboard';
    setFormPage(initialPage);
    setFormPermissionType(
      initialPage === 'acuitysis'
        ? 'platform_access'
        : PAGES_WITH_CONFIG.has(initialPage) ? 'page_configuration' : 'page_access'
    );
    if (initialPage === 'acuitysis') setFormAccess(true);
    setPagePickerOpen(false);
    setShowCreateModal(true);
  };

  const openCreatePlatformModal = (productId = 'acuitycte') => {
    resetForm();
    setFormScope('platform');
    setFormPlatformId(productId || 'acuitycte');
    setFormPermissionType('platform_access');
    setFormPlatformKey(PLATFORM_ACCESS_RESOURCE_ID);
    setFormAccess(true);
    setPagePickerOpen(false);
    setProductPickerOpen(false);
    setShowCreateModal(true);
  };

  const handlePlatformProductChange = (productId) => {
    setFormPlatformId(productId);
    setProductPickerOpen(false);
    setPagePickerOpen(false);
    const nextType =
      formPermissionType === 'platform_access' ? 'page_access' : formPermissionType;
    const keys = getPlatformKeysForType(productId, nextType === 'platform_access' ? 'page_access' : nextType);
    if (
      formPlatformKey === PLATFORM_ACCESS_RESOURCE_ID ||
      formPermissionType === 'platform_access'
    ) {
      setFormPlatformKey(PLATFORM_ACCESS_RESOURCE_ID);
      setFormPermissionType('platform_access');
      return;
    }
    if (!keys.some((k) => k.id === formPlatformKey)) {
      setFormPlatformKey('');
    }
  };

  const handlePlatformResourceChange = (resourceId) => {
    setPagePickerOpen(false);
    setProductPickerOpen(false);
    if (resourceId === PLATFORM_ACCESS_RESOURCE_ID) {
      setFormPlatformKey(PLATFORM_ACCESS_RESOURCE_ID);
      setFormPermissionType('platform_access');
      setFormAccess(true);
      return;
    }
    setFormPlatformKey(resourceId);
    if (formPermissionType === 'platform_access') {
      setFormPermissionType('page_access');
      setFormAccess(true);
    }
    if (resourceId === 'app.sidebar') {
      setFormSidebarItems((prev) =>
        prev.length ? prev : DEFAULT_LEARNPATH_SIDEBAR_ITEMS.map((x) => ({ ...x }))
      );
    }
    if (resourceId === HOUR_WAIVER_APPROVAL_PIPELINE_KEY) {
      setFormApprovalPipeline((prev) =>
        Array.isArray(prev) && prev.length
          ? prev
          : normalizeHourWaiverApprovalPipeline(null)
      );
    }
  };

  const handlePlatformPermissionTypeChange = (nextType) => {
    setPagePickerOpen(false);
    setProductPickerOpen(false);
    if (nextType === 'page_access') {
      const pages = getPlatformKeysForType(formPlatformId, 'page_access');
      if (
        formPlatformKey === PLATFORM_ACCESS_RESOURCE_ID ||
        formPermissionType === 'platform_access' ||
        !pages.some((k) => k.id === formPlatformKey)
      ) {
        setFormPlatformKey(PLATFORM_ACCESS_RESOURCE_ID);
        setFormPermissionType('platform_access');
      } else {
        setFormPermissionType('page_access');
      }
      setFormAccess(true);
      return;
    }
    setFormPermissionType(nextType);
    const keys = getPlatformKeysForType(formPlatformId, nextType);
    if (
      formPlatformKey === PLATFORM_ACCESS_RESOURCE_ID ||
      !keys.some((k) => k.id === formPlatformKey)
    ) {
      setFormPlatformKey('');
    }
    if (nextType === 'capability') {
      setFormAccess(true);
    }
  };

  const openEditModal = (perm) => {
    if (isPermissionHiddenFromViewer(perm)) return;
    const platformId = perm.platform || perm.product_id;
    const isPlatform = Boolean(platformId) && platformId !== 'main';
    if (isPlatform) {
      setFormScope('platform');
      setFormPlatformId(platformId || 'acuitycte');
      setFormPermissionType(perm.permission_type || 'platform_access');
      setFormPlatformKey(
        perm.permission_type === 'platform_access'
          ? PLATFORM_ACCESS_RESOURCE_ID
          : canonicalPlatformResourceId(
              platformId,
              perm.permission_type,
              perm.resource_id
            ) || perm.resource_id || ''
      );
      setFormPermissionName((perm.permission_name || '').trim());
      setFormPage('dashboard');
    } else {
      setFormScope('page');
      setFormPermissionName((perm.permission_name || '').trim());
      setFormPermissionType(perm.permission_type === 'global_page_configuration' ? 'page_configuration' : (perm.permission_type || 'page_configuration'));
      setFormPage(perm.page_name || perm.resource_id);
    }
    setFormGranteeType(perm.grantee_type === 'global' ? 'groups_periods' : perm.grantee_type);
    setFormGrantees(perm.grantees || []);
    setFormGranteesInput((perm.grantees || []).join(', '));
    const accessVal = perm.access;
    setFormAccess(accessVal === false || accessVal === 'false' ? false : true);
    const rawCfg = perm.permission_type === 'global_page_configuration'
      ? (perm.metadata || perm.configuration || {})
      : (perm.configuration || {});
    const cfg = Array.isArray(rawCfg) ? {} : { ...rawCfg };
    if (Array.isArray(cfg.columns) && !cfg.user_profile_view_config) {
      cfg.user_profile_view_config = { columns: cfg.columns };
      delete cfg.columns;
    }
    setFormConfig(cfg);
    if (perm.page_name === 'user-profiles') {
      const layout = getUserProfileViewLayoutFromConfig(cfg);
      setFormUserProfileViewJson(
        stringifyUserProfileViewLayout(layout || EMPTY_USER_PROFILE_VIEW_LAYOUT)
      );
      setFormUserProfileViewJsonError('');
    }
    setFormConfigWidgets(cfg.widgets || []);
    setFormSidebarItems(
      Array.isArray(cfg.items) && cfg.items.length
        ? cfg.items.map((x) => normalizeSidebarItem(x))
        : perm.resource_id === 'app.sidebar' || perm.page_name === 'app.sidebar'
          ? DEFAULT_LEARNPATH_SIDEBAR_ITEMS.map((x) => ({ ...x }))
          : []
    );
    setFormApprovalPipeline(
      normalizeHourWaiverApprovalPipeline(
        Array.isArray(rawCfg)
          ? rawCfg
          : Array.isArray(cfg.slots) || cfg.pipelineKey
            ? cfg
            : null
      )
    );
    setFormConfigGroupsView(cfg.view || '');
    setFormConfigRoleGroupNameIncludes(Array.isArray(cfg.role_group_name_includes) ? cfg.role_group_name_includes.join(', ') : '');
    setFormConfigRoleGroupNameExcludes(Array.isArray(cfg.role_group_name_excludes) ? cfg.role_group_name_excludes.join(', ') : '');
    const roleGroupIdExcludes = Array.isArray(cfg.role_group_id_excludes)
      ? cfg.role_group_id_excludes.map(String)
      : [];
    setFormConfigRoleGroupIdExcludes(roleGroupIdExcludes);
    setExcludeTeamsDetailsOpen(roleGroupIdExcludes.length > 0);
    setFormConfigGroupsCreateEntities(cfg.can_create_entities === true);
    setFormConfigGroupsCreateOrganizations(cfg.can_create_organizations === true);
    setFormConfigGroupsCreateOwnGroup(cfg.create_entities_in_own_group === true);
    setFormConfigGroupsCreateSameLevel(cfg.create_entities_same_level === true);
    setFormConfigGroupsCreateOrgWide(cfg.create_entities_org_wide === true);
    setFormConfigGroupsAddMembers(cfg.can_add_members === true);
    setFormConfigGroupsSeeAllSameLevel(cfg.see_all_same_level_children === true);
    setFormConfigGroupsAddMembersOwnGroup(
      cfg.add_members_to_own_group == null ? true : cfg.add_members_to_own_group === true
    );
    setFormConfigGroupsAddMembersSameLevel(cfg.can_add_members_same_level === true);
    setFormConfigGroupsOpenMemberProfiles(cfg.can_open_member_profiles === true);
    setFormConfigGroupsOpenMemberProfilesOwnGroup(
      cfg.open_member_profiles_to_own_group == null
        ? true
        : cfg.open_member_profiles_to_own_group === true
    );
    setFormConfigGroupsOpenMemberProfilesSameLevel(
      cfg.can_open_member_profiles_same_level === true
    );
    setFormConfigGroupsViewGroupMembers(cfg.can_view_group_members === true);
    setFormConfigGroupsViewGroupMembersOwnGroup(
      cfg.view_group_members_in_own_group == null ? true : cfg.view_group_members_in_own_group === true
    );
    setFormConfigGroupsViewGroupMembersSameLevel(cfg.can_view_group_members_same_level === true);
    setFormConfigGroupsViewMemberDetails(cfg.can_view_member_details === true);
    setFormConfigGroupsViewMemberDetailsOwnGroup(
      cfg.view_member_details_in_own_group == null
        ? true
        : cfg.view_member_details_in_own_group === true
    );
    setFormConfigGroupsViewMemberDetailsSameLevel(cfg.can_view_member_details_same_level === true);
    setFormConfigGroupsViewMemberAccess(cfg.can_view_member_access === true);
    setFormConfigGroupsViewMemberAccessOwnGroup(
      cfg.view_member_access_in_own_group == null
        ? true
        : cfg.view_member_access_in_own_group === true
    );
    setFormConfigGroupsViewMemberAccessSameLevel(cfg.can_view_member_access_same_level === true);
    setFormConfigGroupsViewDerivedGroups(cfg.can_view_derived_groups === true);
    setFormConfigUserProfilesEdit(cfg.can_edit_user_data === true);
    setFormConfigUserProfilesEditSignIn(cfg.can_edit_sign_in === true);
    setFormConfigUserProfilesGrantPlatformAccess(cfg.can_grant_platform_access === true);
    setFormConfigUserProfilesAddPersonLinks(cfg.can_add_person_links === true);
    setFormConfigUserProfilesRemovePersonLinks(cfg.can_remove_person_links === true);
    setFormConfigUserProfilesEditPersonLinks(cfg.can_edit_person_links === true);
    setFormConfigUserProfilesAddAssociations(cfg.can_add_associations === true);
    setFormConfigUserProfilesRemoveAssociations(cfg.can_remove_associations === true);
    setFormConfigUserProfilesEditStudentInformation(cfg.can_edit_student_information === true);
    setFormConfigUserProfilesAddCredentials(cfg.can_add_credentials === true);
    setFormConfigUserProfilesEditCredentials(cfg.can_edit_credentials === true);
    setFormConfigUserProfilesRemoveCredentials(cfg.can_remove_credentials === true);
    setFormConfigUserProfilesAddAttachments(cfg.can_add_attachments === true);
    setFormConfigUserProfilesEditAttachments(cfg.can_edit_attachments === true);
    setFormConfigUserProfilesRemoveAttachments(cfg.can_remove_attachments === true);
    setFormConfigUserProfilesAddUgpProperties(cfg.can_add_ugp_properties === true);
    setFormConfigUserProfilesEditUgpProperties(cfg.can_edit_ugp_properties === true);
    setFormConfigUserProfilesRemoveUgpProperties(cfg.can_remove_ugp_properties === true);
    setFormConfigUserProfilesViewForm(cfg.can_view_form === true);
    setFormConfigUserProfilesCreateSubmission(cfg.can_create_submission === true);
    setFormConfigUserProfilesViewSubmission(cfg.can_view_submission === true);
    setFormConfigUserProfilesEditDraft(cfg.can_edit_draft === true);
    setFormConfigUserProfilesEditSubmission(cfg.can_edit_submission === true);
    setFormConfigUserProfilesDeleteSubmission(cfg.can_delete_submission === true);
    setFormConfigUserProfilesViewQuestionAnswers(cfg.can_view_question_answers === true);
    setFormConfigUserProfilesUpdatePoints(cfg.can_update_points === true);
    setFormConfigCollaboration(normalizeCollaborationConfig(cfg.collaboration));
    setFormConfigCustomFormsSubmissionScope(cfg.custom_forms_submission_scope || 'own_profile_only');
    setFormConfigProfileEditName(cfg.can_edit_name === true);
    setFormConfigProfileUpdatePassword(cfg.can_update_password === true);
    setFormConfigSchedulerCreateAllOrgs(cfg.can_create_periods_all_organizations === true);
    setFormConfigSchedulerEditAllOrgs(cfg.can_edit_periods_all_organizations === true);
    setFormConfigSchedulerRemoveAllOrgs(cfg.can_remove_periods_all_organizations === true);
    setFormConfigSchedulerCreateOwnOrg(cfg.can_create_periods_own_organization === true);
    setFormConfigSchedulerEditOwnOrg(cfg.can_edit_periods_own_organization === true);
    setFormConfigSchedulerRemoveOwnOrg(cfg.can_remove_periods_own_organization === true);
    setFormConfigFormsViewScope(cfg.forms_view_scope || 'own');
    setFormConfigFormsTaxonomyScope(cfg.forms_taxonomy_scope || 'own_organization');
    setFormConfigFormsCreate(cfg.can_create_forms === true);
    setFormConfigFormsEdit(cfg.can_edit_forms === true);
    setFormConfigFormsDelete(cfg.can_delete_forms === true);
    setFormConfigFormsAssign(cfg.can_assign_forms === true);
    setFormConfigWsAssignForms(cfg.can_assign_work_sample_forms === true);
    setFormConfigWsViewScope(cfg.work_samples_view_scope || 'linked_students');
    setFormConfigWsCurate(cfg.can_curate_work_samples === true);
    setFormConfigWsRemove(cfg.can_remove_work_samples === true);
    setFormConfigWsSubmit(cfg.can_submit_portfolios === true);
    setFormConfigWsReview(cfg.can_review_portfolios === true);
    setFormConfigNotifyOnInquiry(cfg.notify_on_inquiry === true);
    const vendorFromRules = vendorRuleGpIdsFromRules(perm.rules, groupsPeriods);
    setFormVendorRuleGpIds(vendorFromRules.gpIds);
    setFormVendorRuleMatchByGroup(vendorFromRules.matchByGroup);
    setVendorRuleGpSearch('');
    const scopeFromRules = scopeListedGpIdsFromRules(perm.rules, groupsPeriods);
    setFormScopeListedGpIds(scopeFromRules.gpIds);
    setFormScopeListedMatchByGroup(scopeFromRules.matchByGroup);
    setScopeListedGpSearch('');
    setFormPeopleListDirectory(peopleListDirectoryFromMetadata(perm.metadata, perm.rules));
    setFormSchoolAddUsers(schoolAddUsersFromMetadata(perm.metadata));
    setFormLinkPersonTypes(linkPersonTypesFromMetadata(perm.metadata));
    setFormMessageThreadKinds(messageThreadKindsFromMetadata(perm.metadata));
    setFormInboxRoles(inboxRolesFromMetadata(perm.metadata));
    setFormRelationshipTypes(relationshipTypesFromMetadata(perm.metadata));
    setFormSchoolViewUsers(schoolViewUsersFromMetadata(perm.metadata));
    setFormInstructAssigned(instructAssignedFromMetadata(perm.metadata));
    if (String(perm.resource_id || '') === 'course.TeachWithoutEnrollment') {
      setFormSupervisorKit(supervisorKitFromMetadata(perm.metadata));
    } else {
      setFormSupervisorKit({ ...EMPTY_SUPERVISOR_KIT });
    }
    setFormLibrarySourceKit(librarySourceKitFromMetadata(perm.metadata));
    setFormGradeRubricActions(gradeRubricActionsFromMetadata(perm.metadata));
    setEditingId(perm.id);
    setEditingPermission(perm);
    setPagePickerOpen(false);
    setProductPickerOpen(false);
    setShowCreateModal(true);
  };

  const syncFormConfig = () => {
    const cfg = { ...formConfig };
    if (formPage === 'dashboard') {
      cfg.widgets = formConfigWidgets.length ? formConfigWidgets : undefined;
    } else if (formPage === 'groups') {
      cfg.view = formConfigGroupsView || undefined;
      const includesStr = (formConfigRoleGroupNameIncludes || '').trim();
      const excludesStr = (formConfigRoleGroupNameExcludes || '').trim();
      cfg.role_group_name_includes = includesStr
        ? includesStr.split(',').map(s => s.trim()).filter(Boolean)
        : undefined;
      cfg.role_group_name_excludes = excludesStr
        ? excludesStr.split(',').map(s => s.trim()).filter(Boolean)
        : undefined;
      cfg.role_group_id_excludes =
        formConfigRoleGroupIdExcludes.length > 0
          ? formConfigRoleGroupIdExcludes.map(String)
          : undefined;
      cfg.can_create_entities = formConfigGroupsCreateEntities;
      cfg.can_create_organizations = formConfigGroupsCreateOrganizations;
      cfg.create_entities_in_own_group = formConfigGroupsCreateOwnGroup;
      cfg.create_entities_same_level = formConfigGroupsCreateSameLevel;
      cfg.create_entities_org_wide = formConfigGroupsCreateOrgWide;
      cfg.can_add_members = formConfigGroupsAddMembers;
      cfg.see_all_same_level_children = formConfigGroupsSeeAllSameLevel;
      cfg.add_members_to_own_group = formConfigGroupsAddMembersOwnGroup;
      cfg.can_add_members_same_level = formConfigGroupsAddMembersSameLevel;
      cfg.can_open_member_profiles = formConfigGroupsOpenMemberProfiles;
      cfg.open_member_profiles_to_own_group = formConfigGroupsOpenMemberProfilesOwnGroup;
      cfg.can_open_member_profiles_same_level = formConfigGroupsOpenMemberProfilesSameLevel;
      cfg.can_view_group_members = formConfigGroupsViewGroupMembers;
      cfg.view_group_members_in_own_group = formConfigGroupsViewGroupMembersOwnGroup;
      cfg.can_view_group_members_same_level = formConfigGroupsViewGroupMembersSameLevel;
      cfg.can_view_member_details = formConfigGroupsViewMemberDetails;
      cfg.view_member_details_in_own_group = formConfigGroupsViewMemberDetailsOwnGroup;
      cfg.can_view_member_details_same_level = formConfigGroupsViewMemberDetailsSameLevel;
      cfg.can_view_member_access = formConfigGroupsViewMemberAccess;
      cfg.view_member_access_in_own_group = formConfigGroupsViewMemberAccessOwnGroup;
      cfg.can_view_member_access_same_level = formConfigGroupsViewMemberAccessSameLevel;
      cfg.can_view_derived_groups = formConfigGroupsViewDerivedGroups;
    } else if (formPage === 'user-profiles') {
      cfg.can_edit_user_data = formConfigUserProfilesEdit;
      cfg.can_edit_sign_in = formConfigUserProfilesEditSignIn;
      cfg.can_grant_platform_access = formConfigUserProfilesGrantPlatformAccess;
      cfg.can_add_person_links = formConfigUserProfilesAddPersonLinks;
      cfg.can_remove_person_links = formConfigUserProfilesRemovePersonLinks;
      cfg.can_edit_person_links = formConfigUserProfilesEditPersonLinks;
      cfg.can_add_associations = formConfigUserProfilesAddAssociations;
      cfg.can_remove_associations = formConfigUserProfilesRemoveAssociations;
      cfg.can_edit_student_information = formConfigUserProfilesEditStudentInformation;
      cfg.can_add_credentials = formConfigUserProfilesAddCredentials;
      cfg.can_edit_credentials = formConfigUserProfilesEditCredentials;
      cfg.can_remove_credentials = formConfigUserProfilesRemoveCredentials;
      cfg.can_add_attachments = formConfigUserProfilesAddAttachments;
      cfg.can_edit_attachments = formConfigUserProfilesEditAttachments;
      cfg.can_remove_attachments = formConfigUserProfilesRemoveAttachments;
      cfg.can_add_ugp_properties = formConfigUserProfilesAddUgpProperties;
      cfg.can_edit_ugp_properties = formConfigUserProfilesEditUgpProperties;
      cfg.can_remove_ugp_properties = formConfigUserProfilesRemoveUgpProperties;
      cfg.can_view_form = formConfigUserProfilesViewForm;
      cfg.can_create_submission = formConfigUserProfilesCreateSubmission;
      cfg.can_view_submission = formConfigUserProfilesViewSubmission;
      cfg.can_edit_draft = formConfigUserProfilesEditDraft;
      cfg.can_edit_submission = formConfigUserProfilesEditSubmission;
      cfg.can_delete_submission = formConfigUserProfilesDeleteSubmission;
      cfg.can_view_question_answers = formConfigUserProfilesViewQuestionAnswers;
      cfg.can_update_points = formConfigUserProfilesUpdatePoints;
      cfg.collaboration = normalizeCollaborationConfig(formConfigCollaboration);
      delete cfg.view_public_comments;
      delete cfg.create_public_comments;
      delete cfg.view_staff_comments;
      delete cfg.create_staff_comments;
      delete cfg.create_notes;
      delete cfg.view_notes;
      delete cfg.edit_notes;
      delete cfg.view_all_notes;
      delete cfg.view_flags;
      delete cfg.create_flags;
      delete cfg.create_work_sample_flags;
      delete cfg.resolve_flags;
      cfg.custom_forms_submission_scope = formConfigCustomFormsSubmissionScope || 'own_profile_only';
      const layout = getUserProfileViewLayoutFromConfig(formConfig);
      if (layout?.columns?.length) {
        cfg.user_profile_view_config = layout;
      } else {
        delete cfg.user_profile_view_config;
      }
      delete cfg.columns;
    } else if (formPage === 'profile') {
      cfg.can_edit_name = formConfigProfileEditName;
      cfg.can_update_password = formConfigProfileUpdatePassword;
    } else if (formPage === 'scheduler') {
      cfg.can_create_periods_all_organizations = formConfigSchedulerCreateAllOrgs;
      cfg.can_edit_periods_all_organizations = formConfigSchedulerEditAllOrgs;
      cfg.can_remove_periods_all_organizations = formConfigSchedulerRemoveAllOrgs;
      cfg.can_create_periods_own_organization = formConfigSchedulerCreateOwnOrg;
      cfg.can_edit_periods_own_organization = formConfigSchedulerEditOwnOrg;
      cfg.can_remove_periods_own_organization = formConfigSchedulerRemoveOwnOrg;
    } else if (formPage === 'forms') {
      cfg.forms_view_scope = formConfigFormsViewScope || 'own';
      cfg.forms_taxonomy_scope = formConfigFormsTaxonomyScope || 'own_organization';
      cfg.can_create_forms = formConfigFormsCreate;
      cfg.can_edit_forms = formConfigFormsEdit;
      cfg.can_delete_forms = formConfigFormsDelete;
      cfg.can_assign_forms = formConfigFormsAssign;
      delete cfg.can_comment_on_submissions;
    } else if (formPage === 'work-samples') {
      cfg.work_samples_view_scope = formConfigWsViewScope || 'linked_students';
      cfg.can_assign_work_sample_forms = formConfigWsAssignForms;
      cfg.can_curate_work_samples = formConfigWsCurate;
      delete cfg.can_edit_work_samples;
      cfg.can_remove_work_samples = formConfigWsRemove;
      cfg.can_submit_portfolios = formConfigWsSubmit;
      cfg.can_review_portfolios = formConfigWsReview;
    } else if (formPage === 'inquiries') {
      cfg.notify_on_inquiry = formConfigNotifyOnInquiry;
    }
    setFormConfig(cfg);
    return cfg;
  };

  const handleSave = async () => {
    if (
      formScope === 'page' &&
      formPermissionType === 'page_configuration' &&
      formPage === 'groups'
    ) {
      if (
        formConfigGroupsCreateEntities &&
        !formConfigGroupsCreateOrganizations &&
        !formConfigGroupsCreateOwnGroup &&
        !formConfigGroupsCreateSameLevel &&
        !formConfigGroupsCreateOrgWide
      ) {
        setModalData({
          title: 'Validation',
          message: 'Create Entities is enabled, but no create scope is selected.',
          details:
            'Enable at least one nested create option: Create organizations, Create in own group, Create in same-level peers, or Create org-wide.'
        });
        setShowErrorModal(true);
        return;
      }

      if (
        formConfigGroupsAddMembers &&
        !formConfigGroupsAddMembersOwnGroup &&
        !formConfigGroupsAddMembersSameLevel
      ) {
        setModalData({
          title: 'Validation',
          message: 'Add Members is enabled, but no member scope is selected.',
          details:
            'Enable at least one nested add-members option: Add to own group + nested, or Add to same-level peers + nested.'
        });
        setShowErrorModal(true);
        return;
      }

      if (
        formConfigGroupsOpenMemberProfiles &&
        !formConfigGroupsOpenMemberProfilesOwnGroup &&
        !formConfigGroupsOpenMemberProfilesSameLevel
      ) {
        setModalData({
          title: 'Validation',
          message: 'Open member profiles is enabled, but no scope is selected.',
          details:
            'Enable at least one nested option: own group + nested, or same-level peers + nested.'
        });
        setShowErrorModal(true);
        return;
      }

      if (
        formConfigGroupsViewGroupMembers &&
        !formConfigGroupsViewGroupMembersOwnGroup &&
        !formConfigGroupsViewGroupMembersSameLevel
      ) {
        setModalData({
          title: 'Validation',
          message: 'View group members is enabled, but no member roster scope is selected.',
          details:
            'Enable at least one nested option: own group + nested, or same-level peers + nested.'
        });
        setShowErrorModal(true);
        return;
      }

      if (
        formConfigGroupsViewMemberDetails &&
        !formConfigGroupsViewMemberDetailsOwnGroup &&
        !formConfigGroupsViewMemberDetailsSameLevel
      ) {
        setModalData({
          title: 'Validation',
          message: 'View member details is enabled, but no details scope is selected.',
          details:
            'Enable at least one nested option: own group + nested, or same-level peers + nested.'
        });
        setShowErrorModal(true);
        return;
      }

      if (
        formConfigGroupsViewMemberAccess &&
        !formConfigGroupsViewMemberAccessOwnGroup &&
        !formConfigGroupsViewMemberAccessSameLevel
      ) {
        setModalData({
          title: 'Validation',
          message: 'View member access is enabled, but no access scope is selected.',
          details:
            'Enable at least one nested option: own group + nested, or same-level peers + nested.'
        });
        setShowErrorModal(true);
        return;
      }
    }

    const parsedInputGrantees = splitGranteesInput(formGranteesInput);
    const grantees = normalizeGranteesForType(formGranteeType, parsedInputGrantees);
    if (grantees.length === 0) {
      setModalData({ title: 'Validation', message: 'At least one grantee is required — select a group-period or enter a grantee ID.', details: '' });
      setShowErrorModal(true);
      return;
    }

    if (modalOwnerReadOnly) {
      setModalData({
        title: 'Protected',
        message: 'This permission applies to the platform Owner and cannot be modified here.',
        details: OWNER_PROTECTED_HINT
      });
      setShowErrorModal(true);
      return;
    }

    if (formSelectionTouchesOwnerGrantees(formGranteeType, grantees)) {
      setModalData({
        title: 'Protected',
        message: 'Platform Owner grantees cannot be assigned or changed here.',
        details: OWNER_PROTECTED_HINT
      });
      setShowErrorModal(true);
      return;
    }

    if (formScope === 'platform') {
      if (!getPlatformProduct(formPlatformId)) {
        setModalData({
          title: 'Validation',
          message: 'Select a platform product.',
          details: ''
        });
        setShowErrorModal(true);
        return;
      }
      if (formPermissionType !== 'platform_access') {
        const keys = getPlatformKeysForType(formPlatformId, formPermissionType);
        if (!formPlatformKey || !keys.some((k) => k.id === formPlatformKey)) {
          setModalData({
            title: 'Validation',
            message: 'Select a page, capability, or configuration key from the catalog.',
            details:
              keys.length === 0
                ? `No catalog keys for ${formPlatformId} / ${formPermissionType} yet.`
                : ''
          });
          setShowErrorModal(true);
          return;
        }
      }

      if (!formAllowedGranteeTypes.includes(formGranteeType)) {
        setModalData({
          title: 'Validation',
          message: 'This capability cannot be granted to that grantee type.',
          details: `Allowed: ${formAllowedGranteeTypes.join(', ')}. school.AppearAsSchool requires Groups (groups.id). Role / scope badges require GP/UGP.`,
        });
        setShowErrorModal(true);
        return;
      }

      const platformResourceId =
        formPermissionType === 'platform_access'
          ? PLATFORM_ACCESS_RESOURCE_ID
          : canonicalPlatformResourceId(formPlatformId, formPermissionType, formPlatformKey);
      const platformRules =
        formPlatformKey === 'scope.ListedGroups'
          ? buildScopeListedRulesPayload(
              formScopeListedGpIds,
              formScopeListedMatchByGroup,
              groupsPeriods
            )
          : buildVendorRulesPayload(
              formVendorRuleGpIds,
              formVendorRuleMatchByGroup,
              groupsPeriods
            );
      if (
        formPlatformKey === 'scope.ListedGroups' &&
        (!platformRules ||
          (!platformRules.scope_group_ids?.length &&
            !platformRules.scope_groups_periods_ids?.length))
      ) {
        setModalData({
          title: 'Validation',
          message: 'Listed Groups requires at least one group or groups-period in Rules.',
          details:
            'Pick the organizations or teams this grant should cover. Prefer “Match by group” so the allowlist stores groups.id.',
        });
        setShowErrorModal(true);
        return;
      }
      if (
        formPlatformKey === 'people.ListDirectory' &&
        formAccess &&
        !peopleListDirectoryHasAnySlice(formPeopleListDirectory)
      ) {
        setModalData({
          title: 'Validation',
          message: 'List Directory requires at least one role checkbox.',
          details:
            'On Target, check Learners, Instructors, Admins, and/or Super Admins (stored on metadata.list_roles).',
        });
        setShowErrorModal(true);
        setPermissionSheetTab('target');
        return;
      }
      if (
        formPlatformKey === 'school.AddUsers' &&
        formAccess &&
        !schoolAddUsersHasAnySlice(formSchoolAddUsers)
      ) {
        setModalData({
          title: 'Validation',
          message: 'Add Users to Schools requires at least one role checkbox.',
          details:
            'On Target, check which Acuity Learn roles this grantee may attach to a school (stored on metadata.add_roles).',
        });
        setShowErrorModal(true);
        setPermissionSheetTab('target');
        return;
      }
      if (
        isMessageThreadKindKey(formPlatformKey) &&
        formAccess &&
        formMessageThreadKinds.office !== true
      ) {
        setModalData({
          title: 'Validation',
          message: `${labelForPlatformKey(formPlatformId, 'capability', formPlatformKey)} requires Office.`,
          details:
            'On Target, check Office (stored on metadata.thread_kinds). Support is in development and is not stored.',
        });
        setShowErrorModal(true);
        setPermissionSheetTab('target');
        return;
      }
      if (
        formPlatformKey === 'people.PersonLinks' &&
        formAccess &&
        !linkPersonTypesHasAny(formLinkPersonTypes)
      ) {
        setModalData({
          title: 'Validation',
          message: 'Person Links requires at least one relationship type.',
          details:
            'On Target, check Guardians, Counselors, and/or Siblings (stored on metadata.link_types).',
        });
        setShowErrorModal(true);
        setPermissionSheetTab('target');
        return;
      }
      if (
        formPlatformKey === 'dm.SendStudentRelationship' &&
        formAccess &&
        !relationshipTypesHasAny(formRelationshipTypes)
      ) {
        setModalData({
          title: 'Validation',
          message: 'Send Student Relationship requires at least one person-link type.',
          details:
            'On Target, check Guardian, Sibling, Teacher of Record, Counselor, and/or Supervisor (stored on metadata.relationship_types). None checked means this person cannot message those links.',
        });
        setShowErrorModal(true);
        setPermissionSheetTab('target');
        return;
      }
      if (
        formPlatformKey === 'dm.ViewOthersInboxes' &&
        formAccess &&
        !inboxRolesHasAny(formInboxRoles)
      ) {
        setModalData({
          title: 'Validation',
          message: 'View Others Inboxes requires at least one role.',
          details:
            'On Target, check which roles’ direct-message inboxes this person may read (stored on metadata.inbox_roles). This is not their own Messages.',
        });
        setShowErrorModal(true);
        setPermissionSheetTab('target');
        return;
      }
      if (
        formPlatformKey === 'school.ViewUsers' &&
        formAccess &&
        !schoolViewUsersHasAnySlice(formSchoolViewUsers)
      ) {
        setModalData({
          title: 'Validation',
          message: 'View People requires at least one role checkbox.',
          details:
            'On Target, check which Acuity Learn roles this grantee may see on a school roster (stored on metadata.view_roles).',
        });
        setShowErrorModal(true);
        setPermissionSheetTab('target');
        return;
      }
      if (
        isLibrarySourceKitKey(formPlatformKey) &&
        formAccess &&
        !librarySourceKitHasAny(formLibrarySourceKit)
      ) {
        setModalData({
          title: 'Validation',
          message: 'Library sources need at least one kind or action checkbox.',
          details:
            'On Target, check document / image / audio / video / URL, and whether they may replace the file or only edit tags (stored on metadata.source_kit). Size caps are optional.',
        });
        setShowErrorModal(true);
        setPermissionSheetTab('target');
        return;
      }
      if (
        formGranteeType === 'groups' ||
        formGranteeType === 'groups_periods' ||
        formGranteeType === 'user_groups_periods' ||
        formGranteeType === 'users'
      ) {
        const conflicts = findGranteePermissionConflicts({
          permissions: platformPermissions,
          productId: formPlatformId,
          resourceId: platformResourceId,
          permissionType: formPermissionType,
          granteeType: formGranteeType,
          editingId,
          grantees,
          rules: platformRules,
        });
        if (conflicts.length > 0) {
          setModalData({
            title: 'Conflict',
            message: PERMISSION_CONFLICT_MESSAGE,
            details: buildGranteeOverlapConflictDetails(conflicts, {
              granteeType: formGranteeType,
              groupsPeriodsById,
              groupsPeriods,
              userGroupsPeriods,
              groupsById,
            }),
          });
          setShowErrorModal(true);
          return;
        }
        const roleBadgeConflicts = findExclusiveRoleBadgeConflicts({
          permissions: platformPermissions,
          productId: formPlatformId,
          resourceId: platformResourceId,
          permissionType: formPermissionType,
          granteeType: formGranteeType,
          editingId,
          grantees,
        });
        if (roleBadgeConflicts.length > 0) {
          setModalData({
            title: 'Conflict',
            message: ROLE_BADGE_EXCLUSIVE_MESSAGE,
            details: buildGranteeOverlapConflictDetails(roleBadgeConflicts, {
              granteeType: formGranteeType,
              groupsPeriodsById,
              groupsPeriods,
              userGroupsPeriods,
              groupsById,
            }),
          });
          setShowErrorModal(true);
          return;
        }
        const scopeConflicts = findExclusiveScopeConflicts({
          permissions: platformPermissions,
          productId: formPlatformId,
          resourceId: platformResourceId,
          permissionType: formPermissionType,
          granteeType: formGranteeType,
          editingId,
          grantees,
        });
        if (scopeConflicts.length > 0) {
          setModalData({
            title: 'Conflict',
            message: scopeExclusiveMessageForPlatform(formPlatformId),
            details: buildGranteeOverlapConflictDetails(scopeConflicts, {
              granteeType: formGranteeType,
              groupsPeriodsById,
              groupsPeriods,
              userGroupsPeriods,
              groupsById,
            }),
          });
          setShowErrorModal(true);
          return;
        }
      }

      setIsSaving(true);
      try {
        const trimmedTitle = (formPermissionName || '').trim();
        const body = {
          product_id: formPlatformId,
          grantee_type: formGranteeType,
          grantees,
          permission_type: formPermissionType,
          permission_name: trimmedTitle || null,
          resource_id: platformResourceId,
        };
        if (editingId) body.id = editingId;
        if (
          formPermissionType === 'platform_access' ||
          formPermissionType === 'page_access' ||
          formPermissionType === 'capability'
        ) {
          body.access = formAccess;
          if (formPlatformKey === 'people.ListDirectory') {
            body.metadata = buildPeopleListDirectoryMetadataPayload(formPeopleListDirectory);
          }
          if (formPlatformKey === 'school.AddUsers') {
            body.metadata = buildSchoolAddUsersMetadataPayload(formSchoolAddUsers);
          }
          if (isMessageThreadKindKey(formPlatformKey)) {
            body.metadata = buildMessageThreadKindsMetadataPayload(formMessageThreadKinds);
          }
          if (formPlatformKey === 'people.PersonLinks') {
            body.metadata = buildLinkPersonTypesMetadataPayload(formLinkPersonTypes);
          }
          if (formPlatformKey === 'dm.SendStudentRelationship') {
            body.metadata = buildRelationshipTypesMetadataPayload(formRelationshipTypes);
          }
          if (formPlatformKey === 'dm.ViewOthersInboxes') {
            body.metadata = buildInboxRolesMetadataPayload(formInboxRoles);
          }
          if (formPlatformKey === 'school.ViewUsers') {
            body.metadata = buildSchoolViewUsersMetadataPayload(formSchoolViewUsers);
          }
          if (formPlatformKey === 'course.TeachEnrolled') {
            body.metadata = buildInstructAssignedMetadataPayload(formInstructAssigned);
          }
          if (formPlatformKey === 'course.TeachWithoutEnrollment') {
            body.metadata = buildSupervisorKitMetadataPayload(formSupervisorKit);
          }
          if (isLibrarySourceKitKey(formPlatformKey)) {
            body.metadata = buildLibrarySourceKitMetadataPayload(formLibrarySourceKit);
          }
          if (formPermissionType === 'page_access' && formPlatformKey === 'grade-rubrics') {
            body.metadata = buildGradeRubricActionsMetadataPayload(formGradeRubricActions);
          }
        } else {
          if (formPlatformKey === 'home.widgets') {
            body.configuration = { widgets: formConfigWidgets || [] };
          } else if (formPlatformKey === 'app.sidebar') {
            body.configuration = { items: serializeSidebarItems(formSidebarItems) };
          } else if (formPlatformKey === HOUR_WAIVER_APPROVAL_PIPELINE_KEY) {
            body.configuration = normalizeHourWaiverApprovalPipeline(formApprovalPipeline);
          } else {
            body.configuration = {};
          }
        }
        body.rules = platformRules;

        const res = await fetchWithAuth('/api/platform-permissions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || data.error || 'Save failed');
        }
        if (
          editingId &&
          editingPermission?.id &&
          data.data?.id &&
          String(data.data.id) !== String(editingPermission.id)
        ) {
          await fetchWithAuth(`/api/platform-permissions/${editingPermission.id}`, {
            method: 'DELETE',
          });
        }
        setCornerNotice({
          isOpen: true,
          title: 'Success',
          text: editingId ? 'Platform grant updated' : 'Platform grant created',
          position: 'bottom-left',
          containerColor: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
          textColor: '#14532d',
          showOverlay: false,
          dismissOnClick: true,
          autoClose: true,
          viewTime: 5500,
        });
        setShowCreateModal(false);
        resetForm();
        loadPlatformPermissions();
      } catch (err) {
        setModalData({
          title: 'Error',
          message: 'Failed to save platform permission',
          details: err.message,
        });
        setShowErrorModal(true);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const pagePermissionType = formPage === 'acuitysis' ? 'platform_access' : formPermissionType;
    const pageRules = buildVendorRulesPayload(
      formVendorRuleGpIds,
      formVendorRuleMatchByGroup,
      groupsPeriods
    );

    // Same page/type + overlapping grantees + same rules scope → block (not confirm).
    // GP-only vs GP+vendor are different scopes and may both exist.
    if (
      formGranteeType === 'groups_periods' ||
      formGranteeType === 'user_groups_periods' ||
      formGranteeType === 'users'
    ) {
      const conflicts = findGranteePermissionConflicts({
        permissions,
        page: formPage,
        permissionType: pagePermissionType,
        granteeType: formGranteeType,
        editingId,
        grantees,
        rules: pageRules,
      });
      if (conflicts.length > 0) {
        setModalData({
          title: 'Conflict',
          message: PERMISSION_CONFLICT_MESSAGE,
          details: buildGranteeOverlapConflictDetails(conflicts, {
            granteeType: formGranteeType,
            groupsPeriodsById,
            groupsPeriods,
            userGroupsPeriods,
            groupsById,
          }),
        });
        setShowErrorModal(true);
        return;
      }
    }

    setIsSaving(true);
    try {
      const trimmedPermissionName = (formPermissionName || '').trim();
      const body = {
        page_name: formPage,
        grantee_type: formGranteeType,
        grantees,
        permission_type: pagePermissionType,
        permission_name: trimmedPermissionName || null
      };
      if (editingId) {
        body.id = editingId;
      }
      if (formPage === 'acuitysis') {
        body.access = formAccess;
      } else if (pagePermissionType === 'page_access') {
        body.access = formAccess;
      } else {
        body.configuration = syncFormConfig();
      }
      body.rules = pageRules;
      const res = await fetchWithAuth('/api/page-configurations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (data.success) {
        const savedGrantees = new Set(grantees.map(String));
        const hasGroupsPeriodsBaseConfig = formGranteeType === 'user_groups_periods' && permissions.some((p) => {
          if (p.page_name !== formPage || p.permission_type !== formPermissionType || p.grantee_type !== 'groups_periods' || p.id === editingId) return false;
          const gpGrantees = (p.grantees || []).map(String);
          if (gpGrantees.includes('*all*')) return true;
          return userGroupsPeriods.some((ugp) => (
            savedGrantees.has(String(ugp.id)) &&
            gpGrantees.includes(String(ugp.groups_periods_id))
          ));
        });
        if (editingId && editingPermission?.id && data.data?.id && String(data.data.id) !== String(editingPermission.id)) {
          await fetchWithAuth(`/api/page-configurations/${editingPermission.id}`, { method: 'DELETE' });
        }
        const successMessage = editingId ? 'Permission updated' : 'Permission created';
        const successDetail = hasGroupsPeriodsBaseConfig
          ? 'A groups-period configuration also exists for at least one selected UGP. This UGP-specific configuration will prevail.'
          : '';
        setCornerNotice({
          isOpen: true,
          title: 'Success',
          text: successDetail ? `${successMessage} ${successDetail}` : successMessage,
          position: 'bottom-left',
          containerColor: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
          textColor: '#14532d',
          showOverlay: false,
          dismissOnClick: true,
          autoClose: true,
          viewTime: 5500
        });
        setShowCreateModal(false);
        resetForm();
        loadPermissions();
      } else {
        throw new Error(data.message || data.error || 'Save failed');
      }
    } catch (err) {
      setModalData({
        title: 'Error',
        message: 'Failed to save permission',
        details: err.message
      });
      setShowErrorModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    const pagePerm = permissions.find((p) => String(p.id) === String(deleteConfirmId));
    const platformPerm = platformPermissions.find((p) => String(p.id) === String(deleteConfirmId));
    const perm = pagePerm || platformPerm;
    if (perm && permissionIsOwnerProtected(perm)) {
      setModalData({
        title: 'Protected',
        message: 'This permission applies to the platform Owner and cannot be deleted here.',
        details: OWNER_PROTECTED_HINT
      });
      setShowErrorModal(true);
      setDeleteConfirmId(null);
      return;
    }
    setIsSaving(true);
    try {
      const endpoint = platformPerm
        ? `/api/platform-permissions/${deleteConfirmId}`
        : `/api/page-configurations/${deleteConfirmId}`;
      const res = await fetchWithAuth(endpoint, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setCornerNotice({
          isOpen: true,
          title: 'Success',
          text: 'Permission deleted',
          position: 'bottom-left',
          containerColor: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
          textColor: '#14532d',
          showOverlay: false,
          dismissOnClick: true,
          autoClose: true,
          viewTime: 5500
        });
        setDeleteConfirmId(null);
        if (platformPerm) loadPlatformPermissions();
        else loadPermissions();
      } else {
        throw new Error(data.message || data.error || 'Delete failed');
      }
    } catch (err) {
      setModalData({ title: 'Error', message: 'Failed to delete', details: err.message });
      setShowErrorModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  const formatGrantees = (perm) => {
    const arr = perm.grantees;
    if (perm.grantee_type === 'global' || (arr && arr.includes('*global*'))) return 'Global';
    if (!arr || !arr.length) return '—';
    if (perm.grantee_type === 'user_groups_periods' && arr.includes('*all*')) return 'Custom (UGP)';
    if (arr.includes('*all*')) return '*all*';
    if (perm.grantee_type === 'groups_periods' && groupsPeriods.length > 0) {
      const labels = arr.map(id => {
        const gp = groupsPeriods.find(g => String(g.id) === String(id));
        if (gp) return `${gp.group_name || '?'} — ${gp.period_title || gp.period_id || '?'}`;
        return id;
      });
      const show = labels.slice(0, 3);
      return show.join(', ') + (labels.length > 3 ? ` +${labels.length - 3}` : '');
    }
    if (perm.grantee_type === 'groups' && durableGroups.length > 0) {
      const labels = arr.map((id) => {
        const g = groupsById.get(String(id));
        if (g) {
          const type = g.group_type ? ` (${g.group_type})` : '';
          return `${g.name || '?'}${type}`;
        }
        return id;
      });
      const show = labels.slice(0, 3);
      return show.join(', ') + (labels.length > 3 ? ` +${labels.length - 3}` : '');
    }
    if (perm.grantee_type === 'user_groups_periods' && userGroupsPeriods.length > 0) {
      const labels = arr.map(id => {
        const ugp = userGroupsPeriods.find(u => String(u.id) === String(id));
        if (ugp) return userGroupsPeriodsLabel(ugp);
        return id;
      });
      const show = labels.slice(0, 2);
      return show.join(', ') + (labels.length > 2 ? ` +${labels.length - 2}` : '');
    }
    return arr.slice(0, 3).join(', ') + (arr.length > 3 ? ` +${arr.length - 3}` : '');
  };

  const granteeTypeLabel = (type) => {
    if (type === 'groups_periods') return 'Groups Periods';
    if (type === 'user_groups_periods') return 'User Groups Periods';
    if (type === 'users') return 'Users';
    if (type === 'global') return 'Global';
    return type;
  };

  const granteeTypeAcronym = (type) => {
    const entry = GRANTEE_ACRONYMS[type];
    return entry ? entry.code : clipText(type || '?', 4);
  };

  const formatConfig = (perm) => {
    let base;
    if (perm.permission_type === 'page_access') {
      const accessVal = perm.access;
      const isDeny = accessVal === false || accessVal === 'false';
      base = isDeny ? 'access: deny' : 'access: allow';
      if (!isDeny && String(perm.resource_id || '') === 'grade-rubrics') {
        const flags = gradeRubricActionsFromMetadata(perm.metadata);
        const bits = GRADE_RUBRIC_ACTION_OPTIONS.filter((opt) => flags[opt.key]).map((opt) => opt.label);
        base = `${base} · ${bits.length ? bits.join(', ') : 'view only'}`;
      }
    } else {
      base = summarizePageConfiguration(perm.configuration, {
        pageName: perm.page_name || perm.resource_id,
      });
    }
    const rules = parsePermissionRules(perm.rules);
    if (!rules) return base;
    const gpN = Array.isArray(rules.vendor_groups_periods_ids)
      ? rules.vendor_groups_periods_ids.length
      : 0;
    const groupN = Array.isArray(rules.vendor_group_ids) ? rules.vendor_group_ids.length : 0;
    if (!gpN && !groupN) return base;
    const bits = [];
    if (gpN) bits.push(`vendor GP ×${gpN}`);
    if (groupN) bits.push(`vendor group ×${groupN}`);
    return base && base !== '—' ? `${base} · ${bits.join(', ')}` : bits.join(', ');
  };

  const userProfileViewLayout = getUserProfileViewLayoutFromConfig(formConfig);

  // Column templates from DEFAULT; checked state only when present in saved layout.
  const userProfileViewDisplayColumns = (DEFAULT_USER_PROFILE_VIEW_CONFIG.columns || []).map(
    (defaultCol) => {
      const fromConfig = (userProfileViewLayout?.columns || []).find((c) => c.id === defaultCol.id);
      const merged = fromConfig || { ...defaultCol, enabled: false };
      if (defaultCol.id === 'apps' && apps.length > 0) {
        const appBuckets = apps.map((a, i) => ({
          id: `app_${a.slug}`,
          title: a.title,
          enabled: fromConfig
            ? (fromConfig.buckets || []).some((b) => b.id === `app_${a.slug}`)
            : false,
          order: i + 1
        }));
        return { ...merged, buckets: appBuckets.length ? appBuckets : merged.buckets };
      }
      return merged;
    }
  );

  const setUserProfileViewLayout = (layout) => {
    setFormConfig(prev => {
      const next = { ...(prev || {}) };
      if (layout?.columns?.length) {
        next.user_profile_view_config = layout;
      } else {
        delete next.user_profile_view_config;
      }
      delete next.columns;
      return next;
    });
    setFormUserProfileViewJson(stringifyUserProfileViewLayout(layout || EMPTY_USER_PROFILE_VIEW_LAYOUT));
    setFormUserProfileViewJsonError('');
  };

  const handleUserProfileViewToggleColumn = (columnId) => {
    const currentColumns = (userProfileViewLayout?.columns || []).slice();
    const isIncluded = currentColumns.some(c => c.id === columnId);
    const defaultCol = (DEFAULT_USER_PROFILE_VIEW_CONFIG.columns || []).find(c => c.id === columnId);
    let nextColumns;
    if (isIncluded) {
      nextColumns = currentColumns.filter(c => c.id !== columnId);
    } else if (defaultCol) {
      nextColumns = [...currentColumns, { ...defaultCol, enabled: true }].sort((a, b) => (a.order || 0) - (b.order || 0));
    } else {
      return;
    }
    setUserProfileViewLayout({ columns: nextColumns });
  };

  const handleUserProfileViewToggleBucket = (columnId, bucketId) => {
    const currentColumns = (userProfileViewLayout?.columns || []).slice();
    let col = currentColumns.find(c => c.id === columnId);
    if (!col && columnId === 'apps') {
      col = { id: 'apps', title: 'Apps', buckets: [] };
      currentColumns.push(col);
    }
    const defaultCol = (DEFAULT_USER_PROFILE_VIEW_CONFIG.columns || []).find(c => c.id === columnId);
    let defaultBucket = (defaultCol?.buckets || []).find(b => b.id === bucketId);
    if (!defaultBucket && columnId === 'apps' && bucketId.startsWith('app_')) {
      const slug = bucketId.replace(/^app_/, '');
      const app = apps.find(a => a.slug === slug);
      if (app) defaultBucket = { id: bucketId, title: app.title, enabled: true, order: 0 };
    }
    if (!col) return;
    const currentBuckets = (col.buckets || []).slice();
    const isIncluded = currentBuckets.some(b => b.id === bucketId);
    let nextBuckets;
    if (isIncluded) {
      nextBuckets = currentBuckets.filter(b => b.id !== bucketId);
    } else if (defaultBucket) {
      nextBuckets = [...currentBuckets, { ...defaultBucket, enabled: true }].sort((a, b) => (a.order || 0) - (b.order || 0));
    } else {
      return;
    }
    setUserProfileViewLayout({
      columns: currentColumns.map(c => c.id === columnId ? { ...c, buckets: nextBuckets } : c)
    });
  };

  const handleUserProfileViewClearLayout = () => {
    setUserProfileViewLayout(EMPTY_USER_PROFILE_VIEW_LAYOUT);
  };

  const handleUserProfileViewLoadPresetLayout = () => {
    setUserProfileViewLayout(DEFAULT_USER_PROFILE_VIEW_CONFIG);
  };

  const handleUserProfileViewApplyJson = () => {
    try {
      const trimmed = (formUserProfileViewJson || '').trim();
      const parsed = JSON.parse(trimmed || '{"columns":[]}');
      if (Array.isArray(parsed)) {
        setUserProfileViewLayout({ columns: parsed });
        setFormUserProfileViewJsonError('');
        return;
      }
      if (!parsed || typeof parsed !== 'object') {
        setFormUserProfileViewJsonError('JSON must be an object with "columns" or a columns array.');
        return;
      }
      const layout = Array.isArray(parsed.columns)
        ? parsed
        : parsed.user_profile_view_config || parsed;
      if (!Array.isArray(layout.columns)) {
        setFormUserProfileViewJsonError('JSON must include a "columns" array.');
        return;
      }
      setUserProfileViewLayout(layout);
      setFormUserProfileViewJsonError('');
    } catch (e) {
      setFormUserProfileViewJsonError(e.message || 'Invalid JSON');
    }
  };

  const permissionsByPage = useMemo(
    () =>
      KNOWN_PAGES.map((page) => ({
        ...page,
        permissions: permissions
          .filter((p) => p.page_name === page.id)
          .filter((p) => !isPermissionHiddenFromViewer(p)),
      })).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [permissions, hidePlatformOwnerFromViewer, ownerContext, groupsPeriods, groupsPeriodsHierarchyMeta, groupsPeriodsById]
  );

  const selectedPage = useMemo(
    () => permissionsByPage.find(p => p.id === selectedPageId) || null,
    [permissionsByPage, selectedPageId]
  );

  const showRoleTemplates = selectedPageId === ROLE_TEMPLATES_PANEL_ID;
  const platformProducts = usePlatformPermissionsByProduct(
    platformPermissions,
    isPermissionHiddenFromViewer
  );
  const selectedPlatformProduct =
    platformProducts.find((p) => p.id === selectedPlatformId) || null;
  const showBottomPanel =
    Boolean(selectedPage) || showRoleTemplates || Boolean(selectedPlatformProduct);

  const bottomPanelStyle = useMemo(
    () => ({
      ...styles.bottomPanel,
      top: `${100 - bottomPanelHeightVh}vh`
    }),
    [bottomPanelHeightVh]
  );

  useEffect(() => {
    if (!isResizingBottomPanel) return undefined;
    const onMove = (e) => {
      const next =
        ((window.innerHeight - e.clientY) / Math.max(window.innerHeight, 1)) * 100;
      setBottomPanelHeightVh(
        Math.min(
          BOTTOM_PANEL_HEIGHT_MAX_VH,
          Math.max(BOTTOM_PANEL_HEIGHT_MIN_VH, Math.round(next))
        )
      );
    };
    const onUp = () => setIsResizingBottomPanel(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingBottomPanel]);

  useEffect(() => {
    setPanelTypeFilter('all');
    setPanelScopeFilter('all');
    setPanelSortBy('default');
    setPlatformGrantView('rows');
  }, [selectedPageId, selectedPlatformId]);

  const togglePlatformSelection = (productId) => {
    setSelectedPageId(null);
    setSelectedPlatformId((prev) => (prev === productId ? null : productId));
  };

  const togglePageSelection = (pageId) => {
    setSelectedPlatformId(null);
    setSelectedPageId((prev) => (prev === pageId ? null : pageId));
  };

  const selectedPlatformPermissionsSorted = useMemo(
    () =>
      applyPermissionListView(selectedPlatformProduct?.permissions, {
        typeFilter: panelTypeFilter,
        scopeFilter: panelScopeFilter,
        sortBy: panelSortBy,
        isPlatform: true
      }),
    [
      selectedPlatformProduct,
      panelTypeFilter,
      panelScopeFilter,
      panelSortBy
    ]
  );

  const selectedPagePermissionsSorted = useMemo(
    () =>
      applyPermissionListView(selectedPage?.permissions, {
        typeFilter: panelTypeFilter,
        scopeFilter: panelScopeFilter,
        sortBy: panelSortBy,
        isPlatform: false
      }),
    [selectedPage, panelTypeFilter, panelScopeFilter, panelSortBy]
  );

  const renderBottomPanelGrabber = () => (
    <div
      className={`ppm-bottom-grabber${isResizingBottomPanel ? ' ppm-bottom-grabber--active' : ''}`}
      onPointerDown={(e) => {
        e.preventDefault();
        setIsResizingBottomPanel(true);
      }}
      role="separator"
      aria-orientation="horizontal"
      aria-label="Resize permissions panel"
      aria-valuemin={BOTTOM_PANEL_HEIGHT_MIN_VH}
      aria-valuemax={BOTTOM_PANEL_HEIGHT_MAX_VH}
      aria-valuenow={bottomPanelHeightVh}
      title="Drag to resize panel height"
    >
      <span className="ppm-bottom-grabber-bar" aria-hidden />
    </div>
  );

  const renderPanelListTools = ({ includePlatformTypes = false } = {}) => (
    <div className="ppm-panel-tools">
      <select
        className="ppm-panel-tool-select"
        value={panelTypeFilter}
        onChange={(e) => setPanelTypeFilter(e.target.value)}
        aria-label="Filter by permission type"
        title="Filter by permission type"
      >
        {PANEL_TYPE_FILTER_OPTIONS.filter(
          (opt) =>
            includePlatformTypes ||
            (opt.value !== 'platform_access' && opt.value !== 'capability')
        ).map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <select
        className="ppm-panel-tool-select"
        value={panelScopeFilter}
        onChange={(e) => setPanelScopeFilter(e.target.value)}
        aria-label="Filter by scope"
        title="Filter by grantee scope"
      >
        {PANEL_SCOPE_FILTER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <select
        className="ppm-panel-tool-select"
        value={panelSortBy}
        onChange={(e) => setPanelSortBy(e.target.value)}
        aria-label="Sort permissions"
        title="Sort permissions"
      >
        {PANEL_SORT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );

  const renderPasswordResetPolicySection = () => (
    <div className="ppm-page-section">
      <p className="ppm-page-section-desc">
        Self-service reset on <code>/auth</code> is allowed by default. Rules below define who is{' '}
        <strong>blocked</strong> (deny-list). A user is blocked only when every active enrollment
        matches at least one rule. Blocked users receive a private email; the sign-in page always
        shows a generic success message.
      </p>
      {resetPolicyLoading ? (
        <p className="ppm-page-section-desc">Loading policy…</p>
      ) : (
        <>
          {resetPolicyRules.length === 0 && (
            <p className="ppm-page-section-desc">No block rules — everyone can reset via /auth.</p>
          )}
          {resetPolicyRules.map((rule, index) => (
            <div key={`reset-rule-${index}`} className="ppm-form-section" style={{ marginBottom: 12 }}>
              <div className="ppm-form-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="ppm-form-section-title" style={{ margin: 0 }}>
                  Block rule {index + 1}
                </h3>
                <button
                  type="button"
                  className="ppm-row-delete"
                  onClick={() => removeResetPolicyRule(index)}
                  title="Remove rule"
                >
                  Remove
                </button>
              </div>
              <div className="ppm-form-row">
                <label className="ppm-field-label">Group name includes</label>
                <input
                  type="text"
                  value={rule.group_name_includes}
                  onChange={(e) =>
                    updateResetPolicyRule(index, 'group_name_includes', e.target.value)
                  }
                  placeholder="e.g. Student"
                  className="ppm-field-input"
                />
              </div>
              <div className="ppm-form-row">
                <label className="ppm-field-label">Relationship type</label>
                <input
                  type="text"
                  value={rule.relationship_type}
                  onChange={(e) =>
                    updateResetPolicyRule(index, 'relationship_type', e.target.value)
                  }
                  placeholder="e.g. Role at group"
                  className="ppm-field-input"
                />
              </div>
              <div className="ppm-form-row">
                <label className="ppm-field-label">Parent group name equals</label>
                <input
                  type="text"
                  value={rule.parent_group_name_equals}
                  onChange={(e) =>
                    updateResetPolicyRule(index, 'parent_group_name_equals', e.target.value)
                  }
                  placeholder="e.g. System"
                  className="ppm-field-input"
                />
              </div>
              <div className="ppm-form-row">
                <label className="ppm-field-label">Groups-periods IDs (comma-separated)</label>
                <input
                  type="text"
                  value={rule.groups_periods_ids}
                  onChange={(e) =>
                    updateResetPolicyRule(index, 'groups_periods_ids', e.target.value)
                  }
                  placeholder="uuid, uuid"
                  className="ppm-field-input"
                />
              </div>
            </div>
          ))}
          <div className="ppm-form-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="ppm-sheet-btn ppm-sheet-btn--secondary" onClick={addResetPolicyRule}>
              + Add block rule
            </button>
            <button
              type="button"
              className="ppm-sheet-btn ppm-sheet-btn--primary"
              onClick={handleSavePasswordResetPolicy}
              disabled={resetPolicySaving}
            >
              {resetPolicySaving ? 'Saving…' : 'Save reset policy'}
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderPermissionTypeIcon = (perm) => {
    if (
      perm.permission_type === 'page_access' ||
      perm.permission_type === 'platform_access' ||
      perm.permission_type === 'capability'
    ) {
      const accessVal = perm.access;
      const isDeny = accessVal === false || accessVal === 'false';
      const title =
        perm.permission_type === 'platform_access'
          ? isDeny
            ? 'Platform access (deny)'
            : 'Platform access (allow)'
          : perm.permission_type === 'capability'
            ? isDeny
              ? 'Capability (deny)'
              : 'Capability (allow)'
            : isDeny
              ? 'Access (deny)'
              : 'Access (allow)';
      return (
        <span
          style={styles.typeIconWrap}
          title={title}
          aria-label={title}
        >
          {isDeny ? <IconDeny /> : <IconCheck />}
        </span>
      );
    }
    return (
      <span style={styles.typeIconWrap} title="Configuration" aria-label="Configuration">
        <IconGear />
      </span>
    );
  };

  const platformKeyOptions = useMemo(() => {
    if (
      formPermissionType === 'platform_access' ||
      formPermissionType === 'page_access'
    ) {
      return [
        {
          id: PLATFORM_ACCESS_RESOURCE_ID,
          label: '* · Platform access',
        },
        ...getPlatformKeysForType(formPlatformId, 'page_access'),
      ];
    }
    return getPlatformKeysForType(formPlatformId, formPermissionType);
  }, [formPlatformId, formPermissionType]);

  const formAllowedGranteeTypes = useMemo(() => {
    if (formScope === 'platform') {
      return allowedGranteeTypesForPlatformKey(
        formPlatformId,
        formPermissionType,
        formPermissionType === 'platform_access'
          ? PLATFORM_ACCESS_RESOURCE_ID
          : formPlatformKey
      );
    }
    return ['users', 'groups_periods', 'user_groups_periods'];
  }, [formScope, formPlatformId, formPermissionType, formPlatformKey]);

  const formMembershipOnlyCapability =
    formScope === 'platform' &&
    formPermissionType === 'capability' &&
    formAllowedGranteeTypes.length > 0 &&
    formAllowedGranteeTypes.every((t) =>
      t === 'groups_periods' || t === 'user_groups_periods'
    ) &&
    !formAllowedGranteeTypes.includes('users') &&
    !formAllowedGranteeTypes.includes('groups');

  const formGroupOnlyCapability =
    formScope === 'platform' &&
    formPermissionType === 'capability' &&
    formAllowedGranteeTypes.length > 0 &&
    formAllowedGranteeTypes.every((t) => t === 'groups');

  useEffect(() => {
    if (!formAllowedGranteeTypes.includes(formGranteeType)) {
      setFormGranteeType(formAllowedGranteeTypes[0] || 'groups_periods');
      setFormGrantees([]);
      setFormGranteesInput('');
    }
  }, [formAllowedGranteeTypes, formGranteeType]);

  useEffect(() => {
    if (!pagePickerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPagePickerOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pagePickerOpen]);

  const formPlatformKeyDescription = useMemo(() => {
    if (
      formScope !== 'platform' ||
      formPermissionType === 'platform_access' ||
      !formPlatformKey
    ) {
      return '';
    }
    return descriptionForPlatformKey(
      formPlatformId,
      formPermissionType,
      formPlatformKey
    );
  }, [formScope, formPlatformId, formPermissionType, formPlatformKey]);

  const platformResourceBadgeLabel = useMemo(() => {
    if (
      formPlatformKey === PLATFORM_ACCESS_RESOURCE_ID ||
      formPermissionType === 'platform_access'
    ) {
      return '*';
    }
    if (!formPlatformKey) {
      return formPermissionType === 'capability'
        ? 'Select capability…'
        : formPermissionType === 'page_configuration'
          ? 'Select config…'
          : 'Select page…';
    }
    return (
      labelForPlatformKey(formPlatformId, formPermissionType, formPlatformKey) ||
      formPlatformKey
    );
  }, [formPlatformId, formPlatformKey, formPermissionType]);

  const platformAccessTypeActive =
    formPermissionType === 'platform_access' || formPermissionType === 'page_access';

  const showAccessDecisionBadges =
    formPermissionType === 'page_access' ||
    formPermissionType === 'platform_access' ||
    formPermissionType === 'capability';

  return (
    <div style={{
      ...styles.container,
      paddingBottom: showBottomPanel ? `${bottomPanelHeightVh}vh` : undefined
    }}>
      <LoadingOverlay isVisible={isLoading} message="Loading permissions..." />

      <div style={styles.header}>
        <h1 style={styles.title}>Permissions</h1>
        <p style={styles.subtitle}>
          Select a UIS page or child platform. Gear = configuration, check = allow, × = deny.
          Platform Access is the main <code>platform_access</code> allow. Child platform tiles need theirs before other grants apply.
        </p>

        <div style={styles.toolbar}>
          <button
            onClick={() =>
              selectedPlatformId
                ? openCreatePlatformModal(selectedPlatformId)
                : openCreateModal(selectedPage?.id || '')
            }
            style={styles.plusButton}
            title="Add permission"
          >
            <span style={styles.plusGlyph}>+</span>
          </button>
        </div>
      </div>

      <div style={styles.widgetGrid}>
        {permissionsByPage.map(page => {
          const isSelected = selectedPageId === page.id;
          const count =
            page.id === 'auth'
              ? resetPolicyRules.length
              : page.permissions.length;
          const countLabel =
            page.id === 'auth'
              ? `${count} block rule${count === 1 ? '' : 's'}`
              : `${count} ${count === 1 ? 'permission' : 'permissions'}`;
          return (
            <button
              key={page.id}
              type="button"
              className={`ppm-page-widget${isSelected ? ' ppm-page-widget--selected' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => togglePageSelection(page.id)}
              style={{
                ...styles.pageWidget,
                ...(isSelected ? styles.pageWidgetSelected : {})
              }}
              title={`${page.label} — ${countLabel}`}
              aria-pressed={isSelected}
            >
              <span style={{
                ...styles.pageWidgetTitle,
                ...(isSelected ? styles.pageWidgetTitleSelected : {})
              }}>
                {page.label}
              </span>
              <span style={{
                ...styles.pageWidgetMeta,
                ...(isSelected ? styles.pageWidgetMetaSelected : {})
              }}>
                {countLabel}
              </span>
            </button>
          );
        })}
        {platformProducts.map((product) => {
          const isSelected = selectedPlatformId === product.id;
          const count = product.permissions.length;
          return (
            <button
              key={`platform-${product.id}`}
              type="button"
              className={`ppm-page-widget ppm-page-widget--platform${
                isSelected ? ' ppm-page-widget--selected' : ''
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => togglePlatformSelection(product.id)}
              style={{
                ...styles.pageWidget,
                ...(isSelected ? styles.pageWidgetSelected : {})
              }}
              title={`${product.label} — ${count} grant${count === 1 ? '' : 's'}`}
              aria-pressed={isSelected}
            >
              <span className="ppm-page-widget-badge">Platform</span>
              <span
                className="ppm-page-widget-title"
                style={{
                  ...styles.pageWidgetTitle,
                  ...(isSelected ? styles.pageWidgetTitleSelected : {}),
                }}
              >
                {product.label}
              </span>
              <span
                className="ppm-page-widget-meta"
                style={{
                  ...styles.pageWidgetMeta,
                  ...(isSelected ? styles.pageWidgetMetaSelected : {})
                }}
              >
                {count} grant{count === 1 ? '' : 's'}
                {product.keyCount === 0 ? ' · catalog empty' : ''}
              </span>
            </button>
          );
        })}
        <button
          type="button"
          className={`ppm-page-widget ppm-page-widget--reference${
            showRoleTemplates ? ' ppm-page-widget--selected' : ''
          }`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => togglePageSelection(ROLE_TEMPLATES_PANEL_ID)}
          style={{
            ...styles.pageWidget,
            ...(showRoleTemplates ? styles.pageWidgetSelected : {})
          }}
          title={`Role templates — ${GROUP_ROLE_TEMPLATES.length} presets (not a page)`}
          aria-pressed={showRoleTemplates}
        >
          <span className="ppm-page-widget-badge">Reference</span>
          <span
            style={{
              ...styles.pageWidgetTitle,
              ...(showRoleTemplates ? styles.pageWidgetTitleSelected : {}),
              paddingRight: 48
            }}
          >
            Role templates
          </span>
          <span
            style={{
              ...styles.pageWidgetMeta,
              ...(showRoleTemplates ? styles.pageWidgetMetaSelected : {})
            }}
          >
            {GROUP_ROLE_TEMPLATES.length} templates
          </span>
        </button>
      </div>

      <aside className="ppm-guidance-banner" aria-label="Permission guidance">
        <p className="ppm-guidance-banner__title">Grantee guidance</p>
        <ul className="ppm-guidance-banner__list">
          <li>
            Prefer <strong>one permission per GP</strong> (groups_period) — avoid combining multiple GPs in
            a single row. Overlapping grantees are rejected and are harder to maintain than separate rules
            per team-period.
          </li>
          <li>
            Organizational chain: <strong>User → UGP → GP → Group</strong> (account, enrollment, team-period,
            team). Page rules attach to <strong>User</strong>, <strong>UGP</strong>, or <strong>GP</strong>
            grantees; the <strong>narrowest match wins</strong> for a given page and permission type (
            User, then UGP, then GP). An explicit <code>page_access</code> deny still blocks the page.
          </li>
          <li>
            <strong>Platform Access</strong> is the main <code>platform_access</code> grant. A page
            grant does not open the app. <strong>Platform</strong> tiles (Acuity CTE, Learn, OTS): grant{' '}
            <code>platform_access</code> allow first — missing or deny platform access blocks the product.
            For page entry, Acuity also treats a page <strong>capability allow</strong> (e.g. SelfRegister)
            as enough to open that page when there is no page_access deny.
          </li>
        </ul>
      </aside>

      {showRoleTemplates && (
        <div style={bottomPanelStyle} role="region" aria-label="Role templates reference">
          {renderBottomPanelGrabber()}
          <div style={styles.bottomPanelInner}>
            <div style={styles.bottomPanelHeader}>
              <div>
                <div style={styles.bottomPanelTitleRow}>
                  <h2 style={styles.bottomPanelTitle}>Role templates</h2>
                  <span className="ppm-page-widget-badge ppm-page-widget-badge--header">Reference</span>
                </div>
                <p style={styles.bottomPanelSubtitle}>
                  {GROUP_ROLE_TEMPLATES.length} presets — not a page permission target
                </p>
              </div>
              <div style={styles.bottomPanelHeaderActions}>
                <button
                  type="button"
                  onClick={() => setSelectedPageId(null)}
                  style={styles.bottomPanelClose}
                  title="Close panel"
                  aria-label="Close panel"
                >
                  ×
                </button>
              </div>
            </div>
            <RoleTemplatesReference knownPages={KNOWN_PAGES} />
          </div>
        </div>
      )}

      {selectedPlatformProduct && (
        <div
          style={bottomPanelStyle}
          role="region"
          aria-label={`Platform permissions for ${selectedPlatformProduct.label}`}
        >
          {renderBottomPanelGrabber()}
          <div style={styles.bottomPanelInner}>
            <div style={styles.bottomPanelHeader}>
              <div>
                <div style={styles.bottomPanelTitleRow}>
                  <h2 style={styles.bottomPanelTitle}>{selectedPlatformProduct.label}</h2>
                  <span className="ppm-page-widget-badge ppm-page-widget-badge--header">Platform</span>
                </div>
                <p style={styles.bottomPanelSubtitle}>
                  {`platform=${selectedPlatformProduct.id} · ${
                    selectedPlatformPermissionsSorted.length ===
                    (selectedPlatformProduct.permissions?.length || 0)
                      ? `${selectedPlatformProduct.permissions.length} permission${
                          selectedPlatformProduct.permissions.length === 1 ? '' : 's'
                        }`
                      : `${selectedPlatformPermissionsSorted.length} of ${selectedPlatformProduct.permissions.length}`
                  }`}
                  {selectedPlatformProduct.keyCount === 0 ? ' · catalog empty' : ''}
                </p>
              </div>
              <div style={styles.bottomPanelHeaderActions}>
                <div className="ppm-view-toggle" role="group" aria-label="Permission list view">
                  <button
                    type="button"
                    aria-pressed={platformGrantView === 'rows'}
                    onClick={() => setPlatformGrantView('rows')}
                  >
                    By key
                  </button>
                  <button
                    type="button"
                    aria-pressed={platformGrantView === 'grantee'}
                    onClick={() => setPlatformGrantView('grantee')}
                  >
                    By team
                  </button>
                </div>
                {renderPanelListTools({ includePlatformTypes: true })}
                <button
                  type="button"
                  onClick={() => openCreatePlatformModal(selectedPlatformProduct.id)}
                  style={styles.plusButtonSm}
                  title={`Add permission for ${selectedPlatformProduct.label}`}
                >
                  <span style={styles.plusGlyphSm}>+</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedPlatformId(null)}
                  style={styles.bottomPanelClose}
                  title="Close panel"
                  aria-label="Close panel"
                >
                  ×
                </button>
              </div>
            </div>

            {platformGrantView === 'grantee' ? (
              <div style={styles.compactTableWrap}>
                <GrantsByGranteePanel
                  productId={selectedPlatformProduct.id}
                  permissions={selectedPlatformPermissionsSorted}
                  groupsPeriods={groupsPeriods}
                  durableGroups={durableGroups}
                  userGroupsPeriods={userGroupsPeriods}
                  onOpenPermission={openEditModal}
                />
              </div>
            ) : (
            <div style={styles.compactTableWrap}>
              {selectedPlatformPermissionsSorted.length === 0 ? (
                <p style={styles.bottomPanelEmpty}>
                  {(selectedPlatformProduct.permissions?.length || 0) === 0 ? (
                    <>
                      No permissions yet. Use + to add one. Grant <strong>platform_access</strong> first
                      so other keys can apply.
                    </>
                  ) : (
                    'No permissions match the current filters.'
                  )}
                </p>
              ) : (
                <table style={styles.compactTable} className="ppm-compact-table">
                  <colgroup>
                    <col style={{ width: 36 }} />
                    <col style={{ width: 44 }} />
                    <col style={{ width: COMPACT_COL_NAME }} />
                    <col />
                    <col />
                    <col style={{ width: 68 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ ...styles.compactTh, width: 36 }} aria-label="Type" />
                      <th style={{ ...styles.compactTh, width: 44 }}>Scope</th>
                      <th style={{ ...styles.compactTh, width: COMPACT_COL_NAME }}>Name</th>
                      <th style={styles.compactTh}>Grantees</th>
                      <th style={styles.compactTh}>Details</th>
                      <th style={{ ...styles.compactTh, width: 68 }} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPlatformPermissionsSorted.map((perm) => (
                      <tr
                        key={perm.id}
                        className="ppm-compact-row"
                        style={styles.compactRow}
                        onClick={() => openEditModal(perm)}
                        title="Click to edit"
                      >
                        <td style={styles.compactTdIcon}>{renderPermissionTypeIcon(perm)}</td>
                        <td style={styles.compactTdIcon}>
                          <span
                            style={styles.scopeBadge}
                            title={granteeTypeLabel(perm.grantee_type)}
                          >
                            {granteeTypeAcronym(perm.grantee_type)}
                          </span>
                        </td>
                        <td
                          style={{ ...styles.compactTdText, ...styles.compactTdName }}
                          title={(perm.permission_name || '').trim() || undefined}
                        >
                          {clipText((perm.permission_name || '').trim(), CLIP_NAME) || '—'}
                        </td>
                        <td style={styles.compactTdText} title={formatGrantees(perm)}>
                          {clipText(formatGrantees(perm), CLIP_GRANTEES)}
                        </td>
                        <td
                          style={styles.compactTdText}
                          title={formatPlatformPermissionDetails(selectedPlatformProduct.id, perm)}
                        >
                          {clipText(
                            formatPlatformPermissionDetails(selectedPlatformProduct.id, perm),
                            CLIP_CONFIG
                          )}
                        </td>
                        <td style={styles.compactTdActions} onClick={(e) => e.stopPropagation()}>
                          <div className="ppm-row-actions">
                            <button
                              type="button"
                              className="ppm-row-edit"
                              onClick={() => openEditModal(perm)}
                              title="Edit permission"
                              aria-label="Edit permission"
                            >
                              <IconPencil />
                            </button>
                            <button
                              type="button"
                              className="ppm-row-delete"
                              onClick={() => setDeleteConfirmId(perm.id)}
                              title="Delete permission"
                              aria-label="Delete permission"
                            >
                              <IconDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            )}

            <div style={styles.bottomPanelLegend}>
              <span style={styles.legendLabel}>Scope:</span>
              {Object.entries(GRANTEE_ACRONYMS).map(([key, { code, label }]) => (
                <span key={key} style={styles.legendItem} title={label}>
                  <strong>{code}</strong> {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedPage && (
        <div style={bottomPanelStyle} role="region" aria-label={`Permissions for ${selectedPage.label}`}>
          {renderBottomPanelGrabber()}
          <div style={styles.bottomPanelInner}>
            <div style={styles.bottomPanelHeader}>
              <div>
                <div style={styles.bottomPanelTitleRow}>
                  <h2 style={styles.bottomPanelTitle}>{selectedPage.label}</h2>
                  {selectedPage.id !== 'acuitysis' && (
                    <Link
                      to={getPagePath(selectedPage.id)}
                      className="ppm-page-open"
                      title={`Open ${selectedPage.label}`}
                      aria-label={`Open ${selectedPage.label}`}
                    >
                      <PageOpenIcon />
                    </Link>
                  )}
                </div>
                <p style={styles.bottomPanelSubtitle}>
                  {selectedPage.id === 'acuitysis'
                    ? 'Main platform_access. Page grants do not open the app.'
                    : selectedPage.id === 'auth'
                    ? 'Self-service password reset · /auth'
                    : selectedPagePermissionsSorted.length === selectedPage.permissions.length
                      ? `${selectedPage.permissions.length} permission${
                          selectedPage.permissions.length === 1 ? '' : 's'
                        }`
                      : `${selectedPagePermissionsSorted.length} of ${selectedPage.permissions.length}`}
                </p>
              </div>
              <div style={styles.bottomPanelHeaderActions}>
                {selectedPage.id !== 'auth' && (
                  <>
                    {renderPanelListTools()}
                    <button
                      type="button"
                      onClick={() => openCreateModal(selectedPage.id)}
                      style={styles.plusButtonSm}
                      title={`Add permission for ${selectedPage.label}`}
                    >
                      <span style={styles.plusGlyphSm}>+</span>
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedPageId(null)}
                  style={styles.bottomPanelClose}
                  title="Close panel"
                  aria-label="Close panel"
                >
                  ×
                </button>
              </div>
            </div>

            {selectedPage.id === 'auth' ? (
              renderPasswordResetPolicySection()
            ) : (
            <div style={styles.compactTableWrap}>
              {selectedPagePermissionsSorted.length === 0 ? (
                <p style={styles.bottomPanelEmpty}>
                  {selectedPage.permissions.length === 0
                    ? 'No permissions yet. Use + to add one.'
                    : 'No permissions match the current filters.'}
                </p>
              ) : (
                <table style={styles.compactTable} className="ppm-compact-table">
                  <colgroup>
                    <col style={{ width: 36 }} />
                    <col style={{ width: 44 }} />
                    <col style={{ width: COMPACT_COL_NAME }} />
                    <col />
                    <col />
                    <col style={{ width: 68 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ ...styles.compactTh, width: 36 }} aria-label="Type" />
                      <th style={{ ...styles.compactTh, width: 44 }}>Scope</th>
                      <th style={{ ...styles.compactTh, width: COMPACT_COL_NAME }}>Name</th>
                      <th style={styles.compactTh}>Grantees</th>
                      <th style={styles.compactTh}>Details</th>
                      <th style={{ ...styles.compactTh, width: 68 }} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPagePermissionsSorted.map(perm => (
                      <tr
                        key={perm.id}
                        className="ppm-compact-row"
                        style={styles.compactRow}
                        onClick={() => openEditModal(perm)}
                        title="Click to edit"
                      >
                        <td style={styles.compactTdIcon}>{renderPermissionTypeIcon(perm)}</td>
                        <td style={styles.compactTdIcon}>
                          <span
                            style={styles.scopeBadge}
                            title={granteeTypeLabel(perm.grantee_type)}
                          >
                            {granteeTypeAcronym(perm.grantee_type)}
                          </span>
                        </td>
                        <td
                          style={{ ...styles.compactTdText, ...styles.compactTdName }}
                          title={(perm.permission_name || '').trim() || undefined}
                        >
                          {clipText((perm.permission_name || '').trim(), CLIP_NAME) || '—'}
                        </td>
                        <td style={styles.compactTdText} title={formatGrantees(perm)}>
                          {clipText(formatGrantees(perm), CLIP_GRANTEES)}
                        </td>
                        <td style={styles.compactTdText} title={formatConfig(perm)}>
                          {clipText(formatConfig(perm), CLIP_CONFIG)}
                        </td>
                        <td style={styles.compactTdActions} onClick={(e) => e.stopPropagation()}>
                          <div className="ppm-row-actions">
                            <button
                              type="button"
                              className="ppm-row-edit"
                              onClick={() => openEditModal(perm)}
                              title="Edit permission"
                              aria-label="Edit permission"
                            >
                              <IconPencil />
                            </button>
                            <button
                              type="button"
                              className="ppm-row-delete"
                              onClick={() => setDeleteConfirmId(perm.id)}
                              title="Delete permission"
                              aria-label="Delete permission"
                            >
                              <IconDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            )}

            {selectedPage.id !== 'auth' && (
            <div style={styles.bottomPanelLegend}>
              <span style={styles.legendLabel}>Scope:</span>
              {Object.entries(GRANTEE_ACRONYMS).map(([key, { code, label }]) => (
                <span key={key} style={styles.legendItem} title={label}>
                  <strong>{code}</strong> {label}
                </span>
              ))}
            </div>
            )}
          </div>
        </div>
      )}

      {/* Page / resource catalog picker (overlay cards) */}
      {showCreateModal &&
        pagePickerOpen &&
        createPortal(
          <div
            className="ppm-key-picker-overlay"
            role="presentation"
            onClick={() => setPagePickerOpen(false)}
          >
            <div
              className="ppm-key-picker-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ppm-key-picker-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ppm-key-picker-header">
                <div>
                  <h3 id="ppm-key-picker-title" className="ppm-key-picker-title">
                    {formScope === 'platform'
                      ? formPermissionType === 'capability'
                        ? 'Select capability'
                        : formPermissionType === 'page_configuration'
                          ? 'Select configuration'
                          : 'Select page or resource'
                      : 'Select page'}
                  </h3>
                  <p className="ppm-key-picker-subtitle">
                    {formScope === 'platform'
                      ? 'Choose a catalog key. Cards with longer notes explain scope and grant rules.'
                      : 'Choose which UIS page this permission applies to.'}
                  </p>
                </div>
                <button
                  type="button"
                  className="ppm-key-picker-close"
                  onClick={() => setPagePickerOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="ppm-key-picker-list" role="listbox">
                {formScope === 'page' ? (
                  KNOWN_PAGES.map((p) => {
                    const selected = formPage === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`ppm-key-picker-card${selected ? ' ppm-key-picker-card--selected' : ''}`}
                        onClick={() => handleFormPageChange(p.id)}
                      >
                        <span className="ppm-key-picker-card__title">{p.label}</span>
                        <span className="ppm-key-picker-card__key">{p.id}</span>
                      </button>
                    );
                  })
                ) : platformKeyOptions.length === 0 ? (
                  <div className="ppm-key-picker-empty">No catalog keys for this type yet</div>
                ) : (
                  <>
                    {formPermissionType === 'capability' &&
                      formPlatformId === 'acuitycte' && (
                        <div
                          className="ppm-key-picker-card ppm-key-picker-card--disabled"
                          role="note"
                          aria-disabled="true"
                        >
                          <span className="ppm-key-picker-card__title">
                            Scope Ancestors (default)
                          </span>
                          <span className="ppm-key-picker-card__key">
                            always on — no grant needed
                          </span>
                          <span className="ppm-key-picker-card__desc">
                            Every caller already has their current UGP group plus parents up the
                            tree. Example: District A → School A → Admin A matches School A and
                            District A events, and students whose memberships share those ancestors
                            (e.g. Students under School A, via School A). It does not add sibling
                            team ids for event tags — an event tagged only “Students” needs
                            SameLevelChildren (or ListedGroups). Caps below expand beyond parents.
                          </span>
                        </div>
                      )}
                    {platformKeyOptions.map((k) => {
                      const selected =
                        formPlatformKey === k.id ||
                        (k.id === PLATFORM_ACCESS_RESOURCE_ID &&
                          formPermissionType === 'platform_access');
                      return (
                        <button
                          key={k.id}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={`ppm-key-picker-card${selected ? ' ppm-key-picker-card--selected' : ''}`}
                          onClick={() => handlePlatformResourceChange(k.id)}
                        >
                          <span className="ppm-key-picker-card__title">
                            {k.label}
                          </span>
                          {k.id !== PLATFORM_ACCESS_RESOURCE_ID ? (
                            <span className="ppm-key-picker-card__key">{k.id}</span>
                          ) : (
                            <span className="ppm-key-picker-card__key">platform_access</span>
                          )}
                          {k.description ? (
                            <span className="ppm-key-picker-card__desc">{k.description}</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className="ppm-sheet-overlay"
          role="presentation"
        >
          <div
            className="ppm-sheet-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ppm-permission-sheet-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ppm-sheet-header">
              <div className="ppm-sheet-header-main">
                <h2 id="ppm-permission-sheet-title" className="ppm-sheet-title">
                  {modalOwnerReadOnly
                    ? 'View · platform Owner'
                    : editingId
                      ? 'Edit permission'
                      : 'Add permission'}
                </h2>
                {!modalOwnerReadOnly && formScope === 'page' && (
                  <div className="ppm-sheet-header-badges">
                    <div className="ppm-header-badge-wrap">
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--page${pagePickerOpen ? ' ppm-header-badge--open' : ''}`}
                        onClick={() => {
                          setPagePickerOpen((open) => !open);
                          setProductPickerOpen(false);
                        }}
                        aria-expanded={pagePickerOpen}
                        aria-haspopup="dialog"
                        title="Change page"
                      >
                        {KNOWN_PAGES.find((p) => p.id === formPage)?.label || formPage}
                        <span className="ppm-header-badge-caret" aria-hidden>▾</span>
                      </button>
                    </div>
                    {formPage !== 'acuitysis' && (
                    <div className="ppm-header-badge-group" role="group" aria-label="Permission type">
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--type${formPermissionType === 'page_configuration' ? ' ppm-header-badge--active' : ''}`}
                        onClick={() => setFormPermissionType('page_configuration')}
                        aria-pressed={formPermissionType === 'page_configuration'}
                      >
                        Config
                      </button>
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--type${formPermissionType === 'page_access' ? ' ppm-header-badge--active' : ''}`}
                        onClick={() => setFormPermissionType('page_access')}
                        aria-pressed={formPermissionType === 'page_access'}
                      >
                        Access
                      </button>
                    </div>
                    )}
                    {showAccessDecisionBadges && (
                      <div className="ppm-header-badge-group" role="group" aria-label="Allow or deny">
                        <button
                          type="button"
                          className={`ppm-header-badge ppm-header-badge--allow${formAccess ? ' ppm-header-badge--active' : ''}`}
                          onClick={() => setFormAccess(true)}
                          aria-pressed={formAccess === true}
                        >
                          Allow
                        </button>
                        <button
                          type="button"
                          className={`ppm-header-badge ppm-header-badge--deny${!formAccess ? ' ppm-header-badge--active' : ''}`}
                          onClick={() => setFormAccess(false)}
                          aria-pressed={formAccess === false}
                        >
                          Deny
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!modalOwnerReadOnly && formScope === 'platform' && (
                  <div className="ppm-sheet-header-badges">
                    <div className="ppm-header-badge-wrap">
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--page${productPickerOpen ? ' ppm-header-badge--open' : ''}`}
                        onClick={() => {
                          setProductPickerOpen((open) => !open);
                          setPagePickerOpen(false);
                        }}
                        aria-expanded={productPickerOpen}
                        aria-haspopup="listbox"
                        title="Change product"
                      >
                        {getPlatformProduct(formPlatformId)?.label || formPlatformId}
                        <span className="ppm-header-badge-caret" aria-hidden>▾</span>
                      </button>
                      {productPickerOpen && (
                        <div className="ppm-page-picker" role="listbox" aria-label="Select product">
                          {PLATFORM_PRODUCTS.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              role="option"
                              aria-selected={formPlatformId === p.id}
                              className={`ppm-page-picker-item${formPlatformId === p.id ? ' ppm-page-picker-item--selected' : ''}`}
                              onClick={() => handlePlatformProductChange(p.id)}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="ppm-header-badge-wrap">
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--page${pagePickerOpen ? ' ppm-header-badge--open' : ''}`}
                        onClick={() => {
                          setPagePickerOpen((open) => !open);
                          setProductPickerOpen(false);
                        }}
                        aria-expanded={pagePickerOpen}
                        aria-haspopup="dialog"
                        title="Change page / resource"
                      >
                        {platformResourceBadgeLabel}
                        <span className="ppm-header-badge-caret" aria-hidden>▾</span>
                      </button>
                    </div>
                    <div className="ppm-header-badge-group" role="group" aria-label="Permission type">
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--type${platformAccessTypeActive ? ' ppm-header-badge--active' : ''}`}
                        onClick={() => handlePlatformPermissionTypeChange('page_access')}
                        aria-pressed={platformAccessTypeActive}
                        title="Page access, or * for platform access"
                      >
                        Access
                      </button>
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--type${formPermissionType === 'page_configuration' ? ' ppm-header-badge--active' : ''}`}
                        onClick={() => handlePlatformPermissionTypeChange('page_configuration')}
                        aria-pressed={formPermissionType === 'page_configuration'}
                        title="Page configuration key"
                      >
                        Config
                      </button>
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--type${formPermissionType === 'capability' ? ' ppm-header-badge--active' : ''}`}
                        onClick={() => handlePlatformPermissionTypeChange('capability')}
                        aria-pressed={formPermissionType === 'capability'}
                        title="Capability key"
                      >
                        Capability
                      </button>
                    </div>
                    {showAccessDecisionBadges && (
                      <div className="ppm-header-badge-group" role="group" aria-label="Allow or deny">
                        <button
                          type="button"
                          className={`ppm-header-badge ppm-header-badge--allow${formAccess ? ' ppm-header-badge--active' : ''}`}
                          onClick={() => setFormAccess(true)}
                          aria-pressed={formAccess === true}
                        >
                          Allow
                        </button>
                        <button
                          type="button"
                          className={`ppm-header-badge ppm-header-badge--deny${!formAccess ? ' ppm-header-badge--active' : ''}`}
                          onClick={() => setFormAccess(false)}
                          aria-pressed={formAccess === false}
                        >
                          Deny
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {modalOwnerReadOnly && showAccessDecisionBadges && (
                  <div className="ppm-sheet-header-badges">
                    <div className="ppm-header-badge-group" role="group" aria-label="Allow or deny">
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--allow${formAccess ? ' ppm-header-badge--active' : ''}`}
                        aria-pressed={formAccess === true}
                        disabled
                      >
                        Allow
                      </button>
                      <button
                        type="button"
                        className={`ppm-header-badge ppm-header-badge--deny${!formAccess ? ' ppm-header-badge--active' : ''}`}
                        aria-pressed={formAccess === false}
                        disabled
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="ppm-sheet-close"
                onClick={closeCreateModal}
                aria-label="Close"
                disabled={isSaving}
              >
                ×
              </button>
            </div>
            {showOwnerProtectedBanner && (
              <p className="ppm-sheet-callout">{OWNER_PROTECTED_HINT}</p>
            )}
            {formScope === 'platform' && formPlatformKey === 'scope.ListedGroups' ? (
              <p className="ppm-sheet-callout ppm-sheet-callout--scope-listed">
                {scopeListedGroupsWarningForPlatform(formPlatformId)}
              </p>
            ) : null}

            <div className="ppm-sheet-tabs" role="tablist" aria-label="Permission sections">
              {[
                { id: 'target', label: 'Target' },
                { id: 'grantee', label: 'Grantee' },
                { id: 'rules', label: 'Rules' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`ppm-sheet-tab-${tab.id}`}
                  aria-selected={permissionSheetTab === tab.id}
                  aria-controls={`ppm-sheet-panel-${tab.id}`}
                  className={`ppm-sheet-tab${permissionSheetTab === tab.id ? ' ppm-sheet-tab--active' : ''}`}
                  onClick={() => setPermissionSheetTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="ppm-sheet-form-body">
            <fieldset disabled={modalOwnerReadOnly} className="ppm-sheet-fieldset">
            {permissionSheetTab === 'target' && (
            <div
              className="ppm-sheet-tab-panel"
              role="tabpanel"
              id="ppm-sheet-panel-target"
              aria-labelledby="ppm-sheet-tab-target"
            >
            <div className="ppm-form-section">
            {formScope === 'platform' && formPermissionType === 'platform_access' && (
              <p className="ppm-field-hint">
                Resource <strong>*</strong> is platform access for{' '}
                <strong>{getPlatformProduct(formPlatformId)?.label || formPlatformId}</strong>.
                Without allow, other grants for this product are ignored.
              </p>
            )}
            {formScope === 'platform' &&
              formPermissionType !== 'platform_access' &&
              !formPlatformKey && (
              <p className="ppm-field-hint">
                Choose a {formPermissionType === 'capability' ? 'capability' : formPermissionType === 'page_configuration' ? 'configuration key' : 'page'} from the header badge
                {formPermissionType === 'page_access' ? ' (* = platform access).' : '.'}
              </p>
            )}
            {formScope === 'platform' && formPlatformKeyDescription && (
              <p className="ppm-field-hint ppm-platform-key-description">
                <strong>{formPlatformKey}</strong> — {formPlatformKeyDescription}
              </p>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'people.ListDirectory' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">Directory roles</span>
                <span className="ppm-field-hint">
                  Required when access is allow. Stored on <code>metadata.list_roles</code>
                  {' '}(<code>students</code>, <code>instructors</code>, <code>admins</code>,{' '}
                  <code>super_admins</code>) — not in Rules (Rules is for vendor / ListedGroups only).
                </span>
                {PEOPLE_LIST_DIRECTORY_ROLE_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formPeopleListDirectory[opt.key] === true}
                      onChange={(e) =>
                        setFormPeopleListDirectory((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'school.AddUsers' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">Roles this user may add to a school</span>
                <span className="ppm-field-hint">
                  Required when access is allow. Stored on <code>metadata.add_roles</code>
                  {' '}— not in Rules. Acuity Learn will only offer these roles when connecting a
                  person to a school (creates GP / UGP + the matching role.* badge).
                  Changing which pages and capabilities that person receives is a separate
                  capability: <code>school.OverrideAddUserPermissions</code> (the gear on add).
                </span>
                {SCHOOL_ADD_USERS_ROLE_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formSchoolAddUsers[opt.key] === true}
                      onChange={(e) =>
                        setFormSchoolAddUsers((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              isMessageThreadKindKey(formPlatformKey) && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">Which message kinds</span>
                <span className="ppm-field-hint">{messageThreadKindHint(formPlatformKey)}</span>
                <label className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={formMessageThreadKinds.office === true}
                    onChange={(e) =>
                      setFormMessageThreadKinds({ office: e.target.checked })
                    }
                  />
                  Office
                </label>
                <label className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                  <input type="checkbox" checked={false} disabled />
                  Support — in development
                </label>
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'people.PersonLinks' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">Relationship types this user may link</span>
                <span className="ppm-field-hint">
                  Required when access is allow. Stored on <code>metadata.link_types</code>
                  {' '}— not in Rules. Parents are guardians, not a separate type.
                </span>
                {LINK_PERSON_TYPE_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formLinkPersonTypes[opt.key] === true}
                      onChange={(e) =>
                        setFormLinkPersonTypes((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'parent.ManageStudentGuardianRelationships' && (
              <p className="ppm-field-hint" style={{ marginTop: 12 }}>
                A parent may add a guardian to a student already linked to them, and update that
                guardian&apos;s type, primary, and emergency on the contact card. Guardian type only.
                It does not add a counselor or a sibling, and it does not remove a link.
              </p>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'student.AddSelfGuardianRelationships' && (
              <p className="ppm-field-hint" style={{ marginTop: 12 }}>
                A student may add a guardian to themselves only. The student id comes from the
                signed-in person. It does not add a guardian to another student.
              </p>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'people.EditUserData' && (
              <p className="ppm-field-hint" style={{ marginTop: 12 }}>
                Lets a contact update overwrite a sign-in email or phone that already has a value.
                Filling an empty email or phone does not need this, and this cap does not grant the
                contact update itself. Someone else can be changed only when every membership they
                have is inside this school scope. <code>scope.Global</code> covers every school and
                still does not cover a System seat. A context outside that scope needs{' '}
                <code>people.EditUserDataMaster</code>. The platform owner (Owner team under System)
                can change their own email and phone with this cap. Nobody else can change that person.
              </p>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'people.EditUserDataMaster' && (
              <p className="ppm-field-hint" style={{ marginTop: 12 }}>
                Overwrites a sign-in email or phone when that person also has a membership outside
                this school scope, including a System seat. It includes{' '}
                <code>people.EditUserData</code>. It still cannot change the platform owner unless
                the actor is that person. <code>scope.Global</code> does not grant this.
              </p>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'people.SearchPeople' && (
              <p className="ppm-field-hint" style={{ marginTop: 12 }}>
                Shows directory search when adding a guardian, and allows linking that existing person.
                Create new stays available without this. It does not cover counselor search.
              </p>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'people.GrantContactPermissions' && (
              <p className="ppm-field-hint" style={{ marginTop: 12 }}>
                Shows the gear on Edit relationship in Acuity Learn. That gear sets{' '}
                <code>parent.ViewStudentContact</code>, <code>parent.UpdateStudentContact</code>,{' '}
                <code>parent.ViewGuardianContact</code>, and <code>parent.UpdateGuardianContact</code>{' '}
                on the guardian&apos;s membership (UGP). Update includes view, so view is not stored
                when update is checked. Which schools the gear covers follows this grant&apos;s scope:
                this membership&apos;s school, <code>scope.ListedGroups</code>, or <code>scope.Global</code>.
              </p>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'dm.SendStudentRelationship' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">Person links this person may direct-message</span>
                <span className="ppm-field-hint">
                  Required when access is allow. Stored on <code>metadata.relationship_types</code>.
                  At least one type. The student is <code>parent_id</code>. The person messaged is{' '}
                  <code>child_id</code>. This does not message the student. Course teachers are{' '}
                  <code>dm.SendStudentTeacher</code>.
                </span>
                {RELATIONSHIP_TYPE_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formRelationshipTypes[opt.key] === true}
                      onChange={(e) =>
                        setFormRelationshipTypes((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'dm.ViewOthersInboxes' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">Whose inboxes this person may read</span>
                <span className="ppm-field-hint">
                  Required when access is allow. Stored on <code>metadata.inbox_roles</code>.
                  At least one role. This is other people’s direct messages, not the holder’s own Messages.
                  Own threads stay <code>dm.Send</code> and <code>dm.Reply</code>. Schools follow this
                  membership’s scope.
                </span>
                {INBOX_ROLE_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formInboxRoles[opt.key] === true}
                      onChange={(e) =>
                        setFormInboxRoles((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'school.ViewUsers' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">Roles this user may see on a school</span>
                <span className="ppm-field-hint">
                  Required when access is allow. Stored on <code>metadata.view_roles</code>
                  {' '}— not in Rules. Acuity Learn only lists people in these role slices.
                </span>
                {SCHOOL_VIEW_USERS_ROLE_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formSchoolViewUsers[opt.key] === true}
                      onChange={(e) =>
                        setFormSchoolViewUsers((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'course.TeachEnrolled' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">What they may do on enrolled courses</span>
                <span className="ppm-field-hint">
                  Ceiling for enrolled teachers only — defaults all four on when omitted. Stored on <code>metadata.instructor_kit</code>
                  {' '}— not in Rules. Which activities those apply to is the course enrollment kit, not this grant.
                  Uncheck a verb to deny it for every enrolled course. The holder still needs an instructor
                  enrollment on that copy. Teach Without Enrollment has its own kit on that grant, not this one.
                  Grade is view and mark. This kit is the papers door for enrolled teachers.
                </span>
                {INSTRUCT_ASSIGNED_KIT_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formInstructAssigned[opt.key] === true}
                      onChange={(e) =>
                        setFormInstructAssigned((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'course.TeachWithoutEnrollment' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">What they may do without being enrolled</span>
                <span className="ppm-field-hint">
                  Own kit for every in-scope school copy — not Teach Enrolled, and not propose or live-edit
                  (that is course.EditCourses). Stored on <code>metadata.supervisor_kit</code>. Defaults all
                  on when omitted. Grade includes view; View papers is read without mark. Empty list is all
                  off. If the holder is also enrolled with Teach Enrolled on a copy, the enrollment kit wins
                  there.
                </span>
                {SUPERVISOR_KIT_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formSupervisorKit[opt.key] === true}
                      onChange={(e) =>
                        setFormSupervisorKit((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              isLibrarySourceKitKey(formPlatformKey) && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">What they may add and change</span>
                <span className="ppm-field-hint">
                  Ceiling only — defaults all kinds and both actions on when omitted. Stored on{' '}
                  <code>metadata.source_kit</code> — not in Rules. Uncheck a kind to block that
                  upload type. <strong>Edit tags</strong> without <strong>Replace file</strong> lets
                  them tweak analysis but not swap the file. Size: largest checked cap wins; leave
                  all size boxes off to use Learn defaults (12 MB docs / 80 MB media).
                </span>
                {LIBRARY_SOURCE_KIT_KIND_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formLibrarySourceKit[opt.key] === true}
                      onChange={(e) =>
                        setFormLibrarySourceKit((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
                {LIBRARY_SOURCE_KIT_ACTION_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formLibrarySourceKit[opt.key] === true}
                      onChange={(e) =>
                        setFormLibrarySourceKit((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
                {LIBRARY_SOURCE_KIT_SIZE_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formLibrarySourceKit[opt.key] === true}
                      onChange={(e) =>
                        setFormLibrarySourceKit((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'capability' &&
              formPlatformKey === 'library.AnalyzeSources' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-hint">
                  Allow-only — no extra checkboxes. Without this grant, Learn hides{' '}
                  <strong>Skip AI analysis</strong> and forces skip. Holders can still type
                  transcript, tags, and taxonomy via Edit / Propose <code>edit_analysis</code>.
                  Audio and video stay skipped until Transcribe is wired.
                </span>
              </div>
            )}
            {formScope === 'platform' &&
              formPermissionType === 'page_access' &&
              formPlatformKey === 'grade-rubrics' && (
              <div className="ppm-form-row" style={{ marginTop: 12 }}>
                <span className="ppm-field-label">What they may do</span>
                <span className="ppm-field-hint">
                  One page grant. Stored on <code>metadata.rubric_actions</code>. Create includes
                  copying a rubric. Edit changes a foundation for every course still using it.
                  Uncheck all three for view only. A grant with the field omitted allows all three.
                </span>
                {GRADE_RUBRIC_ACTION_OPTIONS.map((opt) => (
                  <label key={opt.key} className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={formGradeRubricActions[opt.key] === true}
                      onChange={(e) =>
                        setFormGradeRubricActions((prev) => ({
                          ...prev,
                          [opt.key]: e.target.checked,
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
            <div className="ppm-form-row">
              <label className="ppm-field-label">Name (optional title)</label>
              <input
                type="text"
                value={formPermissionName}
                onChange={(e) => setFormPermissionName(e.target.value)}
                placeholder={
                  formScope === 'platform'
                    ? 'e.g. Coordinators — Acuity access'
                    : 'e.g. Teachers — view only'
                }
                className="ppm-field-input"
                maxLength={120}
              />
            </div>
            <div className="ppm-form-row">
              <span className="ppm-field-label">Grantee type</span>
              {formMembershipOnlyCapability && (
                <span className="ppm-field-hint">
                  This capability is membership-scoped: grant to Groups Periods or User Groups
                  Periods only (not Users). Invite expansion uses active UGPs: if override_period,
                  ugp start/end; otherwise the GP period dates.
                </span>
              )}
              {formGroupOnlyCapability && (
                <span className="ppm-field-hint">
                  This capability is group-scoped: grant to durable Groups only (organizations or
                  teams by groups.id) — not Groups Periods, not UGPs, not Users. Used to mark which
                  groups appear as Schools in LearnPath or Acuity Learn.
                </span>
              )}
              <div className="ppm-grantee-type-cards" role="radiogroup" aria-label="Grantee type">
                {GRANTEE_TYPE_OPTIONS.filter((option) =>
                  formAllowedGranteeTypes.includes(option.id)
                ).map((option) => {
                  const selected = formGranteeType === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`ppm-grantee-type-card${selected ? ' ppm-grantee-type-card--selected' : ''}`}
                      onClick={() => handleFormGranteeTypeChange(option.id)}
                    >
                      <span className="ppm-grantee-type-card__head">
                        <span className="ppm-grantee-type-card__title">{option.title}</span>
                        <span className="ppm-grantee-type-card__code">{option.code}</span>
                      </span>
                      <span className="ppm-grantee-type-card__desc">{option.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            </div>
            </div>
            )}

            {permissionSheetTab === 'grantee' && (
            <div
              className="ppm-sheet-tab-panel"
              role="tabpanel"
              id="ppm-sheet-panel-grantee"
              aria-labelledby="ppm-sheet-tab-grantee"
            >
            <div className="ppm-form-section">
            <div className="ppm-form-row">
              <label className="ppm-field-label">
                {formGranteeType === 'groups'
                  ? 'Groups (organizations / teams)'
                  : formGranteeType === 'groups_periods'
                  ? 'Grantees'
                  : formGranteeType === 'user_groups_periods'
                    ? 'User Groups Periods'
                    : 'Grantees (comma-separated or *all*)'}
              </label>
              {formGranteeType === 'groups' && (
                <>
                  <input
                    type="search"
                    value={groupsSearch}
                    onChange={(e) => setGroupsSearch(e.target.value)}
                    placeholder="Search organizations or teams…"
                    className="ppm-field-input"
                  />
                  <div
                    className="ppm-gp-checkbox-list ppm-grantee-tree"
                    role="listbox"
                    aria-label="Group grantees"
                    aria-multiselectable="true"
                  >
                    {filteredDurableGroups.length === 0 ? (
                      <div className="ppm-key-picker-empty">
                        No groups found from loaded groups-periods.
                      </div>
                    ) : (
                      filteredDurableGroups.map((g, index) => {
                        const id = String(g.id);
                        const selectedIds = splitGranteesInput(formGranteesInput);
                        const selected = selectedIds.includes(id);
                        const isLast = index === filteredDurableGroups.length - 1;
                        const typeBit = g.group_type ? g.group_type : '';
                        const labelBit = g.display_label ? ` · ${g.display_label}` : '';
                        return (
                          <button
                            key={g.id}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`ppm-grantee-tree-row${selected ? ' ppm-grantee-tree-row--selected' : ''}`}
                            onClick={() => {
                              if (selected) {
                                setFormGranteesInput(selectedIds.filter((x) => x !== id).join(', '));
                              } else {
                                setFormGranteesInput([...selectedIds, id].join(', '));
                              }
                            }}
                          >
                            <GranteeTreeGuides depth={0} pipes={[]} isLast={isLast} />
                            <span className="ppm-grantee-tree-label">
                              <span className="ppm-grantee-tree-name">{g.name}</span>
                              <span className="ppm-grantee-tree-meta">
                                {typeBit}
                                {labelBit}
                              </span>
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                  {!editingId && (
                    <span className="ppm-field-hint">
                      Select the organization or team group that should appear as a School in
                      LearnPath or Acuity Learn. This is the durable group id, independent of period.
                    </span>
                  )}
                </>
              )}
              {formGranteeType === 'users' && (
                <>
                <input
                  type="text"
                  value={formGranteesInput}
                  onChange={(e) => setFormGranteesInput(e.target.value)}
                  placeholder="*all* or uuid1, uuid2, email@example.com"
                  className="ppm-field-input"
                />
                {hidePlatformOwnerFromViewer && ownerContext.ownerUserIds.size > 0 && (
                  <span className="ppm-field-hint">
                    Platform Owner accounts are not listed here.
                  </span>
                )}
                </>
              )}
              {formGranteeType === 'groups_periods' && (
                <>
                  <input
                    type="search"
                    value={groupPeriodsSearch}
                    onChange={(e) => setGroupPeriodsSearch(e.target.value)}
                    placeholder="Search groups or periods…"
                    className="ppm-field-input"
                  />
                  <div className="ppm-gp-checkbox-list ppm-grantee-tree" role="listbox" aria-label="Group-period grantees" aria-multiselectable="true">
                    {(() => {
                      const isAll = formGranteesInput.trim() === '*all*';
                      const selectedIds = isAll
                        ? []
                        : formGranteesInput
                            .split(/[,\s]+/)
                            .map((s) => s.trim())
                            .filter(Boolean)
                            .filter((x) => x !== '*all*');
                      return (
                        <>
                          <button
                            type="button"
                            role="option"
                            aria-selected={isAll}
                            className={`ppm-grantee-tree-row ppm-grantee-tree-row--all${isAll ? ' ppm-grantee-tree-row--selected' : ''}`}
                            onClick={() => setFormGranteesInput(isAll ? '' : '*all*')}
                          >
                            <span className="ppm-grantee-tree-label">
                              <span className="ppm-grantee-tree-name">*all*</span>
                              <span className="ppm-grantee-tree-meta">any group-period</span>
                            </span>
                          </button>
                          {groupedGroupsPeriods.map((group) => (
                            <div key={group.organization} className="ppm-grantee-tree-org">
                              <div className="ppm-grantee-tree-org-title">{group.organization}</div>
                              {group.items.map((gp) => {
                                if (isPlatformOwnerGpHidden(gp)) return null;
                                const id = String(gp.id);
                                const selected = selectedIds.includes(id);
                                const depth = Math.min(gp._depth || 0, 8);
                                return (
                                  <button
                                    key={gp.id}
                                    type="button"
                                    role="option"
                                    aria-selected={selected}
                                    disabled={isAll}
                                    className={`ppm-grantee-tree-row${selected ? ' ppm-grantee-tree-row--selected' : ''}`}
                                    onClick={() => {
                                      if (isAll) return;
                                      if (selected) {
                                        setFormGranteesInput(selectedIds.filter((x) => x !== id).join(', '));
                                      } else {
                                        setFormGranteesInput([...selectedIds, id].join(', '));
                                      }
                                    }}
                                  >
                                    <GranteeTreeGuides
                                      depth={depth}
                                      pipes={gp._pipes || []}
                                      isLast={gp._isLast !== false}
                                    />
                                    <span className="ppm-grantee-tree-label">
                                      <span className="ppm-grantee-tree-name">{gp.group_name}</span>
                                      <span className="ppm-grantee-tree-meta">
                                        {gp.period_title || gp.period_id}
                                      </span>
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                  {!editingId && (
                    <span className="ppm-field-hint">
                      One permission per page/type/grantee set per rules scope. Same GP with different vendor rules can coexist; identical scope overlaps are blocked.
                    </span>
                  )}
                </>
              )}
              {formGranteeType === 'user_groups_periods' && (
                <>
                  <input
                    type="search"
                    value={userGroupsPeriodsSearch}
                    onChange={(e) => setUserGroupsPeriodsSearch(e.target.value)}
                    placeholder="Search users, teams, or periods…"
                    className="ppm-field-input"
                  />
                  <div className="ppm-gp-checkbox-list ppm-grantee-tree" role="listbox" aria-label="User groups-period grantees" aria-multiselectable="true">
                    {groupedUserGroupsPeriods.map((group) => {
                      const selectedIds = splitGranteesInput(formGranteesInput);
                      const visibleItems = group.items.filter((ugp) => !isPlatformOwnerUgpHidden(ugp));
                      return (
                        <div key={group.organization} className="ppm-grantee-tree-org">
                          <div className="ppm-grantee-tree-org-title">{group.organization}</div>
                          {visibleItems.map((ugp, index) => {
                            const id = String(ugp.id);
                            const selected = selectedIds.includes(id);
                            const isLast = index === visibleItems.length - 1;
                            return (
                              <button
                                key={ugp.id}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                className={`ppm-grantee-tree-row${selected ? ' ppm-grantee-tree-row--selected' : ''}`}
                                onClick={() => {
                                  if (selected) {
                                    setFormGranteesInput(selectedIds.filter((x) => x !== id).join(', '));
                                  } else {
                                    setFormGranteesInput([...selectedIds, id].join(', '));
                                  }
                                }}
                              >
                                <GranteeTreeGuides depth={1} pipes={[]} isLast={isLast} />
                                <span className="ppm-grantee-tree-label">
                                  <span className="ppm-grantee-tree-name">{userGroupsPeriodsLabel(ugp)}</span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                  {!editingId && (
                    <span className="ppm-field-hint">
                      If both a Groups Periods config and a UGP config match, the UGP config prevails.
                    </span>
                  )}
                </>
              )}
            </div>
            </div>
            </div>
            )}

            {permissionSheetTab === 'rules' && (
            <div
              className="ppm-sheet-tab-panel"
              role="tabpanel"
              id="ppm-sheet-panel-rules"
              aria-labelledby="ppm-sheet-tab-rules"
            >
            {formScope === 'platform' && formPlatformKey === 'scope.ListedGroups' ? (
              <div className="ppm-form-section">
                <h3 className="ppm-form-section-title">Listed groups</h3>
                <p className="ppm-sheet-callout ppm-sheet-callout--scope-listed">
                  {scopeListedGroupsWarningForPlatform(formPlatformId)}
                </p>
                  <div className="ppm-form-row">
                    <span className="ppm-field-hint">
                      Required. Exact groups only — no look-down into child teams. Stores{' '}
                      <code>rules.scope_group_ids</code> (<code>groups.id</code>) when matching by
                      group, or <code>rules.scope_groups_periods_ids</code> for exact group-periods.
                      Listing a school/org does not include nested teams (e.g. Students under that
                      school); list those teams too, or use ParentPeerChildren /
                      OrganizationChildren instead.
                    </span>
                    <label className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={formScopeListedMatchByGroup}
                        onChange={(e) => setFormScopeListedMatchByGroup(e.target.checked)}
                      />
                      Match by group (recommended — stores groups.id)
                    </label>
                    <input
                      type="search"
                      value={scopeListedGpSearch}
                      onChange={(e) => setScopeListedGpSearch(e.target.value)}
                      placeholder="Search organizations and teams…"
                      className="ppm-field-input"
                      style={{ marginTop: 8 }}
                    />
                    <div className="ppm-gp-checkbox-list">
                      {scopeListedGpChoices.map((gp) => {
                        const checked = formScopeListedGpIds.includes(String(gp.id));
                        return (
                          <label key={gp.id} className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const id = String(gp.id);
                                if (e.target.checked) {
                                  setFormScopeListedGpIds((prev) =>
                                    prev.includes(id) ? prev : [...prev, id]
                                  );
                                } else {
                                  setFormScopeListedGpIds((prev) =>
                                    prev.filter((x) => x !== id)
                                  );
                                }
                              }}
                            />
                            {scopeListedGroupsPeriodLabel(gp)}
                            <span className="ppm-field-hint" style={{ marginLeft: 6 }}>
                              ({String(gp.group_id || '').slice(0, 8)}…)
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {formScopeListedGpIds.length > 0 && (
                      <button
                        type="button"
                        style={{
                          marginTop: 8,
                          background: 'none',
                          border: 'none',
                          color: '#1d4ed8',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: 13,
                        }}
                        onClick={() => setFormScopeListedGpIds([])}
                      >
                        Clear listed groups
                      </button>
                    )}
                  </div>
              </div>
            ) : (
              <div className="ppm-form-section">
                <h3 className="ppm-form-section-title">Student vendor (association)</h3>
                  <div className="ppm-form-row">
                    <span className="ppm-field-hint">
                      Optional. Grant only matches when the actor&apos;s current UGP has{' '}
                      <code>student_info.vendor_groups_periods_id</code> in the selection
                      {formVendorRuleMatchByGroup
                        ? ' (matched by vendor group across periods)'
                        : ' (exact vendor group-period)'}
                      .
                    </span>
                    <label className="ppm-checkbox-label" style={{ marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={formVendorRuleMatchByGroup}
                        onChange={(e) => setFormVendorRuleMatchByGroup(e.target.checked)}
                      />
                      Match by vendor group (any term)
                    </label>
                    <input
                      type="search"
                      value={vendorRuleGpSearch}
                      onChange={(e) => setVendorRuleGpSearch(e.target.value)}
                      placeholder="Search vendor group-periods…"
                      className="ppm-field-input"
                      style={{ marginTop: 8 }}
                    />
                    <div className="ppm-gp-checkbox-list">
                      {vendorRuleGpChoices.map((gp) => {
                        const checked = formVendorRuleGpIds.includes(String(gp.id));
                        return (
                          <label key={gp.id} className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const id = String(gp.id);
                                if (e.target.checked) {
                                  setFormVendorRuleGpIds((prev) =>
                                    prev.includes(id) ? prev : [...prev, id]
                                  );
                                } else {
                                  setFormVendorRuleGpIds((prev) =>
                                    prev.filter((x) => x !== id)
                                  );
                                }
                              }}
                            />
                            {gp.group_name} — {gp.period_title || gp.period_id}
                          </label>
                        );
                      })}
                    </div>
                    {formVendorRuleGpIds.length > 0 && (
                      <button
                        type="button"
                        style={{
                          marginTop: 8,
                          background: 'none',
                          border: 'none',
                          color: '#1d4ed8',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: 13,
                        }}
                        onClick={() => setFormVendorRuleGpIds([])}
                      >
                        Clear vendor rule
                      </button>
                    )}
                  </div>
              </div>
            )}
            </div>
            )}

            {permissionSheetTab === 'target' && formScope === 'platform' &&
              formPermissionType === 'page_configuration' &&
              formPlatformKey === 'home.widgets' && (
              <div className="ppm-form-section">
                <h3 className="ppm-form-section-title">Home widgets</h3>
                <p className="ppm-field-hint">
                  Optional widget ids for AcuityCTE home (comma-separated). Stored in configuration.
                </p>
                <input
                  type="text"
                  className="ppm-field-input"
                  value={(formConfigWidgets || []).join(', ')}
                  onChange={(e) =>
                    setFormConfigWidgets(
                      e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                    )
                  }
                  placeholder="e.g. my-hours, my-events"
                />
              </div>
            )}

            {permissionSheetTab === 'target' && formScope === 'platform' &&
              formPermissionType === 'page_configuration' &&
              formPlatformKey === HOUR_WAIVER_APPROVAL_PIPELINE_KEY && (
              <div className="ppm-form-section">
                <h3 className="ppm-form-section-title">Hour waiver approval pipeline</h3>
                <p className="ppm-field-hint">
                  All four levels always stay on the form so those roles can still decide.
                  Required only means the step must happen; a higher-rank approval still
                  counts as the decision for lower required steps.
                </p>
                <div className="ppm-pipeline-rows">
                  {(formApprovalPipeline || []).map((slot) => (
                    <label key={slot.key} className="ppm-pipeline-row">
                      <span className="ppm-pipeline-row__meta">
                        <span className="ppm-pipeline-row__label">{slot.label}</span>
                        <span className="ppm-pipeline-row__sub">
                          Rank {slot.rank}
                        </span>
                      </span>
                      <span className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={Boolean(slot.required)}
                          onChange={(e) =>
                            setFormApprovalPipeline((prev) =>
                              (prev || []).map((s) =>
                                s.key === slot.key ? { ...s, required: e.target.checked } : s
                              )
                            )
                          }
                        />
                        Required
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {permissionSheetTab === 'target' && formScope === 'platform' &&
              formPermissionType === 'page_configuration' &&
              formPlatformKey === 'app.sidebar' && (
              <div className="ppm-form-section">
                <h3 className="ppm-form-section-title">Sidebar items</h3>
                <p className="ppm-form-section-hint">
                  Order, label, path, icon, and nesting. Item <code>id</code> should match a
                  LearnPath <code>page_access</code> key so the nav can filter by permission.
                </p>
                <div className="ppm-sidebar-editor">
                  {(function renderSidebarRows(items, pathPrefix) {
                    return (items || []).map((item, index) => {
                      const path = [...pathPrefix, index];
                      const depth = pathPrefix.length;
                      return (
                        <div
                          key={path.join('-')}
                          className="ppm-sidebar-item"
                          style={{ marginLeft: depth ? `${depth}rem` : 0 }}
                        >
                          <div className="ppm-sidebar-item__grid">
                            <label className="ppm-sidebar-field">
                              <span>Order</span>
                              <input
                                type="number"
                                className="ppm-field-input"
                                value={item.order}
                                onChange={(e) => {
                                  const order = Number(e.target.value);
                                  setFormSidebarItems((prev) =>
                                    updateSidebarAt(prev, path, (row) => ({
                                      ...row,
                                      order: Number.isFinite(order) ? order : 0,
                                    }))
                                  );
                                }}
                              />
                            </label>
                            <label className="ppm-sidebar-field">
                              <span>Id</span>
                              <input
                                type="text"
                                className="ppm-field-input"
                                value={item.id}
                                placeholder="dashboard"
                                onChange={(e) =>
                                  setFormSidebarItems((prev) =>
                                    updateSidebarAt(prev, path, (row) => ({
                                      ...row,
                                      id: e.target.value,
                                    }))
                                  )
                                }
                              />
                            </label>
                            <label className="ppm-sidebar-field ppm-sidebar-field--wide">
                              <span>Label</span>
                              <input
                                type="text"
                                className="ppm-field-input"
                                value={item.label}
                                placeholder="Dashboard"
                                onChange={(e) =>
                                  setFormSidebarItems((prev) =>
                                    updateSidebarAt(prev, path, (row) => ({
                                      ...row,
                                      label: e.target.value,
                                    }))
                                  )
                                }
                              />
                            </label>
                            <label className="ppm-sidebar-field ppm-sidebar-field--wide">
                              <span>Path</span>
                              <input
                                type="text"
                                className="ppm-field-input"
                                value={item.path}
                                placeholder="/dashboard"
                                onChange={(e) =>
                                  setFormSidebarItems((prev) =>
                                    updateSidebarAt(prev, path, (row) => ({
                                      ...row,
                                      path: e.target.value,
                                    }))
                                  )
                                }
                              />
                            </label>
                            <label className="ppm-sidebar-field">
                              <span>Icon</span>
                              <input
                                type="text"
                                className="ppm-field-input"
                                value={item.icon}
                                placeholder="layout-dashboard"
                                onChange={(e) =>
                                  setFormSidebarItems((prev) =>
                                    updateSidebarAt(prev, path, (row) => ({
                                      ...row,
                                      icon: e.target.value,
                                    }))
                                  )
                                }
                              />
                            </label>
                            <label className="ppm-sidebar-field ppm-sidebar-field--check">
                              <span>Hidden</span>
                              <input
                                type="checkbox"
                                checked={item.hidden === true}
                                onChange={(e) =>
                                  setFormSidebarItems((prev) =>
                                    updateSidebarAt(prev, path, (row) => ({
                                      ...row,
                                      hidden: e.target.checked,
                                    }))
                                  )
                                }
                              />
                            </label>
                          </div>
                          <div className="ppm-sidebar-item__actions">
                            <button
                              type="button"
                              className="ppm-mini-btn"
                              onClick={() =>
                                setFormSidebarItems((prev) => moveSidebarAt(prev, path, -1))
                              }
                            >
                              Up
                            </button>
                            <button
                              type="button"
                              className="ppm-mini-btn"
                              onClick={() =>
                                setFormSidebarItems((prev) => moveSidebarAt(prev, path, 1))
                              }
                            >
                              Down
                            </button>
                            <button
                              type="button"
                              className="ppm-mini-btn"
                              onClick={() =>
                                setFormSidebarItems((prev) =>
                                  updateSidebarAt(prev, path, (row) => ({
                                    ...row,
                                    children: [
                                      ...(row.children || []),
                                      normalizeSidebarItem({
                                        id: '',
                                        label: '',
                                        path: '',
                                        icon: '',
                                        order: ((row.children || []).length + 1) * 10,
                                      }),
                                    ],
                                  }))
                                )
                              }
                            >
                              Nest
                            </button>
                            <button
                              type="button"
                              className="ppm-mini-btn ppm-mini-btn--danger"
                              onClick={() =>
                                setFormSidebarItems((prev) => removeSidebarAt(prev, path))
                              }
                            >
                              Remove
                            </button>
                          </div>
                          {item.children?.length
                            ? renderSidebarRows(item.children, path)
                            : null}
                        </div>
                      );
                    });
                  })(formSidebarItems, [])}
                </div>
                <div className="ppm-sidebar-editor__footer">
                  <button
                    type="button"
                    className="ppm-mini-btn"
                    onClick={() =>
                      setFormSidebarItems((prev) => [
                        ...prev,
                        normalizeSidebarItem({
                          id: '',
                          label: '',
                          path: '',
                          icon: '',
                          order: (prev.length + 1) * 10,
                        }),
                      ])
                    }
                  >
                    Add item
                  </button>
                  <button
                    type="button"
                    className="ppm-mini-btn"
                    onClick={() =>
                      setFormSidebarItems(
                        DEFAULT_LEARNPATH_SIDEBAR_ITEMS.map((x) => ({ ...x, children: [] }))
                      )
                    }
                  >
                    Reset defaults
                  </button>
                </div>
              </div>
            )}

            {permissionSheetTab === 'target' && formScope === 'page' && formPermissionType === 'page_configuration' && (
              <details
                className="ppm-details-section ppm-details-section--page-config"
                open
              >
                <summary>
                  Page options · {KNOWN_PAGES.find((p) => p.id === formPage)?.label || formPage}
                </summary>
                <div className="ppm-details-section__body">
                {formPage === 'dashboard' && (
                  <div className="ppm-page-section">
                    <div className="ppm-form-row">
                      <label className="ppm-field-label">Dashboard widgets</label>
                      <div className="ppm-checkbox-grid ppm-checkbox-grid--widgets">
                        {DASHBOARD_WIDGETS.map(w => (
                          <label key={w.id} className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigWidgets.includes(w.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormConfigWidgets([...formConfigWidgets, w.id]);
                                } else {
                                  setFormConfigWidgets(formConfigWidgets.filter(x => x !== w.id));
                                }
                              }}
                            />
                            {w.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {formPage === 'groups' && (
                  <div className="ppm-page-section">
                    <div className="ppm-form-row">
                      <label className="ppm-field-label">View (required)</label>
                      <select
                        value={formConfigGroupsView}
                        onChange={(e) => setFormConfigGroupsView(e.target.value)}
                        className="ppm-field-input"
                      >
                        <option value="">— Select view —</option>
                        {GROUPS_VIEW_OPTIONS.map(v => (
                          <option key={v.id} value={v.id}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="ppm-form-grid-2">
                    <div className="ppm-form-row" style={{ marginBottom: 0 }}>
                      <label className="ppm-field-label">Include teams (optional)</label>
                      <input
                        type="text"
                        value={formConfigRoleGroupNameIncludes}
                        onChange={(e) => setFormConfigRoleGroupNameIncludes(e.target.value)}
                        placeholder="Classroom, Students"
                        className="ppm-field-input"
                      />
                    </div>
                    <div className="ppm-form-row" style={{ marginBottom: 0 }}>
                      <label className="ppm-field-label">Exclude by name (legacy)</label>
                      <input
                        type="text"
                        value={formConfigRoleGroupNameExcludes}
                        onChange={(e) => setFormConfigRoleGroupNameExcludes(e.target.value)}
                        placeholder="Super Admin"
                        className="ppm-field-input"
                      />
                      <span className="ppm-field-hint">
                        Substring on team name — breaks if the team is renamed. Use checkboxes below when possible.
                      </span>
                    </div>
                    </div>
                    {groupedTeamsForIdExcludePicker.length > 0 ? (
                      <details
                        className="ppm-details-section ppm-details-section--team-excludes"
                        open={excludeTeamsDetailsOpen}
                        onToggle={(e) => setExcludeTeamsDetailsOpen(e.currentTarget.open)}
                      >
                        <summary>
                          Exclude teams by group id
                          {formConfigRoleGroupIdExcludes.length > 0
                            ? ` (${formConfigRoleGroupIdExcludes.length})`
                            : ''}
                        </summary>
                        <div className="ppm-details-section__body">
                          <div className="ppm-gp-checkbox-list">
                            {groupedTeamsForIdExcludePicker.map((group) => (
                              <div key={group.organization} className="ppm-grantee-group">
                                <div className="ppm-grantee-group-title">{group.organization}</div>
                                {group.items.map((team) => {
                                  const tid = String(team.id);
                                  const checked = formConfigRoleGroupIdExcludes.includes(tid);
                                  return (
                                    <label key={tid} className="ppm-checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          if (!checked) setExcludeTeamsDetailsOpen(true);
                                          setFormConfigRoleGroupIdExcludes((prev) =>
                                            checked ? prev.filter((id) => id !== tid) : [...prev, tid]
                                          );
                                        }}
                                      />
                                      {team.name}
                                    </label>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                          <span className="ppm-field-hint">
                            Hides the team and nested groups even after rename. Re-open this permission to add new teams.
                          </span>
                        </div>
                      </details>
                    ) : null}
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigGroupsCreateEntities}
                          onChange={(e) => setFormConfigGroupsCreateEntities(e.target.checked)}
                        />
                        Can create entities (organizations, groups, periods — list toolbar + buttons)
                      </label>
                    </div>
                    {formConfigGroupsCreateEntities && (
                      <div className="ppm-nested-controls">
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsCreateOrganizations}
                              onChange={(e) => setFormConfigGroupsCreateOrganizations(e.target.checked)}
                            />
                            Can create organizations (global/System use)
                          </label>
                        </div>
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsCreateOwnGroup}
                              onChange={(e) => setFormConfigGroupsCreateOwnGroup(e.target.checked)}
                            />
                            Create entities in own group + nested descendants
                          </label>
                        </div>
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsCreateSameLevel}
                              onChange={(e) => setFormConfigGroupsCreateSameLevel(e.target.checked)}
                            />
                            Create entities in same-level peers + nested descendants
                          </label>
                        </div>
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsCreateOrgWide}
                              onChange={(e) => setFormConfigGroupsCreateOrgWide(e.target.checked)}
                            />
                            Create entities org-wide (System/global override)
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigGroupsAddMembers}
                          onChange={(e) => setFormConfigGroupsAddMembers(e.target.checked)}
                        />
                        Can add members (+ on members bar — enrolls user in selected group-period)
                      </label>
                    </div>
                    {formConfigGroupsAddMembers && (
                      <div className="ppm-nested-controls">
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsAddMembersOwnGroup}
                              onChange={(e) => setFormConfigGroupsAddMembersOwnGroup(e.target.checked)}
                            />
                            Add members to own group + nested (enrolled team and descendants)
                          </label>
                        </div>
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsAddMembersSameLevel}
                              onChange={(e) => setFormConfigGroupsAddMembersSameLevel(e.target.checked)}
                            />
                            Add members to same-level peers + nested (requires visibility of those teams)
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigGroupsOpenMemberProfiles}
                          onChange={(e) => setFormConfigGroupsOpenMemberProfiles(e.target.checked)}
                        />
                        Open member profiles (click member row → User Profile)
                      </label>
                      <span className="ppm-field-hint">
                        Members stay listed when off; rows are not links. User Profiles page access is
                        still required separately.
                      </span>
                    </div>
                    {formConfigGroupsOpenMemberProfiles && (
                      <div className="ppm-nested-controls">
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsOpenMemberProfilesOwnGroup}
                              onChange={(e) =>
                                setFormConfigGroupsOpenMemberProfilesOwnGroup(e.target.checked)
                              }
                            />
                            Open profiles in own group + nested
                          </label>
                        </div>
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsOpenMemberProfilesSameLevel}
                              onChange={(e) =>
                                setFormConfigGroupsOpenMemberProfilesSameLevel(e.target.checked)
                              }
                            />
                            Open profiles in same-level peers + nested
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigGroupsViewGroupMembers}
                          onChange={(e) => setFormConfigGroupsViewGroupMembers(e.target.checked)}
                        />
                        View group members (member roster in members panel)
                      </label>
                      <span className="ppm-field-hint">
                        Opt-in: other members are hidden unless this is enabled with at least one scope
                        below. You always see your own enrollment in the list.
                      </span>
                    </div>
                    {formConfigGroupsViewGroupMembers && (
                      <div className="ppm-nested-controls">
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsViewGroupMembersOwnGroup}
                              onChange={(e) =>
                                setFormConfigGroupsViewGroupMembersOwnGroup(e.target.checked)
                              }
                            />
                            View members in own group + nested
                          </label>
                        </div>
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsViewGroupMembersSameLevel}
                              onChange={(e) =>
                                setFormConfigGroupsViewGroupMembersSameLevel(e.target.checked)
                              }
                            />
                            View members in same-level peers + nested
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigGroupsViewMemberDetails}
                          onChange={(e) => setFormConfigGroupsViewMemberDetails(e.target.checked)}
                        />
                        View member details (email on member rows)
                      </label>
                      <span className="ppm-field-hint">
                        Opt-in: names still show when the roster is visible; email is hidden unless
                        this is enabled with at least one scope below.
                      </span>
                    </div>
                    {formConfigGroupsViewMemberDetails && (
                      <div className="ppm-nested-controls">
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsViewMemberDetailsOwnGroup}
                              onChange={(e) =>
                                setFormConfigGroupsViewMemberDetailsOwnGroup(e.target.checked)
                              }
                            />
                            View details in own group + nested
                          </label>
                        </div>
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsViewMemberDetailsSameLevel}
                              onChange={(e) =>
                                setFormConfigGroupsViewMemberDetailsSameLevel(e.target.checked)
                              }
                            />
                            View details in same-level peers + nested
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigGroupsViewMemberAccess}
                          onChange={(e) => setFormConfigGroupsViewMemberAccess(e.target.checked)}
                        />
                        View member access (enrollment window, override, UIS login)
                      </label>
                      <span className="ppm-field-hint">
                        Opt-in: shows ugp start/end, override_period, is_active, can_login, and
                        enrollment badges (valid / past period / late start / ends early) on member
                        rows. Requires a scope below.
                      </span>
                    </div>
                    {formConfigGroupsViewMemberAccess && (
                      <div className="ppm-nested-controls">
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsViewMemberAccessOwnGroup}
                              onChange={(e) =>
                                setFormConfigGroupsViewMemberAccessOwnGroup(e.target.checked)
                              }
                            />
                            View access in own group + nested
                          </label>
                        </div>
                        <div className="ppm-form-row">
                          <label className="ppm-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formConfigGroupsViewMemberAccessSameLevel}
                              onChange={(e) =>
                                setFormConfigGroupsViewMemberAccessSameLevel(e.target.checked)
                              }
                            />
                            View access in same-level peers + nested
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigGroupsViewDerivedGroups}
                          onChange={(e) => setFormConfigGroupsViewDerivedGroups(e.target.checked)}
                        />
                        View derived groups (Guardians node from student person links)
                      </label>
                      <span className="ppm-field-hint">
                        Shows the org-level Guardians node when linked guardians exist. Full roster
                        loads when the node is opened.
                      </span>
                    </div>
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigGroupsSeeAllSameLevel}
                          onChange={(e) => setFormConfigGroupsSeeAllSameLevel(e.target.checked)}
                        />
                        See same-level peers + nested
                      </label>
                    </div>
                  </div>
                )}

                {formPage === 'profile' && (
                  <div className="ppm-page-section">
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigProfileEditName}
                          onChange={(e) => setFormConfigProfileEditName(e.target.checked)}
                        />
                        Can edit name (first name and last name)
                      </label>
                    </div>
                    <div className="ppm-form-row">
                      <label className="ppm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formConfigProfileUpdatePassword}
                          onChange={(e) => setFormConfigProfileUpdatePassword(e.target.checked)}
                        />
                        Can update password
                      </label>
                    </div>
                  </div>
                )}

                {formPage === 'scheduler' && (
                  <div className="ppm-page-section">
                    <p className="ppm-field-hint" style={{ marginTop: 0 }}>
                      Applies to all scheduler period types (enrollment, track, learning, exceptions).
                      Page access alone allows viewing; enable create / edit / remove scopes below.
                    </p>
                    <div className="ppm-form-row">
                      <strong className="ppm-field-label">All organizations</strong>
                    </div>
                    <div className="ppm-nested-controls">
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigSchedulerCreateAllOrgs}
                            onChange={(e) => setFormConfigSchedulerCreateAllOrgs(e.target.checked)}
                          />
                          Create periods (all organizations)
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigSchedulerEditAllOrgs}
                            onChange={(e) => setFormConfigSchedulerEditAllOrgs(e.target.checked)}
                          />
                          Edit periods (all organizations)
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigSchedulerRemoveAllOrgs}
                            onChange={(e) => setFormConfigSchedulerRemoveAllOrgs(e.target.checked)}
                          />
                          Remove periods (all organizations)
                        </label>
                      </div>
                    </div>
                    <div className="ppm-form-row" style={{ marginTop: 12 }}>
                      <strong className="ppm-field-label">Current organization</strong>
                      <span className="ppm-field-hint">
                        Organization from the user&apos;s active membership (nav context).
                      </span>
                    </div>
                    <div className="ppm-nested-controls">
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigSchedulerCreateOwnOrg}
                            onChange={(e) => setFormConfigSchedulerCreateOwnOrg(e.target.checked)}
                          />
                          Create periods (current organization)
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigSchedulerEditOwnOrg}
                            onChange={(e) => setFormConfigSchedulerEditOwnOrg(e.target.checked)}
                          />
                          Edit periods (current organization)
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigSchedulerRemoveOwnOrg}
                            onChange={(e) => setFormConfigSchedulerRemoveOwnOrg(e.target.checked)}
                          />
                          Remove periods (current organization)
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {formPage === 'forms' && (
                  <div className="ppm-page-section">
                    <p className="ppm-field-hint" style={{ marginTop: 0 }}>
                      Forms act as teacher assignments (question builder + group-period assignment). Library
                      visibility is scoped below; actions apply within that scope.
                    </p>
                    <div className="ppm-form-row">
                      <label className="ppm-field-label" htmlFor="ppm-forms-view-scope">
                        Library view scope
                      </label>
                      <select
                        id="ppm-forms-view-scope"
                        className="ppm-input"
                        value={formConfigFormsViewScope}
                        onChange={(e) => setFormConfigFormsViewScope(e.target.value)}
                      >
                        <option value="own">Own forms only (created_by = user)</option>
                        <option value="organization">Organization (same org + legacy unscoped)</option>
                        <option value="all">All forms (platform-wide)</option>
                      </select>
                    </div>
                    <div className="ppm-nested-controls">
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigFormsTaxonomyScope === 'all_organizations'}
                            onChange={(e) =>
                              setFormConfigFormsTaxonomyScope(
                                e.target.checked ? 'all_organizations' : 'own_organization'
                              )
                            }
                          />
                          View all organizations taxonomy (subject picker)
                        </label>
                      </div>
                      <p className="ppm-field-hint" style={{ marginTop: 0 }}>
                        When off, the form subject picker shows global subjects plus your organization only.
                        When on, subjects from every organization appear in the picker.
                      </p>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigFormsCreate}
                            onChange={(e) => setFormConfigFormsCreate(e.target.checked)}
                          />
                          Create forms
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigFormsEdit}
                            onChange={(e) => setFormConfigFormsEdit(e.target.checked)}
                          />
                          Edit forms (within view scope)
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigFormsDelete}
                            onChange={(e) => setFormConfigFormsDelete(e.target.checked)}
                          />
                          Delete forms (within view scope)
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigFormsAssign}
                            onChange={(e) => setFormConfigFormsAssign(e.target.checked)}
                          />
                          Assign forms to users / group-periods
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {formPage === 'work-samples' && (
                  <div className="ppm-page-section">
                    <p className="ppm-field-hint" style={{ marginTop: 0 }}>
                      Work Samples at <code>/work-samples</code>. TOR uses linked students; VDM uses
                      organization scope or review permission to see all students (including draft
                      portfolios before submit). Vendor scope is read-only and limits the roster to
                      students whose vendor group-period matches the viewer&apos;s vendor membership
                      &mdash; grant it to a vendor team group-period (e.g. FA01) and leave the write
                      capabilities below unchecked.
                    </p>
                    <div className="ppm-form-row">
                      <label className="ppm-field-label" htmlFor="ppm-ws-view-scope">
                        View scope
                      </label>
                      <select
                        id="ppm-ws-view-scope"
                        className="ppm-input"
                        value={formConfigWsViewScope}
                        onChange={(e) => setFormConfigWsViewScope(e.target.value)}
                      >
                        <option value="linked_students">
                          Linked students (TOR — teacher_of_record links)
                        </option>
                        <option value="organization">
                          Organization (all students under workflow student team)
                        </option>
                        <option value="vendor">
                          Vendor (read-only — only students in the viewer&apos;s vendor group)
                        </option>
                        <option value="all">
                          All organizations (system / cross-org — org picker)
                        </option>
                      </select>
                    </div>
                    <div className="ppm-nested-controls">
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigWsAssignForms}
                            onChange={(e) => setFormConfigWsAssignForms(e.target.checked)}
                          />
                          Assign forms to linked students (relationship type: assignment)
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigWsCurate}
                            onChange={(e) => setFormConfigWsCurate(e.target.checked)}
                          />
                          Curate work samples (mark submissions/docs as portfolio evidence)
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigWsRemove}
                            onChange={(e) => setFormConfigWsRemove(e.target.checked)}
                          />
                          Remove work samples
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigWsSubmit}
                            onChange={(e) => setFormConfigWsSubmit(e.target.checked)}
                          />
                          Submit portfolios for VDM review
                        </label>
                      </div>
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigWsReview}
                            onChange={(e) => setFormConfigWsReview(e.target.checked)}
                          />
                          Review portfolios (start review, mark passed, org roster)
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {formPage === 'inquiries' && (
                  <div className="ppm-page-section">
                    <p className="ppm-field-hint" style={{ marginTop: 0 }}>
                      Inquiries at <code>/inquiries</code>. Grant this page to whoever should see
                      the list. Turn on notify to email those same grantees whenever a school or
                      family form is submitted.
                    </p>
                    <div className="ppm-nested-controls">
                      <div className="ppm-form-row">
                        <label className="ppm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={formConfigNotifyOnInquiry}
                            onChange={(e) => setFormConfigNotifyOnInquiry(e.target.checked)}
                          />
                          Notify on new inquiry (email everyone included in this grant)
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {formPage === 'user-profiles' && (
                  <div className="ppm-page-section">
                    {USER_PROFILE_CAPABILITY_GROUPS.map((group) => {
                      const checkedCount = group.items.filter((item) => {
                        const binding = userProfileCapabilityBindings[item.stateKey];
                        return binding?.[0];
                      }).length;
                      const isCustomFormsGroup = group.title === 'Custom forms';
                      return (
                        <details key={group.title} className="ppm-capability-details">
                          <summary>
                            {group.title}
                            {checkedCount > 0 ? ` (${checkedCount})` : ''}
                          </summary>
                          <div className="ppm-capability-details__body">
                            {isCustomFormsGroup ? (
                              <>
                                <div className="ppm-form-row">
                                  <label
                                    className="ppm-field-label"
                                    htmlFor="ppm-custom-forms-submission-scope"
                                  >
                                    Submission scope
                                  </label>
                                  <select
                                    id="ppm-custom-forms-submission-scope"
                                    className="ppm-input"
                                    value={formConfigCustomFormsSubmissionScope}
                                    onChange={(e) =>
                                      setFormConfigCustomFormsSubmissionScope(e.target.value)
                                    }
                                  >
                                    <option value="own_profile_only">Own profile only</option>
                                    <option value="assigned_students">
                                      Assigned students (other profiles)
                                    </option>
                                    <option value="organization">
                                      Organization (profiles you can open)
                                    </option>
                                    <option value="platform">Platform-wide</option>
                                  </select>
                                </div>
                                <p className="ppm-field-hint" style={{ marginTop: 0 }}>
                                  Scope gates whose profile submissions the flags below apply to.
                                  Max attempts and resubmission rules are assignment policy, not
                                  permissions.
                                </p>
                              </>
                            ) : null}
                            {group.items.map((item) => {
                              const binding = userProfileCapabilityBindings[item.stateKey];
                              if (!binding) return null;
                              const [checked, setChecked] = binding;
                              return (
                                <div key={item.id} className="ppm-form-row">
                                  <label className="ppm-checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => setChecked(e.target.checked)}
                                    />
                                    {item.label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      );
                    })}
                    {(() => {
                      const collab = formConfigCollaboration;
                      const chatCount = COLLABORATION_CHAT_ROWS.reduce(
                        (sum, row) =>
                          sum + row.caps.filter(([key]) => collab.chat[row.group][key]).length,
                        0
                      );
                      const flagCount = COLLABORATION_FLAG_ROWS.filter(
                        ([key]) => collab.flags[key]
                      ).length;
                      const adminCount = collab.admin.viewAllPrivateMessages ? 1 : 0;
                      const total = chatCount + flagCount + adminCount;
                      return (
                        <details className="ppm-capability-details">
                          <summary>
                            Custom forms — Collaboration{total > 0 ? ` (${total})` : ''}
                          </summary>
                          <div className="ppm-capability-details__body">
                            <p className="ppm-field-hint" style={{ marginTop: 0 }}>
                              Chat (Student / Internal / My Notes) and Flags on the{' '}
                              <code>/user-profiles</code> assignment viewer. Staff capabilities are
                              set here; the assignee&apos;s access is set per assignment.
                            </p>
                            <strong className="ppm-subsection-title">Chat</strong>
                            {COLLABORATION_CHAT_ROWS.map((row) => (
                              <div key={row.group} className="ppm-form-row">
                                <div className="ppm-collab-row-label">{row.label}</div>
                                <div className="ppm-collab-row-caps">
                                  {row.caps.map(([key, label]) => (
                                    <label key={key} className="ppm-checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={collab.chat[row.group][key] === true}
                                        onChange={() => toggleCollaboration('chat', row.group, key)}
                                      />
                                      {label}
                                    </label>
                                  ))}
                                </div>
                                <p className="ppm-field-hint" style={{ margin: '2px 0 0' }}>
                                  {row.hint}
                                </p>
                              </div>
                            ))}
                            <strong className="ppm-subsection-title">Flags</strong>
                            {COLLABORATION_FLAG_ROWS.map(([key, label]) => (
                              <div key={key} className="ppm-form-row">
                                <label className="ppm-checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={collab.flags[key] === true}
                                    onChange={() => toggleCollaboration('flags', null, key)}
                                  />
                                  {label}
                                </label>
                              </div>
                            ))}
                            <strong className="ppm-subsection-title">Admin</strong>
                            <div className="ppm-form-row">
                              <label className="ppm-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={collab.admin.viewAllPrivateMessages === true}
                                  onChange={() =>
                                    toggleCollaboration('admin', null, 'viewAllPrivateMessages')
                                  }
                                />
                                View all private notes (audit My Notes across staff)
                              </label>
                            </div>
                          </div>
                        </details>
                      );
                    })()}
                    <details className="ppm-layout-details">
                      <summary>
                        Profile layout
                        {(userProfileViewLayout?.columns || []).length > 0
                          ? ` (${userProfileViewLayout.columns.length} column${userProfileViewLayout.columns.length === 1 ? '' : 's'})`
                          : ''}
                      </summary>
                      <div className="ppm-layout-details__body">
                        <p className="ppm-page-section-desc">
                          Right-column sections. Empty = left column only. Apps from{' '}
                          <Link to="/apps" style={{ color: '#4f46e5', fontWeight: 500 }}>Apps Manager</Link>.
                        </p>
                        {(userProfileViewDisplayColumns || []).length > 0 ? (
                          <div className="ppm-layout-col-list">
                            {(userProfileViewDisplayColumns || [])
                              .slice()
                              .sort((a, b) => (a.order || 0) - (b.order || 0))
                              .map((col) => {
                                const isColumnIncluded = (userProfileViewLayout?.columns || []).some(
                                  (c) => c.id === col.id
                                );
                                return (
                                  <div key={col.id} className="ppm-layout-col">
                                    <div className="ppm-layout-col__head">
                                      <label className="ppm-layout-col__title">
                                        <input
                                          type="checkbox"
                                          checked={isColumnIncluded}
                                          onChange={() => handleUserProfileViewToggleColumn(col.id)}
                                        />
                                        {col.title || col.id}
                                      </label>
                                      <span className="ppm-layout-col__id">{col.id}</span>
                                    </div>
                                    {isColumnIncluded &&
                                      (() => {
                                        const defaultColBuckets =
                                          (DEFAULT_USER_PROFILE_VIEW_CONFIG.columns || []).find(
                                            (c) => c.id === col.id
                                          )?.buckets ||
                                          col.buckets ||
                                          [];
                                        const formConfigCol = (userProfileViewLayout?.columns || []).find(
                                          (c) => c.id === col.id
                                        );
                                        return Array.isArray(defaultColBuckets) &&
                                          defaultColBuckets.length > 0 ? (
                                          <div className="ppm-layout-buckets">
                                            {defaultColBuckets
                                              .slice()
                                              .sort((a, b) => (a.order || 0) - (b.order || 0))
                                              .map((bucket) => {
                                                const isBucketIncluded = (
                                                  formConfigCol?.buckets || []
                                                ).some((b) => b.id === bucket.id);
                                                return (
                                                  <label
                                                    key={bucket.id}
                                                    className={`ppm-layout-bucket${isBucketIncluded ? ' ppm-layout-bucket--on' : ''}`}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      checked={isBucketIncluded}
                                                      onChange={() =>
                                                        handleUserProfileViewToggleBucket(
                                                          col.id,
                                                          bucket.id
                                                        )
                                                      }
                                                    />
                                                    {bucket.title || bucket.id}
                                                  </label>
                                                );
                                              })}
                                          </div>
                                        ) : null;
                                      })()}
                                  </div>
                                );
                              })}
                          </div>
                        ) : null}
                        <div className="ppm-layout-actions">
                          <button
                            type="button"
                            onClick={handleUserProfileViewClearLayout}
                            style={styles.smallButton}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            onClick={handleUserProfileViewLoadPresetLayout}
                            style={styles.smallButton}
                          >
                            Load preset
                          </button>
                        </div>
                        <div className="ppm-form-row">
                          <label className="ppm-field-label">Layout JSON</label>
                          <textarea
                            className="ppm-layout-json"
                            value={formUserProfileViewJson}
                            onChange={(e) => setFormUserProfileViewJson(e.target.value)}
                            placeholder="[]"
                          />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                            <button
                              type="button"
                              onClick={handleUserProfileViewApplyJson}
                              style={{
                                ...styles.smallButton,
                                borderColor: '#3b82f6',
                                color: '#1d4ed8',
                                background: '#eff6ff'
                              }}
                            >
                              Apply JSON
                            </button>
                            {formUserProfileViewJsonError && (
                              <span style={{ fontSize: '0.6875rem', color: '#b91c1c' }}>
                                {formUserProfileViewJsonError}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </details>
                  </div>
                )}

                {formPage !== 'dashboard' &&
                  formPage !== 'groups' &&
                  formPage !== 'user-profiles' &&
                  formPage !== 'profile' && (
                  <div className="ppm-page-section">
                    <p className="ppm-page-section-desc">No page-specific options; access is granted by this permission alone.</p>
                  </div>
                )}
                </div>
              </details>
            )}

            </fieldset>
            </div>

            <div className="ppm-sheet-actions">
              <button
                type="button"
                className="ppm-sheet-btn ppm-sheet-btn--secondary"
                onClick={closeCreateModal}
                disabled={isSaving}
              >
                {modalOwnerReadOnly ? 'Close' : 'Cancel'}
              </button>
              {!modalOwnerReadOnly ? (
                <button
                  type="button"
                  className="ppm-sheet-btn ppm-sheet-btn--primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <AlertModal
          isOpen={true}
          onClose={() => setDeleteConfirmId(null)}
          variant="warning"
          subject="Delete Permission"
          body="Are you sure you want to delete this permission? Access will be revoked for the affected grantees."
          icon="⚠️"
          showCloseButton={true}
          allowOverlayClose={false}
          actions={[
            { label: 'Delete', onClick: handleDelete, variant: 'primary', style: { background: '#dc3545' } }
          ]}
          zIndex={3000}
        />
      )}

      {cornerNotice ? (
        <CornerNotice
          {...cornerNotice}
          onClose={() => setCornerNotice(null)}
        />
      ) : null}

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={modalData?.title}
        message={modalData?.message}
        details={modalData?.details}
        zIndex={2000}
      />
    </div>
  );
};

const styles = {
  container: {
    padding: '18px 20px',
    maxWidth: 1200,
    margin: '0 auto'
  },
  header: {
    marginBottom: 16
  },
  title: {
    fontSize: '1.45rem',
    fontWeight: 650,
    color: '#1f2937',
    margin: '0 0 6px 0'
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#6b7280',
    margin: '0 0 14px 0'
  },
  toolbar: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap'
  },
  widgetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))',
    gap: 8
  },
  pageWidget: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 6,
    minHeight: 64,
    height: 64,
    padding: '8px 10px',
    border: '1px solid #eceef1',
    borderRadius: 8,
    background: '#fafafa',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease'
  },
  pageWidgetSelected: {
    borderColor: 'var(--color-brand)',
    background: 'var(--color-brand-muted)',
    boxShadow: '0 0 0 1px var(--color-brand-soft)'
  },
  pageWidgetTitle: {
    fontSize: '0.7rem',
    fontWeight: 500,
    color: '#52525b',
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    width: '100%'
  },
  pageWidgetTitleSelected: {
    color: 'var(--color-brand-hover)',
    fontWeight: 600
  },
  pageWidgetMeta: {
    fontSize: '0.62rem',
    fontWeight: 400,
    color: '#a1a1aa',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%'
  },
  pageWidgetMetaSelected: {
    color: 'var(--color-brand)'
  },
  bottomPanel: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: '50vh',
    bottom: 0,
    zIndex: 1200,
    background: 'rgba(255, 255, 255, 0.96)',
    backdropFilter: 'blur(10px)',
    borderTop: '1px solid #e5e7eb',
    boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.08)',
    display: 'flex',
    flexDirection: 'column'
  },
  bottomPanelInner: {
    maxWidth: 1200,
    margin: '0 auto',
    width: '100%',
    height: '100%',
    padding: '12px 20px 14px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1
  },
  bottomPanelHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexShrink: 0
  },
  bottomPanelTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0
  },
  bottomPanelTitle: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#111827'
  },
  bottomPanelSubtitle: {
    margin: '2px 0 0',
    fontSize: '0.72rem',
    color: '#94a3b8'
  },
  bottomPanelHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 6,
    flexShrink: 0,
    maxWidth: '100%'
  },
  bottomPanelClose: {
    width: 28,
    height: 28,
    border: 'none',
    background: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: '1.25rem',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    fontFamily: 'inherit'
  },
  bottomPanelEmpty: {
    margin: 0,
    padding: '16px 0',
    fontSize: '0.8rem',
    color: '#94a3b8',
    textAlign: 'center'
  },
  compactTableWrap: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    border: '1px solid #eef2f7',
    borderRadius: 8,
    background: '#fafbfc'
  },
  compactTable: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed'
  },
  compactTh: {
    textAlign: 'left',
    padding: '5px 8px',
    background: '#f1f5f9',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '0.65rem',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  compactRow: {
    height: 32
  },
  compactTdText: {
    padding: '4px 8px',
    borderBottom: '1px solid #eef2f7',
    fontSize: '0.72rem',
    color: '#475569',
    height: 32,
    maxHeight: 32,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle'
  },
  compactTdName: {
    fontWeight: 500,
    color: '#334155'
  },
  compactTdIcon: {
    padding: '4px 8px',
    borderBottom: '1px solid #eef2f7',
    fontSize: '0.72rem',
    color: '#475569',
    height: 32,
    maxHeight: 32,
    overflow: 'visible',
    verticalAlign: 'middle'
  },
  compactTdActions: {
    padding: '4px 6px',
    borderBottom: '1px solid #eef2f7',
    height: 32,
    maxHeight: 32,
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    textAlign: 'right',
    width: 68
  },
  typeIconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24
  },
  scopeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 32,
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
    color: '#475569',
    background: '#f1f5f9',
    border: '1px solid #e2e8f0'
  },
  bottomPanelLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px 12px',
    marginTop: 8,
    paddingTop: 6,
    borderTop: '1px solid #f1f5f9',
    flexShrink: 0
  },
  legendLabel: {
    fontSize: '0.62rem',
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  legendItem: {
    fontSize: '0.62rem',
    color: '#94a3b8',
    whiteSpace: 'nowrap'
  },
  plusButton: {
    width: 34,
    height: 34,
    borderRadius: 999,
    border: '1px solid #d1d5db',
    background: '#ffffff',
    color: '#374151',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)'
  },
  plusGlyph: {
    fontSize: 19,
    lineHeight: 1,
    marginTop: -1
  },
  plusButtonSm: {
    width: 28,
    height: 28,
    borderRadius: 999,
    border: '1px solid #d1d5db',
    background: '#ffffff',
    color: '#374151',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  plusGlyphSm: {
    fontSize: 16,
    lineHeight: 1
  },
  filters: {
    display: 'flex',
    gap: 12,
    alignItems: 'center'
  },
  filterLabel: {
    fontSize: 13,
    color: '#6b7280',
    marginRight: -4
  },
  select: {
    padding: '6px 10px',
    borderRadius: 7,
    border: '1px solid #d1d5db',
    fontSize: 13,
    minWidth: 150
  },
  input: {
    padding: '7px 10px',
    borderRadius: 7,
    border: '1px solid #d1d5db',
    fontSize: 13,
    width: '100%',
    maxWidth: 400
  },
  primaryButton: {
    padding: '8px 14px',
    background: '#374151',
    color: 'white',
    border: 'none',
    borderRadius: 7,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500
  },
  secondaryButton: {
    padding: '8px 14px',
    background: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: 7,
    cursor: 'pointer',
    fontSize: 13
  },
  smallButton: {
    padding: '5px 10px',
    background: '#ffffff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12
  },
  iconButton: {
    width: 28,
    height: 28,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    background: '#ffffff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1,
    marginRight: 6
  },
  iconButtonDanger: {
    width: 28,
    height: 28,
    border: '1px solid #fecaca',
    borderRadius: 6,
    background: '#fff5f5',
    color: '#991b1b',
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1
  },
  tableWrap: {
    overflowX: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: 'white'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    fontSize: 12,
    fontWeight: 600,
    color: '#374151'
  },
  td: {
    padding: '10px 14px',
    borderBottom: '1px solid #e5e7eb',
    fontSize: 13,
    color: '#374151'
  },
  emptyCell: {
    padding: 40,
    textAlign: 'center',
    color: '#6b7280'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  modal: {
    background: 'white',
    borderRadius: 12,
    padding: 18,
    maxWidth: 500,
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalTitle: {
    margin: '0 0 14px 0',
    fontSize: '1.1rem',
    fontWeight: 600
  },
  formRow: {
    marginBottom: 12
  },
  nestedControls: {
    margin: '2px 0 10px 26px',
    padding: '10px 12px 2px',
    borderLeft: '2px solid #dbeafe',
    background: '#f8fbff',
    borderRadius: 8
  },
  formSection: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    background: '#fcfdff',
    padding: '12px 12px 2px',
    marginBottom: 12
  },
  formSectionTitle: {
    margin: '0 0 2px 0',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1f2937'
  },
  formSectionHint: {
    margin: '0 0 10px 0',
    fontSize: '0.8rem',
    color: '#64748b'
  },
  pageSection: {
    marginTop: 16,
    padding: 16,
    background: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #e2e8f0'
  },
  pageSectionTitle: {
    margin: '0 0 4px 0',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#374151'
  },
  pageSectionDesc: {
    margin: '0 0 12px 0',
    fontSize: '0.875rem',
    color: '#6b7280',
    lineHeight: 1.4
  },
  ownerProtectedBanner: {
    margin: '0 0 12px 0',
    padding: '10px 12px',
    fontSize: '0.8rem',
    lineHeight: 1.45,
    color: '#92400e',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 8
  },
  ownerProtectedFieldset: {
    border: 'none',
    margin: 0,
    padding: 0,
    minWidth: 0
  },
  checkboxLabelOwnerProtected: {
    opacity: 0.85,
    color: '#64748b'
  },
  hint: {
    fontSize: '0.85rem',
    color: '#6b7280',
    marginTop: 4,
    display: 'block'
  },
  label: {
    display: 'block',
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 500,
    color: '#374151'
  },
  checkboxGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    cursor: 'pointer'
  },
  gpCheckboxList: {
    maxHeight: 220,
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    background: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  granteeGroup: {
    padding: '8px 8px 6px',
    border: '1px solid #edf2f7',
    borderRadius: 8,
    background: '#ffffff'
  },
  granteeGroupTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#475569',
    letterSpacing: '0.02em',
    marginBottom: 6,
    textTransform: 'uppercase'
  },
  typeBadge: {
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    border: '1px solid #d1d5db',
    background: '#f9fafb'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 24
  }
};

export default PagePermissionsManager;
